import React from "react";
import { createPortal } from "react-dom";
import { getT } from "../../../locales";
import { areSameColor } from "./colorPalette";
import type { EditorElement } from "../../../editor/types";
import { MathTypeElementSimple } from "../components/MathTypeElementSimple";
import { MathMLContent } from "../components/MathMLContent";
import {
  resolveChoiceAppearanceExtended,
  resolveImageStyle,
  resolveAudioStyle,
  resolveVideoStyle,
  resolveMathTypeStyle,
  resolveDragSourceAppearance,
  resolveDropTargetAppearance,
  resolveOrderListAppearance,
  resolveMatchingPointAppearance,
  mapLabelPosition,
  // resolveSubmitButtonStyle,
  resolveTextStyle,
  resolveFillInTheBlanksAppearance,
  resolvePaintAreaAppearance,
  resolvePaintSwatchAppearance,
  resolvePushToTalkAppearance,
  resolveTableAppearance,
  // resolveTimerStyle,
} from "./appearance";
import { CellElement } from "../components/CellElement";
import type {
  ChoiceAppearanceExtended,
  ChoiceOption,
  RenderOptions,
  TextStyle,
  AudioStyle,
  VideoStyle,
  MathTypeStyle,
  DragSourceAppearance,
  DropTargetAppearance,
  OrderListAppearance,
  MatchingPointAppearance,
  OrderListItem,
  DropAreaConfig,
  FillInTheBlanksAppearance,
  Blank,
  PaintAreaAppearance,
  PaintSwatchAppearance,
  PushToTalkAppearance,
  TableAppearance,
  TableCell,
  ElementTemplateKey,
  KeypadLayout,
  // TimerStyle,
} from "../types";
import { TEMPLATE_MIME } from "../constants";
import { createElementFromTemplate } from "./templates";
import { contentHasBlanks, deriveBlanksFromContent } from "./text";
import {
  SHAPE_BY_KIND,
  POLYGON_POINTS_FOR_SHAPE,
  isRoundableShape,
  roundedPolygonPath,
  type ShapeKind,
} from "./shapes";
import {
  getPaintShapePathD,
  DEFAULT_FREE_POLYGON_POINTS,
  type PaintPoint,
} from "./paintShapes";

const DEFAULT_DROP_TARGET_PLACEHOLDER = () => getT()("renderers.dropTarget.placeholder");
const DEFAULT_DROP_AREA_HIGHLIGHT = "rgba(37, 99, 235, 0.12)";
const isMathTypeElement = (element: EditorElement): boolean =>
  element.type === "mathtype" || element.type === "mathlive";
const isMathTypeContent = (contentType?: string): boolean =>
  contentType === "mathtype" || contentType === "mathlive";

// // Timer formatting helper
// const formatTime = (seconds: number, format: TimerStyle['format']): string => {
//   if (format === 'ss') {
//     return `${seconds}`;
//   }

//   // mm:ss format
//   const mins = Math.floor(seconds / 60);
//   const secs = seconds % 60;
//   return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
// };

// // Timer component for preview mode
// const TimerComponent: React.FC<{ style: TimerStyle; containerStyle: React.CSSProperties }> = ({ style, containerStyle }) => {
//   const [timeLeft, setTimeLeft] = React.useState(style.duration);
//   const [isRunning, setIsRunning] = React.useState(true);
//   const hasExpiredRef = React.useRef(false);

//   // Timer rengi: 0 = kırmızı, <=10 = turuncu, diğer = normal
//   const getTimerColor = () => {
//     if (timeLeft === 0) return '#dc2626'; // kırmızı
//     if (timeLeft <= 10) return '#f59e0b'; // turuncu
//     return style.color; // normal renk
//   };

//   React.useEffect(() => {
//     if (!isRunning || timeLeft <= 0) {
//       // Timer bittiğinde event dispatch et
//       if (timeLeft === 0 && !hasExpiredRef.current) {
//         hasExpiredRef.current = true;

//         // Timer expired event'i gönder
//         window.dispatchEvent(
//           new CustomEvent('timer-expired', {
//             detail: { allowContinue: style.allowContinueAfterExpiry },
//           })
//         );

//         // Otomatik gönder aktifse, submit event'i de gönder
//         if (style.autoSubmitOnExpiry) {
//           window.dispatchEvent(
//             new CustomEvent('question-submit', {
//               detail: { autoSubmitted: true },
//             })
//           );
//         }
//       }
//       return;
//     }

//     const intervalId = setInterval(() => {
//       setTimeLeft((prev) => {
//         const newTime = prev <= 1 ? 0 : prev - 1;
//         if (newTime === 0) {
//           setIsRunning(false);
//         }
//         return newTime;
//       });
//     }, 1000);

//     return () => clearInterval(intervalId);
//   }, [isRunning, timeLeft, style.allowContinueAfterExpiry, style.autoSubmitOnExpiry]);

//   const timerColor = getTimerColor();

//   return (
//     <div style={{ ...containerStyle, color: timerColor, transition: 'color 0.3s ease' }}>
//       {formatTime(timeLeft, style.format)}
//     </div>
//   );
// };

export const renderCanvasElementContent = (
  element: EditorElement,
  renderOptions: RenderOptions = {}
): React.ReactNode => {
  const t = getT();
  const mode = renderOptions.mode ?? "canvas";

  if (element.type === "text") {
    const data = element.data as {
      content?: string;
      htmlContent?: string;
      style?: Partial<TextStyle>;
    };
    const style = resolveTextStyle(element);

    // Inline blanks: a text block containing `[answer]` markers behaves like a
    // fill-in-the-blanks element in PREVIEW/student mode. In the design canvas
    // the raw `[answer]` text stays visible so the author can see/edit it.
    if (mode === "preview" && contentHasBlanks(data.content)) {
      const blanks = deriveBlanksFromContent(data.content);
      const fitbAppearance: FillInTheBlanksAppearance = {
        ...resolveFillInTheBlanksAppearance(element),
        fontFamily: style.fontFamily,
        fontSize: style.fontSize,
        fontWeight: style.fontWeight,
        color: style.color,
        lineHeight: style.lineHeight,
        backgroundColor: style.backgroundColor,
        paddingX: style.paddingX,
        paddingY: style.paddingY,
        borderRadius: style.borderRadius,
        borderWidth: style.borderWidth,
        borderColor: style.borderColor,
        italic: style.italic,
        underline: style.underline,
        textAlign: style.textAlign,
        letterSpacing: style.letterSpacing,
        textTransform: style.textTransform,
        shadow: style.shadow,
      };
      return (
        <FillInTheBlanksRenderer
          elementId={element.id}
          content={data.content || ""}
          blanks={blanks}
          appearance={fitbAppearance}
          mode={mode}
          formState={renderOptions.formState}
          onFillInTheBlanksChange={renderOptions.onFillInTheBlanksChange}
        />
      );
    }

    const textStyle: React.CSSProperties = {
      fontFamily: style.fontFamily,
      fontSize: `${style.fontSize}px`,
      fontWeight: style.fontWeight,
      fontStyle: style.italic ? "italic" : "normal",
      textDecoration: style.underline ? "underline" : "none",
      color: style.color,
      textAlign: style.textAlign,
      lineHeight: style.lineHeight,
      letterSpacing: `${style.letterSpacing}px`,
      backgroundColor: style.backgroundColor,
      padding: `${style.paddingY}px ${style.paddingX}px`,
      borderRadius: style.borderRadius,
      border:
        style.borderWidth > 0
          ? `${style.borderWidth}px solid ${style.borderColor}`
          : "none",
      textTransform: style.textTransform,
      boxShadow: style.shadow,
      boxSizing: "border-box",
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      // Fix double click on formatted text in canvas mode
      pointerEvents: mode === "canvas" ? "none" : undefined,
    };

    // If there's HTML content, render it with dangerouslySetInnerHTML
    if (data.htmlContent) {
      return (
        <div
          className="qe-text-block"
          style={textStyle}
          dangerouslySetInnerHTML={{ __html: data.htmlContent }}
        />
      );
    }

    // Fallback to plain text content
    return (
      <div className="qe-text-block" style={textStyle}>
        {data.content || t("renderers.text.fallbackContent")}
      </div>
    );
  }

  if (element.type === "image") {
    const style = resolveImageStyle(element);

    // Container style for border and effects
    const containerStyle: React.CSSProperties = {
      width: "100%",
      height: "100%",
      borderRadius: `${style.borderRadius}px`,
      border:
        style.borderWidth > 0
          ? `${style.borderWidth}px solid ${style.borderColor}`
          : "none",
      boxShadow: style.shadow,
      overflow: "hidden",
      boxSizing: "border-box",
      pointerEvents: "none",
    };

    const imageStyle: React.CSSProperties = {
      width: "100%",
      height: "100%",
      objectFit: style.objectFit,
      objectPosition: style.objectPosition,
      opacity: style.opacity,
      display: "block",
      userSelect: "none",
    };

    if (!style.url) {
      // Placeholder when no image URL is set
      const placeholderStyle: React.CSSProperties = {
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: "#f3f4f6",
        color: "#9ca3af",
        fontSize: "14px",
        fontWeight: 500,
      };
      return (
        <div style={containerStyle}>
          <div style={placeholderStyle}>
            <span>🖼️ {t("renderers.image.addUrl")}</span>
          </div>
        </div>
      );
    }

    return (
      <div style={containerStyle}>
        <img src={style.url} alt={style.alt} style={imageStyle} />
      </div>
    );
  }

  // if (element.type === 'timer') {
  //   const style = resolveTimerStyle(element);
  //   const containerStyle: React.CSSProperties = {
  //     width: '100%',
  //     height: '100%',
  //     display: 'flex',
  //     alignItems: 'center',
  //     justifyContent: style.textAlign === 'left' ? 'flex-start' : style.textAlign === 'right' ? 'flex-end' : 'center',
  //     fontFamily: style.fontFamily,
  //     fontSize: `${style.fontSize}px`,
  //     fontWeight: style.fontWeight,
  //     color: style.color,
  //     backgroundColor: style.backgroundColor,
  //     padding: `${style.paddingY}px ${style.paddingX}px`,
  //     borderRadius: `${style.borderRadius}px`,
  //     border: style.borderWidth > 0 ? `${style.borderWidth}px solid ${style.borderColor}` : 'none',
  //     boxSizing: 'border-box',
  //     userSelect: 'none',
  //   };

  //   // In preview mode, show running timer
  //   if (mode === 'preview') {
  //     return <TimerComponent style={style} containerStyle={containerStyle} />;
  //   }

  //   // In canvas mode, show static time
  //   return <div style={containerStyle}>{formatTime(style.duration, style.format)}</div>;
  // }

  // if (element.type === 'submit-button') {
  //   const style = resolveSubmitButtonStyle(element);
  //   const buttonStyle: React.CSSProperties = {
  //     fontFamily: style.fontFamily,
  //     fontSize: `${style.fontSize}px`,
  //     fontWeight: style.fontWeight,
  //     color: style.textColor,
  //     backgroundColor: style.backgroundColor,
  //     padding: `${style.paddingY}px ${style.paddingX}px`,
  //     borderRadius: `${style.borderRadius}px`,
  //     border: style.borderWidth > 0 ? `${style.borderWidth}px solid ${style.borderColor}` : 'none',
  //     cursor: mode === 'preview' ? 'pointer' : 'default',
  //     width: '100%',
  //     height: '100%',
  //     display: 'flex',
  //     alignItems: 'center',
  //     justifyContent: 'center',
  //     boxSizing: 'border-box',
  //     userSelect: 'none',
  //     transition: 'opacity 0.2s',
  //     pointerEvents: mode === 'canvas' ? 'none' : 'auto',
  //   };

  //   const handleClick = () => {
  //     if (mode === 'preview') {
  //       window.dispatchEvent(new CustomEvent('question-submit', { detail: { elementId: element.id } }));
  //     }
  //   };

  //   return (
  //     <button
  //       type="button"
  //       style={buttonStyle}
  //       onClick={handleClick}
  //       onMouseEnter={(e) => {
  //         if (mode === 'preview') {
  //           (e.target as HTMLButtonElement).style.opacity = '0.9';
  //         }
  //       }}
  //       onMouseLeave={(e) => {
  //         if (mode === 'preview') {
  //           (e.target as HTMLButtonElement).style.opacity = '1';
  //         }
  //       }}
  //     >
  //       {style.text}
  //     </button>
  //   );
  // }

  if (element.type === "choice") {
    const data = element.data as {
      options?: ChoiceOption[];
      multiple?: boolean;
      appearance?: Partial<ChoiceAppearanceExtended>;
    };

    const appearance = resolveChoiceAppearanceExtended(element);
    const options = data.options || [];
    const multiple = data.multiple ?? false;

    return (
      <ChoiceComponent
        elementId={element.id}
        options={options}
        appearance={appearance}
        multiple={multiple}
        mode={mode}
        formState={renderOptions.formState}
        onChoiceChange={renderOptions.onChoiceChange}
      />
    );
  }

  if (element.type === "audio") {
    const style = resolveAudioStyle(element);
    return (
      <AudioElementRenderer style={style} mode={mode} elementId={element.id} />
    );
  }

  if (element.type === "color-palette") {
    return <ColorPaletteRenderer element={element} mode={mode} />;
  }

  if (element.type === "shape") {
    return (
      <ShapeRenderer
        element={element}
        mode={mode}
        onUpdate={renderOptions.onUpdate}
      />
    );
  }

  if (element.type === "count-area") {
    return (
      <CountAreaRenderer
        element={element}
        mode={mode}
        onUpdate={renderOptions.onUpdate}
      />
    );
  }

  if (element.type === "keypad") {
    return <KeypadRenderer element={element} mode={mode} />;
  }

  if (element.type === "play-button") {
    return <PlayButtonRenderer element={element} mode={mode} />;
  }

  if (element.type === "cube-bar") {
    const data = element.data as { expectedTotal?: number };
    // Doğru cevap (beklenen toplam): önce answer key, yoksa öğe ayarı.
    const expectedTotal =
      renderOptions.cubeExpectedTotals?.[element.id] ?? data.expectedTotal;
    return (
      <CubeBarRenderer
        element={element}
        mode={mode}
        onCubeBarChange={renderOptions.onCubeBarChange}
        expectedTotal={expectedTotal}
      />
    );
  }

  if (element.type === "cube-source") {
    return <CubeSourceRenderer element={element} mode={mode} />;
  }

  if (element.type === "slide") {
    const style = resolveVideoStyle(element);
    return (
      <VideoElementRenderer
        style={style}
        mode={mode}
        elementId={element.id}
        renderOptions={renderOptions}
      />
    );
  }

  if (isMathTypeElement(element)) {
    return (
      <MathTypeElementSimple
        element={element}
        mode={mode}
        onEditClick={renderOptions.onEditMathType}
      />
    );
  }

  if (element.type === "drag-source") {
    const data = element.data as {
      label?: string;
      showLabel?: boolean | number | string;
      appearance?: Partial<DragSourceAppearance>;
    };
    const appearance = resolveDragSourceAppearance(element);
    // Label: if string (including empty), use it; if undefined, use default
    const labelValue = typeof data.label === "string" ? data.label : t("renderers.dragSource.defaultLabel");
    // Show label if showLabel is truthy (for backward compatibility with data.showLabel)
    // Use the resolved appearance value for consistency
    const shouldShowLabel = appearance.showLabel !== false;
    return (
      <DragSourceRenderer
        elementId={element.id}
        label={shouldShowLabel ? labelValue : ""}
        value={element.id}
        appearance={appearance}
        mode={mode}
        size={element.size}
      />
    );
  }

  if (element.type === "drop-target") {
    const data = element.data as {
      label?: string;
      showLabel?: boolean | number | string;
      acceptedSources?: string[];
      appearance?: Partial<DropTargetAppearance>;
      dropArea?: DropAreaConfig;
      placeholder?:
        | string
        | { preview?: string | null; canvas?: string | null }
        | null;
      showPlaceholder?: boolean | number | string;
    };
    const appearance = resolveDropTargetAppearance(element);
    // Label: if string (including empty), use it; if undefined, use default
    const labelValue =
      typeof data.label === "string" ? data.label : t("renderers.dropTarget.defaultLabel");
    // Show label if showLabel is truthy OR undefined (for backward compatibility)
    const shouldShowLabel =
      data.showLabel !== false &&
      data.showLabel !== 0 &&
      data.showLabel !== "0";

    const placeholderValue = (() => {
      const raw = data.placeholder;
      if (typeof raw === "string") {
        return raw;
      }
      if (raw && typeof raw === "object") {
        if (typeof raw.preview === "string") {
          return raw.preview;
        }
        if (typeof raw.canvas === "string") {
          return raw.canvas;
        }
      }
      return undefined;
    })();
    // Show placeholder if showPlaceholder is truthy OR undefined (for backward compatibility)
    const shouldShowPlaceholder =
      data.showPlaceholder !== false &&
      data.showPlaceholder !== 0 &&
      data.showPlaceholder !== "0";

    return (
      <DropTargetRenderer
        elementId={element.id}
        label={shouldShowLabel ? labelValue : ""}
        acceptedSources={data.acceptedSources || []}
        appearance={appearance}
        mode={mode}
        formState={renderOptions.formState}
        allElements={renderOptions.allElements}
        onDragDropChange={renderOptions.onDragDropChange}
        dropArea={data.dropArea}
        placeholder={shouldShowPlaceholder ? placeholderValue : ""}
      />
    );
  }

  if (element.type === "order-list") {
    const data = element.data as {
      label?: string;
      showLabel?: boolean | number | string;
      appearance?: Partial<OrderListAppearance>;
      placeholder?: string | null;
      showPlaceholder?: boolean | number | string;
      items?: Array<OrderListItem>;
    };
    const appearance = resolveOrderListAppearance(element);
    // Label: if string (including empty), use it; if undefined, use default
    const labelValue =
      typeof data.label === "string" ? data.label : t("renderers.orderList.defaultLabel");
    // Show label if showLabel is truthy OR undefined (for backward compatibility)
    const shouldShowLabel = data.showLabel !== false && data.showLabel !== 0;

    const placeholderValue =
      typeof data.placeholder === "string"
        ? data.placeholder
        : t("renderers.orderList.defaultPlaceholder");
    // Show placeholder if showPlaceholder is truthy OR undefined (for backward compatibility)
    const shouldShowPlaceholder =
      data.showPlaceholder !== false &&
      data.showPlaceholder !== 0 &&
      data.showPlaceholder !== "0";

    const items = data.items || [];

    return (
      <OrderListRenderer
        elementId={element.id}
        label={shouldShowLabel ? labelValue : ""}
        appearance={appearance}
        mode={mode}
        formState={renderOptions.formState}
        onDragDropChange={renderOptions.onDragDropChange}
        placeholder={shouldShowPlaceholder ? placeholderValue : ""}
        items={items}
        onUpdate={renderOptions.onUpdate}
      />
    );
  }

  if (element.type === "matching-point") {
    const data = element.data as {
      pointType?: "source" | "target";
      parentElementId?: string | null;
      connections?: string[]; // Source point'ler için target point ID'leri
      appearance?: Partial<MatchingPointAppearance>;
    };
    const appearance = resolveMatchingPointAppearance(element);
    const pointType = data.pointType || "source";
    const pointSize = appearance.pointSize;
    const allElements = renderOptions.allElements || [];

    // İçerik tipi "color" (varsayılan) ise eski küçük renkli nokta;
    // "text"/"image" ise element boyutunda içerikli kutu (frame).
    const contentType = appearance.contentType ?? "color";
    const isFrame = contentType !== "color";
    const widthPx = isFrame ? element.size.width : pointSize;
    const heightPx = isFrame
      ? element.size.height ?? element.size.width
      : pointSize;
    // borderRadius: frame modunda px, color modunda % (yuvarlak nokta).
    const borderRadiusValue = isFrame
      ? `${appearance.borderRadius}px`
      : `${appearance.borderRadius}%`;

    const pointStyle: React.CSSProperties = {
      width: `${widthPx}px`,
      height: `${heightPx}px`,
      borderRadius: borderRadiusValue,
      backgroundColor:
        pointType === "source"
          ? appearance.sourceColor
          : appearance.targetColor,
      border: `${appearance.borderWidth}px solid ${
        pointType === "source"
          ? appearance.sourceBorderColor
          : appearance.targetBorderColor
      }`,
      boxSizing: "border-box",
      cursor:
        mode === "preview"
          ? pointType === "source"
            ? "grab"
            : "pointer"
          : "default",
      transition: "all 0.2s ease",
      position: "relative",
      // Preview modunda modal içinde yüksek z-index (150), canvas modunda normal z-index kullanılır
      // Canvas modunda z-index artışı yapılmaz, element.zIndex kullanılır
      zIndex: mode === "preview" ? 150 : "inherit",
      pointerEvents: "auto", // Tıklanabilir ve sürüklenebilir olmalı
      // Frame modunda içeriği ortala
      display: isFrame ? "flex" : undefined,
      alignItems: isFrame ? "center" : undefined,
      justifyContent: isFrame ? "center" : undefined,
      overflow: isFrame ? "hidden" : undefined,
    };

    // Frame içeriği — yazı ya da resim.
    const frameContent = isFrame ? (
      contentType === "image" && appearance.contentImageUrl ? (
        <img
          src={appearance.contentImageUrl}
          alt=""
          draggable={false}
          style={{
            width: "100%",
            height: "100%",
            objectFit: "contain",
            pointerEvents: "none",
          }}
        />
      ) : (
        <span
          style={{
            fontSize: `${appearance.contentFontSize ?? 24}px`,
            fontWeight: appearance.contentFontWeight ?? 600,
            color: appearance.contentColor ?? "#1f2937",
            fontFamily: appearance.contentFontFamily,
            userSelect: "none",
            padding: "2px 6px",
            textAlign: "center",
            pointerEvents: "none",
          }}
        >
          {appearance.contentText ?? ""}
        </span>
      )
    ) : null;

    // Preview modunda drag & drop için
    const handleDragStart = (e: React.DragEvent) => {
      if (mode !== "preview" || pointType !== "source") return;
      e.dataTransfer.effectAllowed = "link";
      e.dataTransfer.setData("application/x-matching-source-id", element.id);

      // Mouse pozisyonunu canvas'a göre hesapla
      const canvasElement = (e.currentTarget as HTMLElement).closest(
        ".qe-preview-canvas, .qe-fullscreen-preview__canvas, .qe-canvas"
      );
      let mouseX = e.clientX;
      let mouseY = e.clientY;

      if (canvasElement) {
        const rect = canvasElement.getBoundingClientRect();
        mouseX = e.clientX - rect.left;
        mouseY = e.clientY - rect.top;
      }

      // Custom event ile drag start bilgisini ilet (mouse pozisyonunu da ekle)
      window.dispatchEvent(
        new CustomEvent("matching-drag-start", {
          detail: {
            sourceId: element.id,
            sourcePos: {
              x: element.position.x + (element.size.width || 16) / 2,
              y: element.position.y + (element.size.height || 16) / 2,
            },
            mousePos: {
              x: mouseX,
              y: mouseY,
            },
          },
        })
      );
    };

    const handleDragOver = (e: React.DragEvent) => {
      if (mode !== "preview" || pointType !== "target") return;
      e.preventDefault();
      e.dataTransfer.dropEffect = "link";
    };

    const handleDrop = (e: React.DragEvent) => {
      if (mode !== "preview" || pointType !== "target") return;
      e.preventDefault();
      const sourceId = e.dataTransfer.getData(
        "application/x-matching-source-id"
      );
      if (!sourceId || sourceId === element.id) return;

      const sourceElement = allElements.find((el) => el.id === sourceId);
      if (!sourceElement || sourceElement.type !== "matching-point") return;

      const sourceData = sourceElement.data as {
        pointType?: "source" | "target";
        connections?: string[];
        allowMultipleConnections?: boolean;
      };
      if (sourceData.pointType !== "source") return;

      const currentConnections =
        mode === "preview"
          ? renderOptions.matchingConnections?.[sourceId] || []
          : sourceData.connections || [];
      const allowMultiple = sourceData.allowMultipleConnections || false;

      if (!currentConnections.includes(element.id)) {
        const updatedConnections = allowMultiple
          ? [...currentConnections, element.id]
          : [element.id];

        renderOptions.onUpdate?.(sourceId, {
          data: {
            ...sourceData,
            connections: updatedConnections,
          },
        });
      }
    };

    // Target point için mıknatıs efekti görsel geri bildirimi
    if (mode === "preview" && pointType === "target") {
      pointStyle.transition = "all 0.2s ease";
    }

    return (
      <div
        style={pointStyle}
        draggable={mode === "preview" && pointType === "source"}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onMouseEnter={(e) => {
          if (mode === "preview" && appearance.enableHoverEffect) {
            const el = e.currentTarget as HTMLElement;
            el.style.transform = "scale(1.2)";
            if (pointType === "target") {
              el.style.boxShadow = `0 0 20px ${appearance.targetColor}80, 0 0 10px ${appearance.targetColor}40`;
            }
          }
        }}
        onMouseLeave={(e) => {
          if (mode === "preview" && appearance.enableHoverEffect) {
            const el = e.currentTarget as HTMLElement;
            el.style.transform = "scale(1)";
            if (pointType === "target") {
              el.style.boxShadow = "";
            }
          }
        }}
      >
        {frameContent}
      </div>
    );
  }

  if (element.type === "fill-in-the-blanks") {
    const data = element.data as {
      content?: string;
      blanks?: Blank[];
      appearance?: Partial<FillInTheBlanksAppearance>;
    };
    const appearance = resolveFillInTheBlanksAppearance(element);
    const content = data.content || "";
    const blanks = data.blanks || [];

    return (
      <FillInTheBlanksRenderer
        elementId={element.id}
        content={content}
        blanks={blanks}
        appearance={appearance}
        mode={mode}
        formState={renderOptions.formState}
        onFillInTheBlanksChange={renderOptions.onFillInTheBlanksChange}
      />
    );
  }

  if (element.type === "paint-area") {
    const data = element.data as {
      appearance?: Partial<PaintAreaAppearance>;
      paintedColor?: string; // Current painted color
    };
    const appearance = resolvePaintAreaAppearance(element);
    const paintedColor = data.paintedColor;
    // Doğru renk: önce answer key (AnswerKeyModal), yoksa inspector alanı.
    const correctColor =
      renderOptions.paintCorrectColors?.[element.id] ?? appearance.correctColor;

    return (
      <PaintAreaRenderer
        elementId={element.id}
        appearance={appearance}
        paintedColor={paintedColor}
        correctColor={correctColor}
        mode={mode}
        formState={renderOptions.formState}
        allElements={renderOptions.allElements}
        onUpdate={renderOptions.onUpdate}
      />
    );
  }

  if (element.type === "paint-swatch") {
    const appearance = resolvePaintSwatchAppearance(element);

    return (
      <PaintSwatchRenderer
        elementId={element.id}
        appearance={appearance}
        mode={mode}
        formState={renderOptions.formState}
      />
    );
  }

  if (element.type === "push-to-talk") {
    const appearance = resolvePushToTalkAppearance(element);

    return (
      <PushToTalkRenderer
        elementId={element.id}
        appearance={appearance}
        mode={mode}
        formState={renderOptions.formState}
        onPushToTalkChange={renderOptions.onPushToTalkChange}
      />
    );
  }

  if (element.type === "table") {
    return (
      <TableRenderer
        element={element}
        mode={mode}
        onUpdate={renderOptions.onUpdate}
        onSelect={renderOptions.onSelect}
        onContextMenu={renderOptions.onContextMenu}
      />
    );
  }

  if (element.type === "group") {
    const data = element.data as {
      children?: Array<{
        id: string;
        type: string;
        position: { x: number; y: number };
        size: { width: number; height?: number };
        rotation?: number;
        zIndex: number;
        data: Record<string, unknown>;
      }>;
      locked?: boolean;
      label?: string;
    };
    const children = data.children || [];

    return (
      <div
        className="qe-group"
        style={{
          width: "100%",
          height: "100%",
          position: "relative",
          pointerEvents: mode === "canvas" ? "none" : undefined,
        }}
      >
        {children.map((child) => (
          <div
            key={child.id}
            style={{
              position: "absolute",
              left: child.position.x,
              top: child.position.y,
              width: child.size.width,
              height: child.size.height || "auto",
              transform: child.rotation
                ? `rotate(${child.rotation}deg)`
                : undefined,
              zIndex: child.zIndex,
            }}
          >
            {renderCanvasElementContent(child as EditorElement, renderOptions)}
          </div>
        ))}
      </div>
    );
  }

  return <div>Unknown element type: {element.type}</div>;
};

// Choice Component
interface ChoiceComponentProps {
  elementId: string;
  options: ChoiceOption[];
  appearance: ChoiceAppearanceExtended;
  multiple: boolean;
  mode: "canvas" | "preview";
  formState?: Record<string, string | string[] | Record<string, string>>;
  onChoiceChange?: (
    elementId: string,
    optionId: string,
    isMultiple: boolean
  ) => void;
}

const ChoiceComponent: React.FC<ChoiceComponentProps> = ({
  elementId,
  options,
  appearance,
  multiple,
  mode,
  formState,
  onChoiceChange,
}) => {
  const t = getT();
  const [selectedValues, setSelectedValues] = React.useState<string[]>([]);
  const [hoveredOption, setHoveredOption] = React.useState<string | null>(null);
  const [uniformWidth, setUniformWidth] = React.useState<number | null>(null);
  const optionsContainerRef = React.useRef<HTMLDivElement | null>(null);
  const measureContainerRef = React.useRef<HTMLDivElement | null>(null);

  // Initialize selected values from formState in preview mode
  React.useEffect(() => {
    if (mode === "preview" && formState?.[elementId]) {
      const value = formState[elementId];
      if (Array.isArray(value)) {
        setSelectedValues(value);
      } else if (typeof value === "string") {
        setSelectedValues([value]);
      }
    }
  }, [formState, elementId, mode]);

  // MathType rendering is handled via MathML, no dynamic import needed
  const handleOptionClick = (option: ChoiceOption) => {
    if (mode !== "preview" || option.disabled) return;

    let newValues: string[];
    if (multiple) {
      if (selectedValues.includes(option.value)) {
        newValues = selectedValues.filter((v) => v !== option.value);
      } else {
        newValues = [...selectedValues, option.value];
      }
    } else {
      newValues = [option.value];
    }

    setSelectedValues(newValues);

    // Call the callback if provided
    if (onChoiceChange) {
      onChoiceChange(elementId, option.value, multiple);
    }
  };

  // Container styles
  const containerStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    backgroundColor: appearance.groupBackground,
    borderRadius: `${appearance.groupBorderRadius}px`,
    border:
      appearance.groupBorderWidth > 0
        ? `${appearance.groupBorderWidth}px solid ${appearance.groupBorderColor}`
        : "none",
    padding: `${appearance.groupPadding}px`,
    boxShadow: appearance.groupShadow,
    boxSizing: "border-box",
    pointerEvents: mode === "canvas" ? "none" : "auto",
  };

  // Options container layout
  const optionsContainerStyle: React.CSSProperties = {
    display: appearance.layout === "grid" ? "grid" : "flex",
    flexDirection: appearance.layout === "vertical" ? "column" : "row",
    gap: `${appearance.optionSpacing}px`,
    ...(appearance.layout === "grid" && {
      gridTemplateColumns: `repeat(${appearance.gridColumns || 2}, 1fr)`,
    }),
  };

  const shouldUseUniformWidth =
    appearance.uniformOptionWidth && appearance.layout === "horizontal";

  React.useLayoutEffect(() => {
    if (!shouldUseUniformWidth) {
      if (uniformWidth !== null) {
        setUniformWidth(null);
      }
      return;
    }

    if (!measureContainerRef.current) {
      return;
    }

    const optionNodes = Array.from(
      measureContainerRef.current.querySelectorAll(
        "[data-choice-measure='true']"
      )
    ) as HTMLElement[];

    if (optionNodes.length === 0) {
      if (uniformWidth !== null) {
        setUniformWidth(null);
      }
      return;
    }

    const maxWidth = Math.max(
      ...optionNodes.map((node) => node.getBoundingClientRect().width)
    );

    if (!Number.isFinite(maxWidth)) {
      return;
    }

    const nextWidth = Math.ceil(maxWidth);
    if (uniformWidth !== nextWidth) {
      setUniformWidth(nextWidth);
    }
  }, [
    shouldUseUniformWidth,
    appearance.layout,
    appearance.fontFamily,
    appearance.fontSize,
    appearance.fontWeight,
    appearance.optionPaddingX,
    appearance.optionPaddingY,
    appearance.optionBorderWidth,
    appearance.optionBorderRadius,
    appearance.optionPrefix,
    appearance.optionIconSize,
    appearance.optionImageSize,
    appearance.optionImageObjectFit,
    appearance.optionImageBorderRadius,
    options,
    uniformWidth,
  ]);

  const renderIndicator = (isSelected: boolean) => {
    if (!appearance.showIndicator) return null;

    // Custom indicator icons
    if (appearance.indicatorType === "custom") {
      const iconUrl = isSelected
        ? appearance.indicatorCustomIconCheckedUrl ||
          appearance.indicatorCustomIconUrl
        : appearance.indicatorCustomIconUrl;

      if (iconUrl) {
        return (
          <img
            src={iconUrl}
            alt=""
            style={{
              width: `${appearance.indicatorSize}px`,
              height: `${appearance.indicatorSize}px`,
              objectFit: "contain",
              flexShrink: 0,
            }}
          />
        );
      }
    }

    // Default checkbox/radio indicator
    const indicatorBorderRadius =
      appearance.indicatorType === "radio"
        ? "50%"
        : `${appearance.indicatorBorderRadius}px`;

    const indicatorStyle: React.CSSProperties = {
      width: `${appearance.indicatorSize}px`,
      height: `${appearance.indicatorSize}px`,
      borderRadius: indicatorBorderRadius,
      border: `${appearance.indicatorBorderWidth}px solid ${
        isSelected ? appearance.indicatorActiveColor : appearance.indicatorColor
      }`,
      backgroundColor: isSelected
        ? appearance.indicatorActiveColor
        : "transparent",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      flexShrink: 0,
      transition: "all 0.2s",
    };

    return (
      <div style={indicatorStyle}>
        {isSelected && appearance.indicatorType === "checkbox" && (
          <svg
            width={appearance.indicatorSize * 0.6}
            height={appearance.indicatorSize * 0.6}
            viewBox="0 0 24 24"
            fill="none"
            stroke="white"
            strokeWidth="3"
          >
            <polyline points="20 6 9 17 4 12" />
          </svg>
        )}
        {isSelected && appearance.indicatorType === "radio" && (
          <div
            style={{
              width: `${appearance.indicatorSize * 0.5}px`,
              height: `${appearance.indicatorSize * 0.5}px`,
              borderRadius: "50%",
              backgroundColor: "white",
            }}
          />
        )}
      </div>
    );
  };

  const buildLetterPrefix = (index: number, lowercase = false) => {
    let value = index + 1;
    let result = "";
    while (value > 0) {
      const remainder = (value - 1) % 26;
      result = String.fromCharCode(65 + remainder) + result;
      value = Math.floor((value - 1) / 26);
    }
    return lowercase ? result.toLowerCase() : result;
  };

  const buildRomanPrefix = (index: number) => {
    let value = index + 1;
    if (value <= 0) {
      return "";
    }
    const map: Array<[number, string]> = [
      [1000, "M"],
      [900, "CM"],
      [500, "D"],
      [400, "CD"],
      [100, "C"],
      [90, "XC"],
      [50, "L"],
      [40, "XL"],
      [10, "X"],
      [9, "IX"],
      [5, "V"],
      [4, "IV"],
      [1, "I"],
    ];
    let result = "";
    for (const [num, symbol] of map) {
      while (value >= num) {
        result += symbol;
        value -= num;
      }
    }
    return result;
  };

  const getOptionPrefixLabel = (index: number) => {
    const prefixType = appearance.optionPrefix || "none";
    if (prefixType === "letters") {
      return `${buildLetterPrefix(index)})`;
    }
    if (prefixType === "letters-lower") {
      return `${buildLetterPrefix(index, true)})`;
    }
    if (prefixType === "numbers") {
      return `${index + 1})`;
    }
    if (prefixType === "numbers-dot") {
      return `${index + 1}.`;
    }
    if (prefixType === "roman-upper") {
      return `${buildRomanPrefix(index)})`;
    }
    if (prefixType === "roman-lower") {
      return `${buildRomanPrefix(index).toLowerCase()})`;
    }
    return "";
  };

  const renderOptionContent = (
    option: ChoiceOption,
    prefixElement: React.ReactNode,
    hasPrefix: boolean
  ) => {
    const hasMathContent =
      (typeof option.mathml === "string" && option.mathml.trim().length > 0) ||
      (typeof option.latex === "string" && option.latex.trim().length > 0);
    const contentType =
      option.contentType === "text" || !option.contentType
        ? hasMathContent
          ? "mathtype"
          : "text"
        : option.contentType;
    const labelValue = option.label?.trim();
    const isSelected = selectedValues.includes(option.value);

    if (contentType === "image") {
      const imageUrl = option.imageUrl?.trim();
      const borderRadius =
        option.imageBorderRadius ?? appearance.optionImageBorderRadius ?? 8;
      const width = option.imageWidth;
      const height = option.imageHeight;
      const objectFit =
        option.imageObjectFit || appearance.optionImageObjectFit || "cover";

      const fallbackImageSize = appearance.optionImageSize || 120;

      const imageStyles: React.CSSProperties = {
        width: width ? `${width}px` : `${fallbackImageSize}px`,
        height: height ? `${height}px` : `${fallbackImageSize}px`,
        maxWidth: "100%",
        borderRadius: `${borderRadius}px`,
        objectFit,
        display: "block",
        flexShrink: 0,
      };

      const placeholderStyles: React.CSSProperties = {
        width: `${fallbackImageSize}px`,
        height: `${fallbackImageSize}px`,
        borderRadius: `${borderRadius}px`,
        border: "1px dashed #cbd5e1",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        color: "#94a3b8",
        fontSize: "12px",
        padding: "8px",
        boxSizing: "border-box",
        textAlign: "center",
        flexShrink: 0,
        backgroundColor: "#f8fafc",
      };

      const captionColor = appearance.optionTextColor || "#475569";

      return (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "center",
            gap: "14px",
            flex: 1,
            width: "100%",
          }}
        >
          {prefixElement}
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={option.imageAlt || labelValue || option.value}
              style={imageStyles}
              onError={(e) => {
                // Handle broken image
                (e.target as HTMLImageElement).style.display = "none";
              }}
            />
          ) : (
            <div style={placeholderStyles}>
              <span style={{ fontSize: "10px" }}>{t("renderers.choice.imagePlaceholder")}</span>
            </div>
          )}
          {labelValue && (
            <div style={{ flex: 1, minWidth: 0 }}>
              <span
                style={{
                  fontSize: "12px",
                  color: captionColor,
                  display: "block",
                  wordBreak: "break-word",
                }}
              >
                {labelValue}
              </span>
            </div>
          )}
        </div>
      );
    }

    if (isMathTypeContent(contentType)) {
      // Note: This uses MathML from MathType (stored in mathml property)
      // For backward compatibility, latex is still supported
      const mathml = option.mathml || option.latex || "";
      const mathColor = option.mathColor ?? "#000000";
      const mathStyle: React.CSSProperties = {
        fontSize: `${option.mathFontSize ?? 24}px`,
        color: mathColor,
        backgroundColor: option.mathBackgroundColor ?? "transparent",
        borderRadius: `${option.mathBorderRadius ?? 4}px`,
        borderWidth: `${option.mathBorderWidth ?? 0}px`,
        borderColor: option.mathBorderColor ?? "#cccccc",
        borderStyle: (option.mathBorderWidth ?? 0) > 0 ? "solid" : "none",
        padding: `${option.mathPaddingY ?? 4}px ${option.mathPaddingX ?? 8}px`,
        display: "inline-block",
      };

      const mathAlign = option.mathAlign ?? "center";
      const justifyContent =
        mathAlign === "left"
          ? "flex-start"
          : mathAlign === "right"
          ? "flex-end"
          : "center";

      return (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: justifyContent,
            width: "100%",
            gap: hasPrefix ? "10px" : undefined,
          }}
        >
          {prefixElement}
          <MathMLContent html={mathml} style={mathStyle} />
        </div>
      );
    }

    const hasIcon = Boolean(
      option.customIconUrl || option.customIconCheckedUrl
    );
    const hasImage = Boolean(option.imageUrl && appearance.showOptionIcon);
    const hasLabel = Boolean(labelValue);

    let iconElement: React.ReactNode = null;
    if (appearance.showOptionIcon) {
      if (hasIcon) {
        const iconUrl =
          isSelected && option.customIconCheckedUrl
            ? option.customIconCheckedUrl
            : option.customIconUrl;
        if (iconUrl) {
          iconElement = (
            <img
              src={iconUrl}
              alt=""
              style={{
                width: `${appearance.optionIconSize}px`,
                height: `${appearance.optionIconSize}px`,
                objectFit: "contain",
                flexShrink: 0,
              }}
            />
          );
        }
      } else if (hasImage) {
        iconElement = (
          <img
            src={option.imageUrl}
            alt={labelValue || option.value}
            style={{
              width: `${appearance.optionImageSize}px`,
              height: `${appearance.optionImageSize}px`,
              objectFit: appearance.optionImageObjectFit,
              borderRadius: `${appearance.optionImageBorderRadius}px`,
              flexShrink: 0,
            }}
          />
        );
      }
    }

    const iconPosition = appearance.optionIconPosition;
    const contentFlexDirection =
      iconPosition === "top" || iconPosition === "bottom" ? "column" : "row";
    const contentOrder = iconPosition === "right" || iconPosition === "bottom";

    const labelContent =
      hasLabel || hasPrefix ? (
        <span
          style={{
            flex: 1,
            display: "inline-flex",
            alignItems: "center",
            gap: hasPrefix ? "8px" : undefined,
          }}
        >
          {prefixElement}
          {hasLabel ? labelValue : null}
        </span>
      ) : null;

    return (
      <div
        style={{
          display: "flex",
          flexDirection: contentFlexDirection,
          alignItems: "center",
          gap: "10px",
          flex: 1,
          textAlign: appearance.textAlign,
        }}
      >
        {!contentOrder && iconElement}
        {labelContent}
        {contentOrder && iconElement}
      </div>
    );
  };

  const renderOption = (
    option: ChoiceOption,
    index: number,
    forMeasure = false
  ) => {
    const isSelected = selectedValues.includes(option.value);
    const isHovered = hoveredOption === option.id;
    const hasMathContent =
      (typeof option.mathml === "string" && option.mathml.trim().length > 0) ||
      (typeof option.latex === "string" && option.latex.trim().length > 0);
    const contentType =
      option.contentType === "text" || !option.contentType
        ? hasMathContent
          ? "mathtype"
          : "text"
        : option.contentType;
    const prefixLabel = getOptionPrefixLabel(index);
    const hasPrefix = Boolean(prefixLabel);
    const prefixElement = hasPrefix ? (
      <span
        style={{
          fontWeight: appearance.fontWeight,
          flexShrink: 0,
          whiteSpace: "nowrap",
        }}
      >
        {prefixLabel}
      </span>
    ) : null;

    let optionBg = appearance.optionBackground;
    let optionBorderColor = appearance.optionBorderColor;
    let optionTextColor = appearance.optionTextColor;

    if (isSelected) {
      optionBg = appearance.optionActiveBackground;
      optionBorderColor = appearance.optionActiveBorderColor;
      optionTextColor = appearance.optionActiveTextColor;
    } else if (isHovered) {
      optionBg = appearance.optionHoverBackground;
      optionBorderColor = appearance.optionHoverBorderColor;
      optionTextColor = appearance.optionHoverTextColor;
    }

    const optionStyle: React.CSSProperties = {
      display: "flex",
      alignItems: "center",
      justifyContent: appearance.optionAlign,
      gap: "12px",
      padding: `${appearance.optionPaddingY}px ${appearance.optionPaddingX}px`,
      backgroundColor: optionBg,
      color: optionTextColor,
      border: `${appearance.optionBorderWidth}px solid ${optionBorderColor}`,
      borderRadius: `${appearance.optionBorderRadius}px`,
      boxShadow: appearance.optionShadow,
      cursor: option.disabled ? "not-allowed" : "pointer",
      opacity: option.disabled ? 0.5 : 1,
      transition: "all 0.2s",
      fontFamily: appearance.fontFamily,
      fontSize: `${appearance.fontSize}px`,
      fontWeight: appearance.fontWeight,
      boxSizing: "border-box",
      userSelect: "none",
    };

    if (shouldUseUniformWidth && uniformWidth && !forMeasure) {
      optionStyle.width = `${uniformWidth}px`;
    }

    if (forMeasure) {
      optionStyle.width = "max-content";
      optionStyle.position = "relative";
      optionStyle.pointerEvents = "none";
      optionStyle.transition = "none";
    }

    if (contentType === "image") {
      optionStyle.flexDirection = "row";
      optionStyle.justifyContent = "flex-start";
      optionStyle.alignItems = "center";
      optionStyle.gap = "12px";
    }

    return (
      <div
        key={option.id}
        style={optionStyle}
        data-choice-measure={forMeasure ? "true" : undefined}
        onClick={forMeasure ? undefined : () => handleOptionClick(option)}
        onMouseEnter={
          forMeasure
            ? undefined
            : () => {
                if (contentType === "mathtype" || contentType === "mathlive")
                  return;
                setHoveredOption((prev) =>
                  prev === option.id ? prev : option.id
                );
              }
        }
        onMouseLeave={
          forMeasure
            ? undefined
            : (e) => {
                if (contentType === "mathtype" || contentType === "mathlive")
                  return;
                const relatedTarget = e.relatedTarget as Node | null;
                if (relatedTarget && e.currentTarget.contains(relatedTarget))
                  return;
                setHoveredOption((prev) => (prev === null ? prev : null));
              }
        }
      >
        {appearance.indicatorPosition === "leading" &&
          renderIndicator(isSelected)}
        {renderOptionContent(option, prefixElement, hasPrefix)}
        {appearance.indicatorPosition === "trailing" &&
          renderIndicator(isSelected)}
      </div>
    );
  };

  return (
    <div style={containerStyle}>
      {shouldUseUniformWidth && (
        <div
          ref={measureContainerRef}
          style={{
            position: "absolute",
            left: "-10000px",
            top: "-10000px",
            visibility: "hidden",
            pointerEvents: "none",
            whiteSpace: "nowrap",
          }}
        >
          {options.map((option, index) =>
            renderOption(option, index, true)
          )}
        </div>
      )}
      <div ref={optionsContainerRef} style={optionsContainerStyle}>
        {options.map((option, index) => renderOption(option, index))}
      </div>
    </div>
  );
};

interface AudioElementRendererProps {
  elementId: string;
  style: AudioStyle;
  mode: "canvas" | "preview";
}

const AudioElementRenderer: React.FC<AudioElementRendererProps> = ({
  elementId,
  style,
  mode,
}) => {
  const t = getT();
  const audioRef = React.useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const hasAttemptedAutoplayRef = React.useRef(false);

  const togglePlayback = React.useCallback(() => {
    if (!style.sourceUrl) {
      return;
    }

    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    if (isPlaying) {
      audio.pause();
      setIsPlaying(false);
    } else {
      audio
        .play()
        .then(() => {
          setIsPlaying(true);
        })
        .catch(() => {
          // ignore playback errors (browser user gesture restrictions)
        });
    }
  }, [isPlaying, style.sourceUrl]);

  React.useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    const handleEnded = () => {
      setIsPlaying(false);
    };
    audio.addEventListener("ended", handleEnded);
    return () => {
      audio.removeEventListener("ended", handleEnded);
    };
  }, []);

  React.useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }
    audio.loop = Boolean(style.loop);
  }, [style.loop]);

  // Play while the linked text element is hovered (preview mode only).
  // The preview container dispatches these window events with the audio's id.
  React.useEffect(() => {
    if (mode !== "preview" || !style.playOnHover) {
      return;
    }
    const handleHoverPlay = (event: Event) => {
      const detail = (event as CustomEvent<{ elementId?: string }>).detail;
      const audio = audioRef.current;
      if (!audio || detail?.elementId !== elementId || !style.sourceUrl) {
        return;
      }
      audio.currentTime = 0;
      audio
        .play()
        .then(() => setIsPlaying(true))
        .catch(() => {
          // ignore playback errors (browser user gesture restrictions)
        });
    };
    const handleHoverStop = (event: Event) => {
      const detail = (event as CustomEvent<{ elementId?: string }>).detail;
      const audio = audioRef.current;
      if (!audio || detail?.elementId !== elementId) {
        return;
      }
      audio.pause();
      audio.currentTime = 0;
      setIsPlaying(false);
    };
    window.addEventListener("qe-audio-hover-play", handleHoverPlay);
    window.addEventListener("qe-audio-hover-stop", handleHoverStop);
    return () => {
      window.removeEventListener("qe-audio-hover-play", handleHoverPlay);
      window.removeEventListener("qe-audio-hover-stop", handleHoverStop);
    };
  }, [mode, style.playOnHover, style.sourceUrl, elementId]);

  React.useEffect(() => {
    const audio = audioRef.current;
    if (!audio) {
      return;
    }

    if (!style.sourceUrl) {
      audio.pause();
      audio.currentTime = 0;
      setIsPlaying(false);
      hasAttemptedAutoplayRef.current = false;
      return;
    }

    if (
      mode === "preview" &&
      style.autoPlay &&
      !hasAttemptedAutoplayRef.current
    ) {
      hasAttemptedAutoplayRef.current = true;
      audio
        .play()
        .then(() => {
          setIsPlaying(true);
        })
        .catch(() => {
          setIsPlaying(false);
        });
    }
  }, [style.sourceUrl, style.autoPlay, mode]);

  const containerStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "12px",
    backgroundColor: style.backgroundColor,
    border:
      style.borderWidth > 0
        ? `${style.borderWidth}px solid ${style.borderColor}`
        : "none",
    borderRadius: `${style.borderRadius}px`,
    padding: `${style.paddingY}px ${style.paddingX}px`,
    boxSizing: "border-box",
    userSelect: "none",
    cursor: style.sourceUrl && mode === "preview" ? "pointer" : "default",
  };

  const iconStyle: React.CSSProperties = {
    width: 32,
    height: 32,
    borderRadius: 999,
    backgroundColor: style.iconColor,
    color: "#ffffff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 600,
    fontSize: 14,
    flexShrink: 0,
  };

  const labelStyle: React.CSSProperties = {
    flex: 1,
    color: style.textColor,
    fontWeight: 600,
    overflow: "hidden",
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical" as const,
    lineHeight: 1.4,
    minWidth: 0,
  };

  return (
    <div
      style={containerStyle}
      role={mode === "preview" ? "button" : undefined}
      tabIndex={mode === "preview" ? 0 : undefined}
      onClick={mode === "preview" ? () => togglePlayback() : undefined}
      onKeyDown={
        mode === "preview"
          ? (event) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                togglePlayback();
              }
            }
          : undefined
      }
    >
      {style.showIcon && <div style={iconStyle}>{isPlaying ? "⏸" : "▶"}</div>}
      {style.showLabel && (
        <div style={labelStyle}>
          {style.label?.trim() || t("renderers.audio.fallbackLabel")}
          {!style.sourceUrl ? (
            <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 400 }}>
              {t("renderers.audio.addUrl")}
            </div>
          ) : null}
        </div>
      )}
      <audio
        ref={audioRef}
        src={style.sourceUrl || undefined}
        controls={style.showControls}
        controlsList="nodownload"
        onContextMenu={(event) => event.preventDefault()}
        style={{
          display: style.showControls ? "block" : "none",
          maxWidth: "100%",
        }}
      />
    </div>
  );
};

// Video Element Renderer
interface VideoElementRendererProps {
  elementId: string;
  style: VideoStyle;
  mode: "canvas" | "preview";
  renderOptions: RenderOptions;
}

const VideoElementRenderer: React.FC<VideoElementRendererProps> = ({
  style,
  mode,
  elementId,
  renderOptions,
}) => {
  const t = getT();
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const containerRef = React.useRef<HTMLDivElement | null>(null);
  const [isPlaying, setIsPlaying] = React.useState(false);
  const [hasEnded, setHasEnded] = React.useState(false);
  const hasAttemptedAutoplayRef = React.useRef(false);
  const [buttonSize, setButtonSize] = React.useState(80);

  // Dinamik buton boyutunu hesapla
  React.useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      return;
    }

    const updateButtonSize = () => {
      const width = container.offsetWidth;
      const height = container.offsetHeight;
      const minDimension = Math.min(width, height);
      // Buton boyutu, konteynerin minimum boyutunun %15'i olsun, min 32px, max 80px
      const newButtonSize = Math.max(32, Math.min(80, minDimension * 0.15));
      setButtonSize(newButtonSize);
    };

    updateButtonSize();

    const resizeObserver = new ResizeObserver(updateButtonSize);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [style.marginX, style.marginY]);

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    const handleEnded = () => {
      setIsPlaying(false);
      setHasEnded(true);

      if (mode === "preview") {
        // Video bittikten sonra görünecek elemanları göster
        if (
          style.showElementsAfterVideo &&
          style.showElementsAfterVideo.length > 0
        ) {
          window.dispatchEvent(
            new CustomEvent("show-elements-after-video", {
              detail: { elementIds: style.showElementsAfterVideo },
            })
          );
        }
      }
    };
    video.addEventListener("ended", handleEnded);
    return () => {
      video.removeEventListener("ended", handleEnded);
    };
  }, [style.showElementsAfterVideo, mode, elementId]);

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }
    video.loop = Boolean(style.loop);
  }, [style.loop]);

  React.useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    if (!style.sourceUrl) {
      video.pause();
      video.currentTime = 0;
      setIsPlaying(false);
      setHasEnded(false);
      hasAttemptedAutoplayRef.current = false;
      return;
    }

    if (
      mode === "preview" &&
      style.autoPlay &&
      !hasAttemptedAutoplayRef.current
    ) {
      hasAttemptedAutoplayRef.current = true;
      video
        .play()
        .then(() => {
          setIsPlaying(true);
          // Elementler zaten başlangıçta gizli, gizlemeye gerek yok
        })
        .catch(() => {
          setIsPlaying(false);
        });
    }
  }, [style.sourceUrl, style.autoPlay, mode]);

  const containerStyle: React.CSSProperties = {
    width: `calc(100% - ${style.marginX * 2}px)`,
    height: `calc(100% - ${style.marginY * 2}px)`,
    border:
      style.borderWidth > 0
        ? `${style.borderWidth}px solid ${style.borderColor}`
        : "none",
    borderRadius: `${style.borderRadius}px`,
    overflow: "hidden",
    boxSizing: "border-box",
    position: "relative",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    margin: `${style.marginY}px ${style.marginX}px`,
  };

  const videoStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    objectFit: "contain",
    pointerEvents: mode === "canvas" ? "none" : "auto",
  };

  const controlButtonStyle: React.CSSProperties = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: `${buttonSize}px`,
    height: `${buttonSize}px`,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    border: "3px solid white",
    zIndex: 10,
    transition: "transform 0.2s",
    pointerEvents: "none", // Buton kendisi tıklanabilir olmayacak, overlay tıklanacak
  };

  const overlayStyle: React.CSSProperties = {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    zIndex: 5,
    opacity: isPlaying ? 0 : 1,
    transition: "opacity 0.2s",
    pointerEvents: mode === "canvas" || isPlaying ? "none" : "auto",
  };

  const handlePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) {
      video
        .play()
        .then(() => {
          setIsPlaying(true);
        })
        .catch(() => {
          setIsPlaying(false);
        });
    }
  };

  const handleReplay = () => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    setHasEnded(false);

    // Video bir kez izlendiyse, elementler görünür kalmalı
    // Tekrar gizlemeye gerek yok

    video
      .play()
      .then(() => {
        setIsPlaying(true);
        setHasEnded(false);
      })
      .catch(() => {
        setIsPlaying(false);
      });
  };

  const handlePause = () => {
    const video = videoRef.current;
    if (!video) return;
    video.pause();
    setIsPlaying(false);
    setHasEnded(false); // Video durduğunda da hasEnded'i false yap
  };

  const handleTogglePlayPause = (e: React.MouseEvent) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (!video) return;

    if (video.paused) {
      // Play yapıldığında hasEnded'i temizle
      // Video bir kez izlendiyse elementler görünür kalmalı
      setHasEnded(false);
      video
        .play()
        .then(() => {
          setIsPlaying(true);
        })
        .catch(() => {
          setIsPlaying(false);
        });
    } else {
      // Pause yapıldığında hasEnded'i temizle
      video.pause();
      setIsPlaying(false);
      setHasEnded(false);
    }
  };

  const iconSize = buttonSize * 0.4; // Icon size is 40% of button size

  return (
    <div ref={containerRef} style={containerStyle}>
      {!style.sourceUrl && (
        <div
          style={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            color: "#6b7280",
            fontSize: 14,
            textAlign: "center",
          }}
        >
          {t("renderers.video.addUrl")}
        </div>
      )}
      <video
        ref={videoRef}
        src={style.sourceUrl || undefined}
        {...(style.showControls ? { controls: true } : {})}
        style={videoStyle}
        playsInline
        onClick={handleTogglePlayPause}
      />
      {!style.showControls && style.sourceUrl && (
        <div style={overlayStyle} onClick={handleTogglePlayPause}>
          {hasEnded && !isPlaying && (
            <div style={controlButtonStyle}>
              <svg
                width={iconSize}
                height={iconSize}
                viewBox="0 0 24 24"
                fill="white"
              >
                <path d="M12 5V1L7 6l5 5V7c3.31 0 6 2.69 6 6s-2.69 6-6 6-6-2.69-6-6H4c0 4.42 3.58 8 8 8s8-3.58 8-8-3.58-8-8-8z" />
              </svg>
            </div>
          )}
          {!hasEnded && !isPlaying && (
            <div style={controlButtonStyle}>
              <svg
                width={iconSize}
                height={iconSize}
                viewBox="0 0 24 24"
                fill="white"
              >
                <path d="M8 5v14l11-7z" />
              </svg>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// Drag Source Renderer
interface DragSourceRendererProps {
  elementId: string;
  label: string;
  value: string;
  appearance: DragSourceAppearance;
  mode: "canvas" | "preview";
  size?: { width: number; height?: number };
}

const DragSourceRenderer: React.FC<DragSourceRendererProps> = ({
  elementId,
  label,
  value,
  appearance,
  mode,
  size,
}) => {
  const t = getT();
  const [isHidden, setIsHidden] = React.useState(false);
  const shouldRespondToUsageEvents =
    mode === "preview" &&
    (!appearance.clonable || (appearance.clonable && appearance.maxClones > 0));

  React.useEffect(() => {
    if (!shouldRespondToUsageEvents) {
      setIsHidden(false);
    }
  }, [shouldRespondToUsageEvents]);

  const labelPositionStyle = mapLabelPosition(appearance.labelPosition);
  const useImage = appearance.useImage && appearance.imageUrl?.trim() !== "";
  // Geometrik şekil modu — resim modu kapalıyken geçerli.
  const useShape = appearance.useShape === true && !useImage;
  const showLabelPreference = appearance.showLabel !== false;
  const labelText = label ?? "";
  const hasLabel = labelText.trim().length > 0;
  const showLabel = hasLabel && showLabelPreference;
  const altText =
    appearance.imageAlt?.trim() ||
    (hasLabel ? labelText : t("renderers.dragSource.accessibleLabel"));
  const accessibleLabel = hasLabel ? labelText : t("renderers.dragSource.accessibleLabel");

  // Listen for drag-source-used events to hide elements when necessary
  React.useEffect(() => {
    if (!shouldRespondToUsageEvents) return;

    const handleDragSourceUsed = (e: CustomEvent<{ sourceId: string }>) => {
      if (e.detail.sourceId === elementId) {
        setIsHidden(true);
      }
    };

    window.addEventListener(
      "drag-source-used",
      handleDragSourceUsed as EventListener
    );
    return () => {
      window.removeEventListener(
        "drag-source-used",
        handleDragSourceUsed as EventListener
      );
    };
  }, [shouldRespondToUsageEvents, elementId]);

  // Listen for drag-source-freed events to show elements again
  React.useEffect(() => {
    if (!shouldRespondToUsageEvents) return;

    const handleDragSourceFreed = (e: CustomEvent<{ sourceId: string }>) => {
      if (e.detail.sourceId === elementId) {
        setIsHidden(false);
      }
    };

    window.addEventListener(
      "drag-source-freed",
      handleDragSourceFreed as EventListener
    );
    return () => {
      window.removeEventListener(
        "drag-source-freed",
        handleDragSourceFreed as EventListener
      );
    };
  }, [shouldRespondToUsageEvents, elementId]);

  if (isHidden && mode === "preview") {
    return null;
  }

  const containerStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: labelPositionStyle.alignItems,
    justifyContent: labelPositionStyle.justifyContent,
    backgroundColor:
      useImage || useShape ? "transparent" : appearance.backgroundColor,
    color: appearance.textColor,
    border: useShape
      ? "none"
      : `${appearance.borderWidth}px solid ${appearance.borderColor}`,
    borderRadius: `${appearance.borderRadius}px`,
    padding:
      useImage || useShape
        ? 0
        : `${appearance.paddingY}px ${appearance.paddingX}px`,
    boxSizing: "border-box",
    cursor: mode === "preview" ? "grab" : "default",
    userSelect: "none",
    transition: "opacity 0.2s",
    textAlign: labelPositionStyle.textAlign,
    position: "relative",
    overflow: useImage ? "hidden" : "visible",
  };

  // Şekil modu için sentetik "shape" elemanı (ShapeRenderer'ı yeniden
  // kullanır). 100% dolar.
  const shapeElement: EditorElement = {
    id: elementId,
    type: "shape",
    position: { x: 0, y: 0 },
    size: size ?? { width: 100, height: 100 },
    zIndex: 0,
    data: {
      kind: (appearance.shapeKind ?? "circle") as ShapeKind,
      fill: appearance.shapeFill ?? appearance.backgroundColor,
      stroke: appearance.shapeStroke ?? appearance.borderColor,
      strokeWidth: appearance.shapeStrokeWidth ?? appearance.borderWidth,
      strokeStyle: "solid",
    },
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    if (mode !== "preview") return;
    e.dataTransfer.effectAllowed = appearance.clonable ? "copy" : "move";
    e.dataTransfer.setData("application/x-drag-source-id", elementId);
    e.dataTransfer.setData("application/x-drag-source-value", value);
    e.dataTransfer.setData(
      "application/x-drag-source-clonable",
      appearance.clonable.toString()
    );
  };

  const handleDragEnd = () => {};

  const shouldRenderLabel = (showLabel || appearance.clonable) && hasLabel;

  return (
    <div
      style={containerStyle}
      draggable={true}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      className={`qe-drag-source${useImage ? " qe-drag-source--image" : ""}`}
      aria-label={accessibleLabel}
    >
      {useImage ? (
        <img
          src={appearance.imageUrl}
          alt={altText}
          className="qe-drag-source__image"
          loading="lazy"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: appearance.imageObjectFit,
            pointerEvents: "none",
          }}
        />
      ) : null}
      {useShape ? (
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          <ShapeRenderer element={shapeElement} mode="canvas" />
        </div>
      ) : null}
      {shouldRenderLabel ? (
        <span
          style={{
            fontWeight: 600,
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: useImage ? "6px 10px" : 0,
            backgroundColor: useImage ? "rgba(0, 0, 0, 0.4)" : "transparent",
            color: useImage ? "#ffffff" : appearance.textColor,
            borderRadius: useImage ? 8 : 0,
          }}
        >
          {showLabel ? labelText : null}
        </span>
      ) : null}
    </div>
  );
};

// Dropped Item Renderer (for individual items inside drop-target)
interface DroppedItemRendererProps {
  item: {
    instanceId: string;
    baseId: string;
    label: string;
    elementId: string;
    appearance: DragSourceAppearance;
    size: { width: number; height?: number };
  };
  mode: "canvas" | "preview";
  dropTargetId: string;
  onRemoveItem?: (instanceId: string, baseId?: string) => void;
  dropArea?: DropAreaConfig;
  cloneIndex: number;
  cloneCount: number;
}

const DroppedItemRenderer: React.FC<DroppedItemRendererProps> = ({
  item,
  mode,
  dropTargetId,
  onRemoveItem,
  dropArea,
  cloneIndex,
  cloneCount,
}) => {
  const t = getT();
  const labelPositionStyle = mapLabelPosition(item.appearance.labelPosition);
  const useImage =
    item.appearance.useImage && item.appearance.imageUrl?.trim() !== "";
  // Geometrik şekil modu — resim modu kapalıyken geçerli. Bırakılan öğe de
  // kaynak öğe gibi şekil olarak çizilir (aksi halde dikdörtgene düşerdi).
  const useShape = item.appearance.useShape === true && !useImage;
  const labelText = item.label ?? "";
  const hasLabel = labelText.trim().length > 0;
  const showLabelPreference = item.appearance.showLabel !== false;
  const shouldRenderLabel = hasLabel && showLabelPreference;
  const altText =
    item.appearance.imageAlt?.trim() ||
    (hasLabel ? labelText : t("renderers.dragSource.accessibleLabel"));
  const shouldShowNumbering = Boolean(
    item.appearance.clonable && item.appearance.showNumbering
  );
  const maxCloneLimit = item.appearance.maxClones ?? 0;
  const numberingText = shouldShowNumbering ? `${cloneIndex}` : null;
  const totalForLabel = cloneCount > 0 ? cloneCount : cloneIndex;
  const numberingTooltip = shouldShowNumbering
    ? maxCloneLimit > 0
      ? `Kopya ${cloneIndex}/${maxCloneLimit}`
      : `Kopya ${cloneIndex}/${totalForLabel}`
    : null;
  const numberingFontSize = Math.max(
    8,
    item.appearance.numberingFontSize ?? 11
  );
  const numberingSize = Math.max(24, Math.round(numberingFontSize * 2));
  const numberingTextColor =
    item.appearance.numberingTextColor ?? "#f9fafb";
  const numberingBackgroundColor =
    item.appearance.numberingBackgroundColor ?? "rgba(15, 23, 42, 0.9)";
  const numberingHasBackground = numberingBackgroundColor !== "transparent";
  const numberingBorderRadius = Math.max(
    0,
    item.appearance.numberingBorderRadius ?? 999
  );
  const numberingPosition = item.appearance.numberingPosition ?? "top-left";
  const numberingInset = 6;
  const numberingPositionStyle: React.CSSProperties =
    numberingPosition === "center"
      ? { top: "50%", left: "50%", transform: "translate(-50%, -50%)" }
      : numberingPosition === "top-right"
      ? { top: numberingInset, right: numberingInset }
      : numberingPosition === "bottom-right"
      ? { bottom: numberingInset, right: numberingInset }
      : numberingPosition === "bottom-left"
      ? { bottom: numberingInset, left: numberingInset }
      : { top: numberingInset, left: numberingInset };
  const accessibleLabel = (() => {
    if (hasLabel) {
      if (numberingText) {
        if (maxCloneLimit > 0) {
          return `${labelText} (kopya ${cloneIndex}/${maxCloneLimit})`;
        }
        return `${labelText} (kopya ${cloneIndex}/${totalForLabel})`;
      }
      return labelText;
    }
    if (numberingText) {
      if (maxCloneLimit > 0) {
        return `${t("renderers.dragSource.accessibleLabel")} (kopya ${cloneIndex}/${maxCloneLimit})`;
      }
      return `${t("renderers.dragSource.accessibleLabel")} (kopya ${cloneIndex}/${totalForLabel})`;
    }
    return t("renderers.dragSource.accessibleLabel");
  })();
  // Normalize string "0"/"1" to boolean
  const dropAreaEnabled = dropArea?.enabled
    ? (dropArea.enabled as any) === "1" ||
      (dropArea.enabled as any) === true ||
      (dropArea.enabled as any) === 1
    : false;
  const dropAreaWidth = dropAreaEnabled ? dropArea!.width : undefined;
  const dropAreaHeight = dropAreaEnabled ? dropArea!.height : undefined;
  const [isDragging, setIsDragging] = React.useState(false);

  const handleItemDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    if (mode !== "preview") {
      e.preventDefault();
      return;
    }
    setIsDragging(true);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/x-drag-source-id", item.baseId);
    e.dataTransfer.setData("application/x-drag-source-value", item.baseId);
    e.dataTransfer.setData("application/x-dropped-item-id", item.instanceId);
    e.dataTransfer.setData("application/x-dropped-item-base-id", item.baseId);
    e.dataTransfer.setData("application/x-drop-target-id", dropTargetId);
    e.dataTransfer.setData(
      "application/x-drag-source-clonable",
      item.appearance.clonable.toString()
    );
  };

  const handleItemDragEnd = () => {
    setIsDragging(false);
  };

  const baseWidth = item.size.width ?? 0;
  const baseHeight = item.size.height ?? undefined;
  const constrainedWidth = (() => {
    if (baseWidth > 0 && dropAreaWidth) {
      return Math.min(baseWidth, dropAreaWidth);
    }
    if (baseWidth > 0) {
      return baseWidth;
    }
    if (dropAreaWidth) {
      return dropAreaWidth;
    }
    return undefined;
  })();
  const constrainedHeight = (() => {
    if (baseHeight && dropAreaHeight) {
      return Math.min(baseHeight, dropAreaHeight);
    }
    if (baseHeight) {
      return baseHeight;
    }
    return undefined;
  })();

  // Şekil modu için sentetik "shape" elemanı (ShapeRenderer'ı yeniden kullanır).
  const shapeElement: EditorElement = {
    id: item.instanceId,
    type: "shape",
    position: { x: 0, y: 0 },
    size: item.size ?? { width: 100, height: 100 },
    zIndex: 0,
    data: {
      kind: (item.appearance.shapeKind ?? "circle") as ShapeKind,
      fill: item.appearance.shapeFill ?? item.appearance.backgroundColor,
      stroke: item.appearance.shapeStroke ?? item.appearance.borderColor,
      strokeWidth: item.appearance.shapeStrokeWidth ?? item.appearance.borderWidth,
      strokeStyle: "solid",
    },
  };

  const containerStyle: React.CSSProperties = {
    width: constrainedWidth ? `${constrainedWidth}px` : "auto",
    height: constrainedHeight ? `${constrainedHeight}px` : "auto",
    display: "flex",
    alignItems: labelPositionStyle.alignItems,
    justifyContent: labelPositionStyle.justifyContent,
    backgroundColor:
      useImage || useShape ? "transparent" : item.appearance.backgroundColor,
    color: item.appearance.textColor,
    border: useShape
      ? "none"
      : `${item.appearance.borderWidth}px solid ${item.appearance.borderColor}`,
    borderRadius: `${item.appearance.borderRadius}px`,
    padding:
      useImage || useShape
        ? 0
        : `${item.appearance.paddingY}px ${item.appearance.paddingX}px`,
    boxSizing: "border-box",
    userSelect: "none",
    textAlign: labelPositionStyle.textAlign,
    cursor: mode === "preview" ? "grab" : "default",
    maxWidth: dropAreaWidth ? `${dropAreaWidth}px` : undefined,
    maxHeight: dropAreaHeight ? `${dropAreaHeight}px` : undefined,
    transition: isDragging
      ? "opacity 0.2s"
      : "all 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s",
    opacity: isDragging ? 0.5 : 1,
    transform: isDragging ? "scale(0.95)" : "scale(1)",
    position: "relative",
    overflow: useImage ? "hidden" : "visible",
  };

  return (
    <div
      draggable={true}
      onDragStart={handleItemDragStart}
      onDragEnd={handleItemDragEnd}
      style={containerStyle}
      className={`qe-drag-source${useImage ? " qe-drag-source--image" : ""}`}
      aria-label={accessibleLabel}
      data-base-id={item.baseId}
    >
      {shouldShowNumbering ? (
        <span
          aria-hidden
          style={{
            position: "absolute",
            width: numberingSize,
            height: numberingSize,
            borderRadius: `${numberingBorderRadius}px`,
            backgroundColor: numberingBackgroundColor,
            color: numberingTextColor,
            fontSize: `${numberingFontSize}px`,
            fontWeight: 600,
            lineHeight: 1,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
            zIndex: 2,
            boxShadow: numberingHasBackground
              ? "0 2px 4px rgba(15, 23, 42, 0.35)"
              : "none",
            ...numberingPositionStyle,
          }}
          title={numberingTooltip ?? undefined}
        >
          {numberingText}
        </span>
      ) : null}
      {useImage ? (
        <img
          src={item.appearance.imageUrl}
          alt={altText}
          className="qe-drag-source__image"
          loading="lazy"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: item.appearance.imageObjectFit,
            pointerEvents: "none",
          }}
        />
      ) : null}
      {useShape ? (
        <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
          <ShapeRenderer element={shapeElement} mode="canvas" />
        </div>
      ) : null}
      {shouldRenderLabel ? (
        <span
          style={{
            fontWeight: 600,
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: useImage ? "6px 10px" : 0,
            backgroundColor: useImage ? "rgba(0, 0, 0, 0.4)" : "transparent",
            color: useImage ? "#ffffff" : item.appearance.textColor,
            borderRadius: useImage ? 8 : 0,
          }}
        >
          {labelText}
        </span>
      ) : null}
    </div>
  );
};

interface DropTargetRendererProps {
  elementId: string;
  label?: string;
  acceptedSources?: string[];
  appearance: DropTargetAppearance;
  mode: "canvas" | "preview";
  formState?: Record<string, string | string[] | Record<string, string>>;
  allElements?: EditorElement[];
  onDragDropChange?: (
    targetId: string,
    sourceIds: string | string[],
    sourceId: string | null
  ) => void;
  dropArea?: DropAreaConfig;
  placeholder?:
    | string
    | { preview?: string | null; canvas?: string | null }
    | null;
}

const DropTargetRenderer: React.FC<DropTargetRendererProps> = ({
  elementId,
  label,
  acceptedSources,
  appearance,
  mode,
  formState,
  allElements,
  onDragDropChange,
  dropArea,
  placeholder,
}) => {
  const t = getT();
  const [isDragOver, setIsDragOver] = React.useState(false);
  const [isDragOutsideArea, setIsDragOutsideArea] = React.useState(false);
  const [droppedItemsState, setDroppedItemsState] = React.useState<
    Array<{ instanceId: string; baseId: string }>
  >([]);
  const dropTargetRef = React.useRef<HTMLDivElement>(null);
  const contentContainerRef = React.useRef<HTMLDivElement>(null);
  const labelText = label ?? "";
  const hasLabel = labelText.trim().length > 0;
  // Maksimum kapasite artık Cevap bölümündeki kabul edilen öğe adetleriyle
  // belirlenir; hedef alanda client-side sert sınır uygulanmaz.
  const maxItems = 0;

  const normalizedPlaceholder = React.useMemo(() => {
    // If placeholder is explicitly set as a string (including empty string), use it
    if (typeof placeholder === "string") {
      return placeholder;
    }
    // If placeholder is undefined/null, use default fallback
    return DEFAULT_DROP_TARGET_PLACEHOLDER();
  }, [placeholder]);

  const normalizedDropArea = React.useMemo(() => {
    if (!dropArea) {
      return null;
    }
    // Normalize enabled from string "0"/"1" to boolean
    const enabled =
      (dropArea.enabled as any) === "1" ||
      (dropArea.enabled as any) === true ||
      (dropArea.enabled as any) === 1;
    if (!enabled) {
      return null;
    }
    // Normalize highlightEnabled from string "0"/"1" to boolean
    const highlightEnabled =
      (dropArea.highlightEnabled as any) === "1" ||
      (dropArea.highlightEnabled as any) === true ||
      (dropArea.highlightEnabled as any) === 1;
    const highlightColor =
      typeof dropArea.highlightColor === "string"
        ? dropArea.highlightColor
        : DEFAULT_DROP_AREA_HIGHLIGHT;
    // Normalize showOutline from string "0"/"1" to boolean
    const showOutline =
      (dropArea.showOutline as any) === "1" ||
      (dropArea.showOutline as any) === true ||
      (dropArea.showOutline as any) === 1;
    return {
      ...dropArea,
      enabled: true, // Always true if we get here
      x: Math.max(
        0,
        typeof dropArea.x === "string"
          ? parseFloat(dropArea.x)
          : dropArea.x ?? 0
      ),
      y: Math.max(
        0,
        typeof dropArea.y === "string"
          ? parseFloat(dropArea.y)
          : dropArea.y ?? 0
      ),
      width: Math.max(
        0,
        typeof dropArea.width === "string"
          ? parseFloat(dropArea.width)
          : dropArea.width ?? 120
      ),
      height: Math.max(
        0,
        typeof dropArea.height === "string"
          ? parseFloat(dropArea.height)
          : dropArea.height ?? 80
      ),
      outlineWidth: Math.max(
        0,
        typeof dropArea.outlineWidth === "string"
          ? parseFloat(dropArea.outlineWidth)
          : dropArea.outlineWidth ?? 2
      ),
      showOutline,
      highlightEnabled,
      highlightColor,
    };
  }, [dropArea]);

  const isPointWithinDropArea = React.useCallback(
    (clientX: number, clientY: number) => {
      if (!normalizedDropArea || !dropTargetRef.current) {
        return true;
      }
      const rect = dropTargetRef.current.getBoundingClientRect();
      const offsetX = clientX - rect.left;
      const offsetY = clientY - rect.top;
      return (
        offsetX >= normalizedDropArea.x &&
        offsetX <= normalizedDropArea.x + normalizedDropArea.width &&
        offsetY >= normalizedDropArea.y &&
        offsetY <= normalizedDropArea.y + normalizedDropArea.height
      );
    },
    [normalizedDropArea]
  );

  // Check if all items can fit within the drop area
  // DISABLED: Always return true to allow items to be placed without space warnings
  // The drop target will automatically adjust or overflow as needed
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _canItemsFitInDropArea = React.useCallback(
    (
      _currentItems: Array<{ instanceId: string; baseId: string }>,
      _newItemBaseId: string
    ) => {
      // Always allow items to be dropped - no space restrictions
      return true;
    },
    []
  );

  // Initialize dropped items from formState (only in preview mode)
  React.useEffect(() => {
    if (mode !== "preview") return;
    const value = formState?.[elementId];
    if (Array.isArray(value)) {
      setDroppedItemsState(
        value.map((storedId, index) => {
          const [baseId] = storedId.split("::instance::");
          const instanceId = storedId.includes("::instance::")
            ? storedId
            : `${storedId}::instance::${index}_${Math.random()
                .toString(36)
                .slice(2, 8)}`;
          return { baseId, instanceId };
        })
      );
    } else if (typeof value === "string") {
      const [baseId] = value.split("::instance::");
      const instanceId = value.includes("::instance::")
        ? value
        : `${value}::instance::${Math.random().toString(36).slice(2, 8)}`;
      setDroppedItemsState([
        {
          baseId,
          instanceId,
        },
      ]);
    } else {
      setDroppedItemsState([]);
    }
  }, [formState, elementId, mode]);

  const handleRemoveItem = React.useCallback(
    (instanceId: string, baseId?: string) => {
      const resolvedBaseId = baseId ?? instanceId;
      setDroppedItemsState((prev) => {
        const newItems = prev.filter((item) => item.instanceId !== instanceId);

        if (onDragDropChange) {
          onDragDropChange(
            elementId,
            newItems.map((item) => item.instanceId),
            null
          );
        }

        window.dispatchEvent(
          new CustomEvent("drag-source-freed", {
            detail: { sourceId: resolvedBaseId },
          })
        );

        return newItems;
      });
    },
    [elementId, onDragDropChange]
  );

  // Note: Document-level drag handling removed to prevent overly sensitive drag-out detection
  // Items are now only removed when dropped on designated global drop zones (modal overlay, fullscreen background)

  // Reset drag-over state when global drop happens
  React.useEffect(() => {
    if (mode !== "preview") return;

    const handleDragSourceUsed = (e: CustomEvent) => {
      // Clear any drag-over visual state
      setIsDragOver(false);
    };

    window.addEventListener(
      "drag-source-used",
      handleDragSourceUsed as EventListener
    );
    return () => {
      window.removeEventListener(
        "drag-source-used",
        handleDragSourceUsed as EventListener
      );
    };
  }, [mode]);

  // Listen for remove requests from other drop targets
  React.useEffect(() => {
    if (mode !== "preview") return;

    const handleRemoveRequest = (e: CustomEvent) => {
      const { targetId, instanceId } = e.detail;
      if (targetId === elementId) {
        setDroppedItemsState((prev) => {
          const newItems = prev.filter(
            (item) => item.instanceId !== instanceId
          );
          if (onDragDropChange) {
            onDragDropChange(
              elementId,
              newItems.map((item) => item.instanceId),
              null
            );
          }
          // NOT sending 'drag-source-freed' event because item is being moved to another target, not freed
          return newItems;
        });
      }
    };

    window.addEventListener(
      "remove-from-drop-target",
      handleRemoveRequest as EventListener
    );
    return () => {
      window.removeEventListener(
        "remove-from-drop-target",
        handleRemoveRequest as EventListener
      );
    };
  }, [mode, elementId, onDragDropChange]);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (mode !== "preview") return;
    // Allow both palette sources and already placed items to be dropped back in
    const hasPaletteSource = e.dataTransfer.types.includes(
      "application/x-drag-source-value"
    );
    const hasDroppedItem = e.dataTransfer.types.includes(
      "application/x-dropped-item-id"
    );
    if (!hasPaletteSource && !hasDroppedItem) {
      return;
    }

    const sourceDropTargetId = e.dataTransfer.getData(
      "application/x-drop-target-id"
    );
    const isInternalMove = sourceDropTargetId === elementId;
    if (maxItems > 0 && droppedItemsState.length >= maxItems && !isInternalMove) {
      setIsDragOver(false);
      setIsDragOutsideArea(false);
      return;
    }

    const withinArea = isPointWithinDropArea(e.clientX, e.clientY);
    if (normalizedDropArea && !withinArea) {
      setIsDragOver(false);
      setIsDragOutsideArea(true);
      return;
    }

    e.preventDefault();
    e.stopPropagation();

    // Accept both copy and move operations
    e.dataTransfer.dropEffect =
      e.dataTransfer.effectAllowed === "copy" ? "copy" : "move";

    setIsDragOver(true);
    setIsDragOutsideArea(false);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
    setIsDragOutsideArea(false);
  };

  // Helper function to find the insertion index based on mouse position
  const findInsertionIndex = (
    clientX: number,
    clientY: number
  ): number | null => {
    if (!contentContainerRef.current) return null;

    const containerRect = contentContainerRef.current.getBoundingClientRect();
    const containerChildren = Array.from(
      contentContainerRef.current.children
    ) as HTMLElement[];

    if (containerChildren.length === 0) {
      return 0;
    }

    // Get the layout direction from computed style
    const computedStyle = window.getComputedStyle(contentContainerRef.current);
    const flexDirection = computedStyle.flexDirection;
    const isHorizontal =
      flexDirection === "row" || flexDirection === "row-reverse";

    // Calculate relative position within container
    const relativeX = clientX - containerRect.left;
    const relativeY = clientY - containerRect.top;

    // Find the insertion point based on layout direction
    for (let i = 0; i < containerChildren.length; i++) {
      const childRect = containerChildren[i].getBoundingClientRect();
      const childRelativeX = childRect.left - containerRect.left;
      const childRelativeY = childRect.top - containerRect.top;
      const childCenterX = childRelativeX + childRect.width / 2;
      const childCenterY = childRelativeY + childRect.height / 2;

      if (isHorizontal) {
        // For horizontal layout, compare X positions
        if (relativeX < childCenterX) {
          return i;
        }
      } else {
        // For vertical layout, compare Y positions
        if (relativeY < childCenterY) {
          return i;
        }
      }
    }

    // If we get here, the drop is after all items
    return containerChildren.length;
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (mode !== "preview") return;
    const withinArea = isPointWithinDropArea(e.clientX, e.clientY);
    if (normalizedDropArea && !withinArea) {
      setIsDragOver(false);
      setIsDragOutsideArea(true);
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    setIsDragOutsideArea(false);

    const draggedSourceId = e.dataTransfer.getData(
      "application/x-drag-source-id"
    );
    const draggedSourceValue = e.dataTransfer.getData(
      "application/x-drag-source-value"
    );
    const droppedItemId = e.dataTransfer.getData(
      "application/x-dropped-item-id"
    );
    const sourceDropTargetId = e.dataTransfer.getData(
      "application/x-drop-target-id"
    );

    if (!draggedSourceId || !draggedSourceValue) return;
    const isInternalMove =
      Boolean(droppedItemId) && sourceDropTargetId === elementId;
    if (maxItems > 0 && droppedItemsState.length >= maxItems && !isInternalMove) {
      return;
    }

    // Check if this source value is accepted (empty array means accept all)
    const isAccepted =
      !acceptedSources ||
      acceptedSources.length === 0 ||
      acceptedSources.includes(draggedSourceValue);
    if (!isAccepted) return;

    // Check if the source element is clonable
    const sourceElement = allElements?.find((el) => el.id === draggedSourceId);
    const sourceAppearance = sourceElement
      ? resolveDragSourceAppearance(sourceElement)
      : null;
    const isClonable = sourceAppearance?.clonable ?? false;
    const maxClones = sourceAppearance?.maxClones ?? 0;

    const baseId = draggedSourceValue;

    const currentCountForBase = droppedItemsState.filter(
      (entry) => entry.baseId === baseId
    ).length;
    const limitReached =
      isClonable && maxClones > 0 && currentCountForBase >= maxClones;

    // Find insertion index based on mouse position
    const insertionIndex = findInsertionIndex(e.clientX, e.clientY);

    // If dragging an item that was already in a drop target
    if (droppedItemId && sourceDropTargetId) {
      // Remove the item from its previous location if it's the same drop target or moving between targets
      if (sourceDropTargetId === elementId) {
        // Reordering within the same target
        const currentIndex = droppedItemsState.findIndex(
          (item) => item.instanceId === droppedItemId
        );

        if (currentIndex === -1) {
          return;
        }

        // If dropping at the same position, do nothing
        if (insertionIndex !== null && insertionIndex === currentIndex) {
          return;
        }

        // Remove the item from its current position
        const newDroppedItems = [...droppedItemsState];
        const [movedItem] = newDroppedItems.splice(currentIndex, 1);

        // Calculate the new insertion index after removal
        let newInsertionIndex = insertionIndex ?? newDroppedItems.length;
        if (newInsertionIndex > currentIndex) {
          newInsertionIndex -= 1;
        }

        // Insert at the new position
        newDroppedItems.splice(newInsertionIndex, 0, movedItem);

        setDroppedItemsState(newDroppedItems);
        if (onDragDropChange) {
          onDragDropChange(
            elementId,
            newDroppedItems.map((item) => item.instanceId),
            draggedSourceId
          );
        }
        return;
      } else {
        // Moving from another drop target - remove from source first
        window.dispatchEvent(
          new CustomEvent("remove-from-drop-target", {
            detail: { targetId: sourceDropTargetId, instanceId: droppedItemId },
          })
        );

        if (limitReached) {
          return;
        }

        // Space check disabled - items are always allowed

        const newInstanceId = isClonable
          ? `${baseId}::instance::${Date.now().toString(36)}_${Math.random()
              .toString(36)
              .slice(2, 8)}`
          : droppedItemId;

        const newDroppedItems = [...droppedItemsState];
        const insertAt = insertionIndex ?? newDroppedItems.length;
        newDroppedItems.splice(insertAt, 0, {
          instanceId: newInstanceId,
          baseId,
        });

        setDroppedItemsState(newDroppedItems);
        if (onDragDropChange) {
          onDragDropChange(
            elementId,
            newDroppedItems.map((item) => item.instanceId),
            draggedSourceId
          );
        }
        return;
      }
    }

    // Dragging from the palette (original drag source element)
    if (limitReached) {
      return;
    }

    // Check if the item will fit in the drop area
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const _prospectiveItems =
      isClonable || !droppedItemsState.some((item) => item.baseId === baseId)
        ? [...droppedItemsState, { instanceId: "", baseId }]
        : droppedItemsState; // Item already exists, no space check needed

    if (
      !isClonable &&
      droppedItemsState.some((item) => item.baseId === baseId)
    ) {
      // Item already exists and is not clonable
      return;
    }

    // Space check disabled - items are always allowed

    const instanceId = isClonable
      ? `${baseId}::instance::${Date.now().toString(36)}_${Math.random()
          .toString(36)
          .slice(2, 8)}`
      : baseId;

    const newDroppedItems = [...droppedItemsState];
    const insertAt = insertionIndex ?? newDroppedItems.length;

    if (isClonable) {
      newDroppedItems.splice(insertAt, 0, { instanceId, baseId });
    } else if (!droppedItemsState.some((item) => item.baseId === baseId)) {
      newDroppedItems.splice(insertAt, 0, { instanceId: baseId, baseId });
    }

    setDroppedItemsState(newDroppedItems);

    // Notify parent component
    if (onDragDropChange) {
      onDragDropChange(
        elementId,
        newDroppedItems.map((item) => item.instanceId),
        draggedSourceId
      );
    }
  };

  const labelPositionStyle = mapLabelPosition(appearance.labelPosition);
  const showBackgroundImage =
    appearance.useBackgroundImage &&
    appearance.backgroundImageUrl.trim() !== "";

  const containerStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: labelPositionStyle.alignItems,
    justifyContent: labelPositionStyle.justifyContent,
    backgroundColor: isDragOver
      ? appearance.borderColor + "20"
      : appearance.backgroundColor,
    border: showBackgroundImage
      ? "none"
      : `${appearance.borderWidth}px ${appearance.borderStyle} ${appearance.borderColor}`,
    borderColor: isDragOutsideArea ? "#dc2626" : appearance.borderColor,
    borderRadius: `${appearance.borderRadius}px`,
    padding: `${appearance.paddingY}px ${appearance.paddingX}px`,
    boxSizing: "border-box",
    transition: "background-color 0.2s, border-color 0.2s, box-shadow 0.2s",
    gap: "12px",
    position: "relative",
    overflow: "hidden",
    boxShadow: showBackgroundImage
      ? `inset 0 0 0 ${Math.max(0, appearance.borderWidth)}px ${
          appearance.borderColor
        }`
      : isDragOver
      ? "0 0 0 2px rgba(37, 99, 235, 0.18)"
      : undefined,
  };

  // Etiket, hedef alanın ÜST kenarında başlık gibi durur (öğe sınırları
  // canvas tarafından kırpıldığından tam dışarı taşırılamaz).
  const labelStyle: React.CSSProperties = {
    position: "absolute",
    top: 6,
    left: 8,
    color: appearance.labelTextColor,
    backgroundColor: appearance.labelBackground,
    padding: `${appearance.labelPaddingY}px ${appearance.labelPaddingX}px`,
    borderRadius: `${appearance.labelBorderRadius}px`,
    boxShadow: appearance.labelShadow,
    fontSize: "14px",
    fontWeight: 600,
    userSelect: "none",
    whiteSpace: "nowrap",
    textAlign: labelPositionStyle.textAlign,
    zIndex: 2,
    pointerEvents: "none",
  };

  // Izgara düzeni — hedef alanı eşit hücrelere böler; çizgiler eşit
  // aralıklı (her köşeden eşit) görünür.
  const useGrid = appearance.useGrid === true;
  const gridRows = Math.max(1, appearance.gridRows ?? 2);
  const gridCols = Math.max(1, appearance.gridCols ?? 3);
  const showGridLines = appearance.showGridLines !== false;
  const gridLineColor = appearance.gridLineColor ?? "rgba(15,23,42,0.08)";

  const contentContainerStyle: React.CSSProperties = useGrid
    ? {
        display: "grid",
        gridTemplateRows: `repeat(${gridRows}, 1fr)`,
        gridTemplateColumns: `repeat(${gridCols}, 1fr)`,
        gap: 0,
        width: "100%",
        height: "100%",
        placeItems: "center",
        backgroundImage: showGridLines
          ? `linear-gradient(to right, ${gridLineColor} 1px, transparent 1px),` +
            `linear-gradient(to bottom, ${gridLineColor} 1px, transparent 1px)`
          : undefined,
        backgroundSize: showGridLines
          ? `calc(100% / ${gridCols}) calc(100% / ${gridRows})`
          : undefined,
        // İç çizgiler her hücrenin sol/üstüne düşer; en sağ ve en alt dış
        // kenar çizgisi bu inset gölge ile tamamlanır (sol/üstle simetrik).
        boxShadow: showGridLines
          ? `inset 0 0 0 1px ${gridLineColor}`
          : undefined,
        zIndex: 1,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }
    : normalizedDropArea
    ? {
        position: "absolute",
        left: `${normalizedDropArea.x}px`,
        top: `${normalizedDropArea.y}px`,
        width: `${normalizedDropArea.width}px`,
        height: `${normalizedDropArea.height}px`,
        display: "flex",
        flexDirection:
          appearance.contentLayout === "horizontal" ? "row" : "column",
        flexWrap: appearance.contentLayout === "horizontal" ? "wrap" : "nowrap",
        gap: `${appearance.itemSpacing}px`,
        alignItems:
          appearance.contentLayout === "horizontal"
            ? "center"
            : appearance.contentAlignment,
        justifyContent:
          appearance.contentLayout === "horizontal"
            ? appearance.contentAlignment
            : "center",
        zIndex: 1,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      }
    : {
        display: "flex",
        flexDirection:
          appearance.contentLayout === "horizontal" ? "row" : "column",
        flexWrap: appearance.contentLayout === "horizontal" ? "wrap" : "nowrap",
        gap: `${appearance.itemSpacing}px`,
        width: "100%",
        alignItems:
          appearance.contentLayout === "horizontal"
            ? "center"
            : appearance.contentAlignment,
        justifyContent:
          appearance.contentLayout === "horizontal"
            ? appearance.contentAlignment
            : "flex-start",
        zIndex: 1,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      };

  // Get drag source elements to display with their original appearance and size
  const droppedItems = React.useMemo(() => {
    if (!allElements) return [];
    const rawItems = droppedItemsState
      .map((entry) => {
        const sourceElement = allElements.find(
          (el) => el.type === "drag-source" && el.id === entry.baseId
        );
        return sourceElement
          ? {
              instanceId: entry.instanceId,
              baseId: entry.baseId,
              label: (() => {
                const raw = (sourceElement.data as any).label;
                return typeof raw === "string" ? raw : t("renderers.droppedItem.defaultLabel");
              })(),
              elementId: sourceElement.id,
              appearance: resolveDragSourceAppearance(sourceElement),
              size: sourceElement.size,
            }
          : null;
      })
      .filter(Boolean) as Array<{
      instanceId: string;
      baseId: string;
      label: string;
      elementId: string;
      appearance: DragSourceAppearance;
      size: { width: number; height?: number };
    }>;
    const totals: Record<string, number> = {};
    rawItems.forEach((item) => {
      totals[item.baseId] = (totals[item.baseId] ?? 0) + 1;
    });
    const running: Record<string, number> = {};
    return rawItems.map((item) => {
      const nextIndex = (running[item.baseId] ?? 0) + 1;
      running[item.baseId] = nextIndex;
      return {
        ...item,
        cloneIndex: nextIndex,
        cloneCount: totals[item.baseId],
      };
    });
  }, [droppedItemsState, allElements]);

  const dropAreaOverlay =
    normalizedDropArea &&
    (normalizedDropArea.showOutline || normalizedDropArea.highlightEnabled) ? (
      <div
        style={{
          position: "absolute",
          left: `${normalizedDropArea.x}px`,
          top: `${normalizedDropArea.y}px`,
          width: `${normalizedDropArea.width}px`,
          height: `${normalizedDropArea.height}px`,
          border: normalizedDropArea.showOutline
            ? `${normalizedDropArea.outlineWidth}px dashed ${normalizedDropArea.outlineColor}`
            : "none",
          borderRadius: `${Math.max(
            0,
            appearance.borderRadius - appearance.borderWidth
          )}px`,
          pointerEvents: "none",
          backgroundColor:
            normalizedDropArea.highlightEnabled && isDragOver
              ? normalizedDropArea.highlightColor
              : "transparent",
          transition: "background-color 0.2s ease",
          zIndex: 0,
        }}
      />
    ) : null;

  const usageSignalRef = React.useRef<Record<string, boolean>>({});

  React.useEffect(() => {
    if (mode !== "preview") {
      if (Object.keys(usageSignalRef.current).length > 0) {
        Object.entries(usageSignalRef.current).forEach(
          ([sourceId, wasHidden]) => {
            if (wasHidden) {
              window.dispatchEvent(
                new CustomEvent("drag-source-freed", {
                  detail: { sourceId },
                })
              );
            }
          }
        );
        usageSignalRef.current = {};
      }
      return;
    }

    if (!allElements || allElements.length === 0) {
      return;
    }

    // Count items across ALL drop targets, not just this one
    const counts: Record<string, number> = {};

    // Count items in this drop target
    droppedItemsState.forEach((entry) => {
      counts[entry.baseId] = (counts[entry.baseId] ?? 0) + 1;
    });

    // Also count items in other drop targets via formState
    if (formState) {
      allElements
        .filter((el) => el.type === "drop-target" && el.id !== elementId)
        .forEach((dropTarget) => {
          const value = formState[dropTarget.id];
          if (Array.isArray(value)) {
            value.forEach((itemId) => {
              const [baseId] = itemId.split("::instance::");
              counts[baseId] = (counts[baseId] ?? 0) + 1;
            });
          } else if (typeof value === "string") {
            const [baseId] = value.split("::instance::");
            counts[baseId] = (counts[baseId] ?? 0) + 1;
          }
        });
    }

    const nextSignals: Record<string, boolean> = {};

    allElements
      .filter((el) => el.type === "drag-source")
      .forEach((sourceElement) => {
        const appearanceForSource = resolveDragSourceAppearance(sourceElement);
        const placements = counts[sourceElement.id] ?? 0;
        let shouldHide = false;
        if (!appearanceForSource.clonable) {
          shouldHide = placements > 0;
        } else if (appearanceForSource.maxClones > 0) {
          shouldHide = placements >= appearanceForSource.maxClones;
        }

        const wasHidden = usageSignalRef.current[sourceElement.id] ?? false;
        if (shouldHide && !wasHidden) {
          window.dispatchEvent(
            new CustomEvent("drag-source-used", {
              detail: { sourceId: sourceElement.id },
            })
          );
        } else if (!shouldHide && wasHidden) {
          window.dispatchEvent(
            new CustomEvent("drag-source-freed", {
              detail: { sourceId: sourceElement.id },
            })
          );
        }

        nextSignals[sourceElement.id] = shouldHide;
      });

    // Free any sources no longer present in the canvas but previously hidden
    Object.entries(usageSignalRef.current).forEach(([sourceId, wasHidden]) => {
      if (
        wasHidden &&
        !Object.prototype.hasOwnProperty.call(nextSignals, sourceId)
      ) {
        window.dispatchEvent(
          new CustomEvent("drag-source-freed", {
            detail: { sourceId },
          })
        );
      }
    });

    usageSignalRef.current = nextSignals;
  }, [mode, allElements, droppedItemsState, formState, elementId]);

  React.useEffect(() => {
    return () => {
      if (Object.keys(usageSignalRef.current).length > 0) {
        Object.entries(usageSignalRef.current).forEach(
          ([sourceId, wasHidden]) => {
            if (wasHidden) {
              window.dispatchEvent(
                new CustomEvent("drag-source-freed", {
                  detail: { sourceId },
                })
              );
            }
          }
        );
        usageSignalRef.current = {};
      }
    };
  }, []);

  return (
    <div style={{ position: "relative", width: "100%", height: "100%" }}>
      {/* Etiket öğenin dışında (üstünde) başlık gibi */}
      {hasLabel ? <div style={labelStyle}>{labelText}</div> : null}
      <div
        ref={dropTargetRef}
        style={containerStyle}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`qe-drop-target${
          showBackgroundImage ? " qe-drop-target--with-image" : ""
        }`}
      >
        {showBackgroundImage ? (
          <img
            src={appearance.backgroundImageUrl}
            alt=""
            aria-hidden
            loading="lazy"
            className="qe-drop-target__image"
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              objectFit: appearance.backgroundImageFit,
              objectPosition: appearance.backgroundImagePosition,
              opacity: appearance.backgroundImageOpacity,
              pointerEvents: "none",
            }}
          />
        ) : null}
        {dropAreaOverlay}
        <div ref={contentContainerRef} style={contentContainerStyle}>
        {droppedItems.length === 0 &&
          (() => {
            const trimmed = normalizedPlaceholder.trim();
            if (trimmed.length === 0) {
              return null;
            }
            return (
              <div
                style={{
                  color: "#9ca3af",
                  fontSize: "12px",
                  fontStyle: "italic",
                  textAlign: "center",
                  pointerEvents: "none",
                }}
              >
                {trimmed}
              </div>
            );
          })()}
        {droppedItems.map((item) => (
          <DroppedItemRenderer
            key={item.instanceId}
            item={item}
            mode={mode}
            dropTargetId={elementId}
            onRemoveItem={handleRemoveItem}
            dropArea={normalizedDropArea ?? dropArea}
            cloneIndex={item.cloneIndex}
            cloneCount={item.cloneCount}
          />
        ))}
        </div>
      </div>
    </div>
  );
};

// Order List Renderer (independent component with its own items, no external drag-source acceptance)
interface OrderListRendererProps {
  elementId: string;
  label?: string;
  appearance: OrderListAppearance;
  mode: "canvas" | "preview";
  formState?: Record<string, string | string[] | Record<string, string>>;
  onDragDropChange?: (
    targetId: string,
    sourceIds: string | string[],
    sourceId: string | null
  ) => void;
  placeholder?: string | null;
  items: Array<OrderListItem>;
  onUpdate?: (elementId: string, data: Partial<EditorElement["data"]>) => void;
}

const OrderListRenderer: React.FC<OrderListRendererProps> = ({
  elementId,
  label,
  appearance,
  mode,
  formState,
  onDragDropChange,
  placeholder,
  items,
  onUpdate,
}) => {
  const t = getT();
  const [isDragOver, setIsDragOver] = React.useState(false);
  // In preview mode, use formState for ordering; in canvas mode, use items from data
  const [orderedItemIds, setOrderedItemIds] = React.useState<string[]>([]);
  const [draggedItemId, setDraggedItemId] = React.useState<string | null>(null);
  const [previewInsertionIndex, setPreviewInsertionIndex] = React.useState<
    number | null
  >(null);
  // Absolutely-positioned blue drop indicator bar (preview reorder).
  const [dragInsertion, setDragInsertion] = React.useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  const [isAnimatingDrop, setIsAnimatingDrop] = React.useState(false);
  const [isApplyingTransform, setIsApplyingTransform] = React.useState(false);
  const [itemPositions, setItemPositions] = React.useState<
    Map<string, { x: number; y: number }>
  >(new Map());
  const draggedItemStartPosition = React.useRef<{
    x: number;
    y: number;
  } | null>(null);
  const orderListRef = React.useRef<HTMLDivElement>(null);
  const contentContainerRef = React.useRef<HTMLDivElement>(null);
  const itemRefs = React.useRef<Map<string, HTMLDivElement>>(new Map());
  const labelText = label ?? "";
  const hasLabel = labelText.trim().length > 0;

  const normalizedPlaceholder = React.useMemo(() => {
    if (typeof placeholder === "string") {
      return placeholder;
    }
    return t("renderers.orderList.defaultPlaceholder");
  }, [placeholder, t]);

  // Initialize ordered items from formState (only in preview mode)
  // Use separate effects to handle formState changes vs items changes
  React.useEffect(() => {
    if (mode !== "preview") {
      // In canvas mode, use items from data in their original order
      setOrderedItemIds(items.map((item) => item.id));
      return;
    }

    // In preview mode, prioritize formState - this effect runs when formState changes
    const value = formState?.[elementId];
    if (Array.isArray(value) && value.length > 0) {
      // formState contains ordered item IDs - validate they exist in items
      const validIds = value.filter((id) =>
        items.some((item) => item.id === id)
      );
      // Add any missing items from items array that aren't in formState
      const missingIds = items
        .map((item) => item.id)
        .filter((id) => !validIds.includes(id));
      const finalIds = [...validIds, ...missingIds];
      if (finalIds.length > 0) {
        setOrderedItemIds(finalIds);
        return;
      }
    }
  }, [formState, elementId, mode, items]);

  // Initialize from items only if formState is empty (runs once on mount or when items change)
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (mode !== "preview") return;

    // Only initialize if formState is empty
    const value = formState?.[elementId];
    if (!value || (Array.isArray(value) && value.length === 0)) {
      const initialOrder = items.map((item) => item.id);
      setOrderedItemIds(initialOrder);
      if (onDragDropChange && items.length > 0) {
        onDragDropChange(elementId, initialOrder, null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [elementId, mode, items, onDragDropChange]); // Note: formState is NOT in deps to avoid re-initializing

  // Listen for drag start and end events from ordered items
  React.useEffect(() => {
    if (mode !== "preview") return;

    const handleOrderedItemDragStart = (
      e: CustomEvent<{ itemId: string; orderListId: string }>
    ) => {
      if (e.detail.orderListId === elementId) {
        setDraggedItemId(e.detail.itemId);

        // Store the dragged item's starting position for animation
        const itemElement = itemRefs.current.get(e.detail.itemId);
        if (itemElement && contentContainerRef.current) {
          const rect = itemElement.getBoundingClientRect();
          const containerRect =
            contentContainerRef.current.getBoundingClientRect();
          draggedItemStartPosition.current = {
            x: rect.left - containerRect.left,
            y: rect.top - containerRect.top,
          };
        }
      }
    };

    const handleOrderedItemDragEnd = (
      e: CustomEvent<{ orderListId: string }>
    ) => {
      if (e.detail.orderListId === elementId) {
        // Only clear if not handled by drop or dragLeave
        // These will be cleared by handleDrop or handleDragLeave
        // But this is a safety net in case drag ends outside the container
        setTimeout(() => {
          setDraggedItemId(null);
          setPreviewInsertionIndex(null);
          setDragInsertion(null);
        }, 100);
      }
    };

    window.addEventListener(
      "ordered-item-drag-start",
      handleOrderedItemDragStart as EventListener
    );
    window.addEventListener(
      "ordered-item-drag-end",
      handleOrderedItemDragEnd as EventListener
    );
    return () => {
      window.removeEventListener(
        "ordered-item-drag-start",
        handleOrderedItemDragStart as EventListener
      );
      window.removeEventListener(
        "ordered-item-drag-end",
        handleOrderedItemDragEnd as EventListener
      );
    };
  }, [mode, elementId]);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (mode !== "preview") return;
    // Only accept items from within this order-list
    const hasOrderedItem = e.dataTransfer.types.includes(
      "application/x-ordered-item-id"
    );
    if (!hasOrderedItem) {
      return;
    }

    e.preventDefault();
    e.stopPropagation();
    e.dataTransfer.dropEffect = "move";

    setIsDragOver(true);

    // Calculate and update preview insertion index
    const insertionIndex = findInsertionIndex(e.clientX, e.clientY);
    if (insertionIndex !== null) {
      setPreviewInsertionIndex(insertionIndex);
    }

    // Compute the blue drop-indicator bar position (absolute overlay).
    const indicator = computeDragInsertionRect(e.clientX, e.clientY);
    if (indicator) setDragInsertion(indicator);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    // Entering a child fires dragleave — ignore when moving within the list.
    const related = e.relatedTarget as Node | null;
    if (
      related &&
      orderListRef.current &&
      orderListRef.current.contains(related)
    ) {
      return;
    }
    setIsDragOver(false);
    setDraggedItemId(null);
    setPreviewInsertionIndex(null);
    setDragInsertion(null);
    draggedItemStartPosition.current = null;
  };

  // Compute the absolute (relative to orderListRef) coordinates of the blue
  // insertion bar for the position the dragged item would drop into. Vertical
  // lists get a horizontal bar; horizontal lists get a vertical bar.
  const computeDragInsertionRect = (
    clientX: number,
    clientY: number
  ): { left: number; top: number; width: number; height: number } | null => {
    if (!contentContainerRef.current || !orderListRef.current) return null;
    const container = contentContainerRef.current;
    const outer = orderListRef.current;
    const outerRect = outer.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    const children = (
      Array.from(container.children) as HTMLElement[]
    ).filter((c) => c.classList.contains("qe-ordered-item"));

    const BAR_THICK = 4;

    if (children.length === 0) {
      const isHoriz = window
        .getComputedStyle(container)
        .flexDirection?.startsWith("row");
      const cLeft = containerRect.left - outerRect.left;
      const cTop = containerRect.top - outerRect.top;
      if (isHoriz) {
        return {
          left: Math.round(cLeft + containerRect.width / 2 - BAR_THICK / 2),
          top: Math.round(cTop),
          width: BAR_THICK,
          height: Math.round(containerRect.height),
        };
      }
      return {
        left: Math.round(cLeft),
        top: Math.round(cTop + containerRect.height / 2 - BAR_THICK / 2),
        width: Math.round(containerRect.width),
        height: BAR_THICK,
      };
    }

    const flexDirection = window.getComputedStyle(container).flexDirection;
    const isHorizontal =
      flexDirection === "row" || flexDirection === "row-reverse";

    const boxes = children.map((c) => {
      const r = c.getBoundingClientRect();
      return {
        left: r.left - outerRect.left,
        top: r.top - outerRect.top,
        right: r.right - outerRect.left,
        bottom: r.bottom - outerRect.top,
        width: r.width,
        height: r.height,
      };
    });

    if (isHorizontal) {
      const sorted = [...boxes].sort((a, b) => a.left - b.left);
      const relX = clientX - outerRect.left;
      let idx = sorted.length;
      for (let i = 0; i < sorted.length; i++) {
        const cX = sorted[i].left + sorted[i].width / 2;
        if (relX < cX) {
          idx = i;
          break;
        }
      }
      let lineX: number;
      if (idx === 0) lineX = Math.max(0, sorted[0].left - 8);
      else if (idx === sorted.length)
        lineX = sorted[sorted.length - 1].right + 8;
      else lineX = (sorted[idx - 1].right + sorted[idx].left) / 2;
      const ref = sorted[Math.min(idx, sorted.length - 1)];
      return {
        left: Math.round(lineX - BAR_THICK / 2),
        top: Math.round(ref.top),
        width: BAR_THICK,
        height: Math.round(ref.height),
      };
    }

    const sorted = [...boxes].sort((a, b) => a.top - b.top);
    const relY = clientY - outerRect.top;
    let idx = sorted.length;
    for (let i = 0; i < sorted.length; i++) {
      const cY = sorted[i].top + sorted[i].height / 2;
      if (relY < cY) {
        idx = i;
        break;
      }
    }
    let lineY: number;
    if (idx === 0) lineY = Math.max(0, sorted[0].top - 8);
    else if (idx === sorted.length)
      lineY = sorted[sorted.length - 1].bottom + 8;
    else lineY = (sorted[idx - 1].bottom + sorted[idx].top) / 2;
    const ref = sorted[Math.min(idx, sorted.length - 1)];
    return {
      left: Math.round(ref.left),
      top: Math.round(lineY - BAR_THICK / 2),
      width: Math.round(ref.width),
      height: BAR_THICK,
    };
  };

  // Helper function to find the insertion index based on mouse position
  const findInsertionIndex = (
    clientX: number,
    clientY: number
  ): number | null => {
    if (!contentContainerRef.current) return null;

    const containerRect = contentContainerRef.current.getBoundingClientRect();
    const containerChildren = Array.from(
      contentContainerRef.current.children
    ) as HTMLElement[];

    if (containerChildren.length === 0) {
      return 0;
    }

    const computedStyle = window.getComputedStyle(contentContainerRef.current);
    const flexDirection = computedStyle.flexDirection;
    const isHorizontal =
      flexDirection === "row" || flexDirection === "row-reverse";

    const relativeX = clientX - containerRect.left;
    const relativeY = clientY - containerRect.top;

    for (let i = 0; i < containerChildren.length; i++) {
      const childRect = containerChildren[i].getBoundingClientRect();
      const childRelativeX = childRect.left - containerRect.left;
      const childRelativeY = childRect.top - containerRect.top;
      const childCenterX = childRelativeX + childRect.width / 2;
      const childCenterY = childRelativeY + childRect.height / 2;

      if (isHorizontal) {
        if (relativeX < childCenterX) {
          return i;
        }
      } else {
        if (relativeY < childCenterY) {
          return i;
        }
      }
    }

    return containerChildren.length;
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (mode !== "preview") return;

    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    setDragInsertion(null);

    const draggedItemId = e.dataTransfer.getData(
      "application/x-ordered-item-id"
    );
    const sourceOrderListId = e.dataTransfer.getData(
      "application/x-order-list-id"
    );

    if (!draggedItemId || sourceOrderListId !== elementId) {
      setDraggedItemId(null);
      setPreviewInsertionIndex(null);
      return;
    }

    const insertionIndex = findInsertionIndex(e.clientX, e.clientY);
    if (insertionIndex === null) {
      setDraggedItemId(null);
      setPreviewInsertionIndex(null);
      return;
    }

    const currentIndex = orderedItemIds.indexOf(draggedItemId);
    if (currentIndex === -1) {
      setDraggedItemId(null);
      setPreviewInsertionIndex(null);
      return;
    }

    // If dropping at the same position, do nothing
    if (insertionIndex === currentIndex) {
      setDraggedItemId(null);
      setPreviewInsertionIndex(null);
      return;
    }

    // Calculate old positions BEFORE reordering (FLIP: First)
    // Use stored start position for dragged item, current positions for others
    const oldPositions = new Map<string, { x: number; y: number }>();
    if (contentContainerRef.current) {
      orderedItemIds.forEach((itemId) => {
        if (itemId === draggedItemId && draggedItemStartPosition.current) {
          // Use stored start position for dragged item
          oldPositions.set(itemId, draggedItemStartPosition.current);
        } else {
          // Use current position for other items
          const itemElement = itemRefs.current.get(itemId);
          if (itemElement) {
            const rect = itemElement.getBoundingClientRect();
            const containerRect =
              contentContainerRef.current!.getBoundingClientRect();
            oldPositions.set(itemId, {
              x: rect.left - containerRect.left,
              y: rect.top - containerRect.top,
            });
          }
        }
      });
    }

    // Clear stored position
    draggedItemStartPosition.current = null;

    // Reorder items
    const newOrderedItemIds = [...orderedItemIds];
    const [movedItemId] = newOrderedItemIds.splice(currentIndex, 1);

    let newInsertionIndex = insertionIndex;
    if (newInsertionIndex > currentIndex) {
      newInsertionIndex -= 1;
    }

    newOrderedItemIds.splice(newInsertionIndex, 0, movedItemId);

    // Update state (FLIP: Last - items will render in new positions)
    setOrderedItemIds(newOrderedItemIds);
    setDraggedItemId(null);
    setPreviewInsertionIndex(null);
    setIsAnimatingDrop(true);

    // Calculate new positions and apply transform (FLIP: Invert)
    // Use double requestAnimationFrame to ensure DOM has updated
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const newPositions = new Map<string, { x: number; y: number }>();
        if (contentContainerRef.current) {
          newOrderedItemIds.forEach((itemId) => {
            const itemElement = itemRefs.current.get(itemId);
            if (itemElement) {
              const rect = itemElement.getBoundingClientRect();
              const containerRect =
                contentContainerRef.current!.getBoundingClientRect();
              newPositions.set(itemId, {
                x: rect.left - containerRect.left,
                y: rect.top - containerRect.top,
              });
            }
          });
        }

        // Calculate transforms: move items from new position back to old position (Invert)
        // Include ALL items (including dragged item) for synchronized animation
        const isHorizontal = appearance.contentLayout === "horizontal";
        const transforms = new Map<string, { x: number; y: number }>();
        newOrderedItemIds.forEach((itemId) => {
          const oldPos = oldPositions.get(itemId);
          const newPos = newPositions.get(itemId);
          if (oldPos && newPos) {
            // Transform: move from new position to old position (so visually they stay in old position)
            // For horizontal layout, only use x transform; for vertical, only use y transform
            // Include dragged item so it animates from its current position to new position
            transforms.set(itemId, {
              x: isHorizontal ? oldPos.x - newPos.x : 0,
              y: isHorizontal ? 0 : oldPos.y - newPos.y,
            });
          }
        });

        // Apply transform to keep items visually in old positions (FLIP: Invert)
        // Disable transition temporarily so transform applies instantly
        setIsApplyingTransform(true);
        setItemPositions(transforms);

        // Remove transform to animate to new positions (FLIP: Play)
        // Use double requestAnimationFrame to ensure DOM has fully updated
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            // Re-enable transition and remove transform - this triggers the animation
            // This happens after DOM has updated, ensuring smooth animation
            setIsApplyingTransform(false);
            setItemPositions(new Map());

            // Clear animation state after transition completes
            setTimeout(() => {
              setIsAnimatingDrop(false);
            }, 350); // Match transition duration
          });
        });
      });
    });

    if (onDragDropChange) {
      onDragDropChange(elementId, newOrderedItemIds, draggedItemId);
    }
  };

  const labelPositionStyle = mapLabelPosition(appearance.labelPosition);
  const showBackgroundImage =
    appearance.useBackgroundImage &&
    appearance.backgroundImageUrl.trim() !== "";

  const containerStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignItems: labelPositionStyle.alignItems,
    justifyContent: labelPositionStyle.justifyContent,
    backgroundColor: isDragOver
      ? appearance.borderColor + "20"
      : appearance.backgroundColor,
    border: showBackgroundImage
      ? "none"
      : `${appearance.borderWidth}px ${appearance.borderStyle} ${appearance.borderColor}`,
    borderColor: appearance.borderColor,
    borderRadius: `${appearance.borderRadius}px`,
    padding: `${appearance.paddingY}px ${appearance.paddingX}px`,
    boxSizing: "border-box",
    transition: "background-color 0.2s, border-color 0.2s, box-shadow 0.2s",
    gap: "12px",
    position: "relative",
    overflow: "hidden",
    boxShadow: showBackgroundImage
      ? `inset 0 0 0 ${Math.max(0, appearance.borderWidth)}px ${
          appearance.borderColor
        }`
      : isDragOver
      ? "0 0 0 2px rgba(37, 99, 235, 0.18)"
      : undefined,
  };

  const labelStyle: React.CSSProperties = {
    color: appearance.labelTextColor,
    backgroundColor: appearance.labelBackground,
    padding: `${appearance.labelPaddingY}px ${appearance.labelPaddingX}px`,
    borderRadius: `${appearance.labelBorderRadius}px`,
    boxShadow: appearance.labelShadow,
    fontSize: "14px",
    fontWeight: 600,
    userSelect: "none",
    textAlign: labelPositionStyle.textAlign,
    position: "relative",
    zIndex: 1,
  };

  // Map contentAlignment to CSS values
  const mapContentAlignment = (
    alignment: "start" | "center" | "end"
  ): "flex-start" | "center" | "flex-end" => {
    switch (alignment) {
      case "start":
        return "flex-start";
      case "center":
        return "center";
      case "end":
        return "flex-end";
      default:
        return "center";
    }
  };

  const contentAlignmentValue = mapContentAlignment(
    appearance.contentAlignment
  );
  const isHorizontalLayout = appearance.contentLayout === "horizontal";
  const contentAlignItems = isHorizontalLayout
    ? "flex-end"
    : contentAlignmentValue;

  const contentContainerStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: appearance.contentLayout === "horizontal" ? "row" : "column",
    flexWrap: "nowrap", // Always nowrap to prevent wrapping issues with drag and drop
    gap: `${appearance.itemSpacing}px`,
    width: "100%",
    // Align main axis via contentAlignment; keep bottom alignment for horizontal layouts.
    alignItems: contentAlignItems,
    justifyContent: contentAlignmentValue,
    zIndex: 1,
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    overflow: "hidden", // Hide items that don't fit instead of showing scroll
  };

  // Get ordered items based on orderedItemIds
  const orderedItems = React.useMemo(() => {
    return orderedItemIds
      .map((itemId) => {
        const item = items.find((i) => i.id === itemId);
        return item || null;
      })
      .filter(Boolean) as Array<OrderListItem>;
  }, [orderedItemIds, items]);

  return (
    <div
      ref={orderListRef}
      style={{ ...containerStyle, position: "relative" }}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className="qe-order-list"
    >
      {/* Blue drop indicator bar — marks where the dragged item will land. */}
      {dragInsertion ? (
        <div
          aria-hidden
          style={{
            position: "absolute",
            left: `${dragInsertion.left}px`,
            top: `${dragInsertion.top}px`,
            width: `${dragInsertion.width}px`,
            height: `${dragInsertion.height}px`,
            background: "#2563eb",
            borderRadius: 2,
            boxShadow: "0 0 8px rgba(37,99,235,0.6)",
            pointerEvents: "none",
            zIndex: 20,
            transition: "left 0.12s, top 0.12s, opacity 0.15s",
            opacity: 0.95,
          }}
        />
      ) : null}
      {showBackgroundImage ? (
        <img
          src={appearance.backgroundImageUrl}
          alt=""
          aria-hidden
          loading="lazy"
          className="qe-order-list__image"
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: appearance.backgroundImageFit,
            objectPosition: appearance.backgroundImagePosition,
            opacity: appearance.backgroundImageOpacity,
            pointerEvents: "none",
          }}
        />
      ) : null}
      {hasLabel ? <div style={labelStyle}>{labelText}</div> : null}
      <div
        ref={contentContainerRef}
        style={{
          ...contentContainerStyle,
          // For horizontal layout, align to start to prevent vertical centering
          // For vertical layout, stretch to take full width and use flex: 1 for height
          alignSelf:
            appearance.contentLayout === "horizontal"
              ? "flex-start"
              : "stretch",
          // For vertical layout, use flex: 1 to take remaining space so justifyContent works
          flex: appearance.contentLayout === "vertical" ? 1 : undefined,
          // Override parent's justifyContent to prevent vertical centering in horizontal layout
          marginTop: appearance.contentLayout === "horizontal" ? 0 : undefined,
          marginBottom:
            appearance.contentLayout === "horizontal" ? "auto" : undefined,
        }}
      >
        {orderedItems.length === 0 &&
          (() => {
            const trimmed = normalizedPlaceholder.trim();
            if (trimmed.length === 0) {
              return null;
            }
            return (
              <div
                style={{
                  color: "#9ca3af",
                  fontSize: "12px",
                  fontStyle: "italic",
                  textAlign: "center",
                  pointerEvents: "none",
                }}
              >
                {trimmed}
              </div>
            );
          })()}
        {orderedItems.map((item, index) => {
          const isDragged = draggedItemId === item.id;
          const currentIndex = orderedItemIds.indexOf(item.id);
          const draggedIndex = draggedItemId
            ? orderedItemIds.indexOf(draggedItemId)
            : -1;

          // Calculate if this item should shift during drag (for preview animation)
          let shouldShift = false;
          let shiftDirection: "forward" | "backward" | null = null;

          // Only show shift preview if not animating drop (during drag over)
          if (
            !isAnimatingDrop &&
            draggedItemId &&
            previewInsertionIndex !== null &&
            draggedIndex !== -1 &&
            currentIndex !== -1 &&
            !isDragged
          ) {
            if (draggedIndex < previewInsertionIndex) {
              // Dragging forward: items between draggedIndex and previewInsertionIndex shift backward
              if (
                currentIndex > draggedIndex &&
                currentIndex < previewInsertionIndex
              ) {
                shouldShift = true;
                shiftDirection = "backward";
              }
            } else if (draggedIndex > previewInsertionIndex) {
              // Dragging backward: items between previewInsertionIndex and draggedIndex shift forward
              if (
                currentIndex >= previewInsertionIndex &&
                currentIndex < draggedIndex
              ) {
                shouldShift = true;
                shiftDirection = "forward";
              }
            }
          }

          // Get drop animation transform (for synchronized drop animation)
          const dropTransform = itemPositions.get(item.id);

          return (
            <OrderedItemRenderer
              key={item.id}
              item={item}
              mode={mode}
              orderListId={elementId}
              orderNumber={index + 1}
              showNumber={appearance.showNumbers}
              numberPosition={appearance.numberPosition}
              numberColor={appearance.numberColor}
              numberBackground={appearance.numberBackground}
              itemBackground={appearance.itemBackground}
              itemBorderColor={appearance.itemBorderColor}
              itemBorderWidth={appearance.itemBorderWidth}
              itemBorderRadius={appearance.itemBorderRadius}
              itemPaddingX={appearance.itemPaddingX}
              itemPaddingY={appearance.itemPaddingY}
              itemTextColor={appearance.itemTextColor}
              itemFontSize={appearance.itemFontSize}
              isDragged={isDragged}
              shouldShift={shouldShift}
              shiftDirection={shiftDirection}
              showPlaceholder={isDragged && !isAnimatingDrop}
              placeholderIndex={previewInsertionIndex}
              contentLayout={appearance.contentLayout}
              itemSpacing={appearance.itemSpacing}
              isAnimatingDrop={isAnimatingDrop}
              isApplyingTransform={isApplyingTransform}
              dropTransform={dropTransform}
              onRef={(ref) => {
                if (ref) {
                  itemRefs.current.set(item.id, ref);
                } else {
                  itemRefs.current.delete(item.id);
                }
              }}
            />
          );
        })}
      </div>
    </div>
  );
};

// Ordered Item Renderer (for individual items inside order-list)
interface OrderedItemRendererProps {
  item: OrderListItem;
  mode: "canvas" | "preview";
  orderListId: string;
  orderNumber: number;
  showNumber: boolean;
  numberPosition:
    | "top-left"
    | "top-center"
    | "top-right"
    | "middle-left"
    | "center"
    | "middle-right"
    | "bottom-left"
    | "bottom-center"
    | "bottom-right";
  numberColor: string;
  numberBackground: string;
  itemBackground: string;
  itemBorderColor: string;
  itemBorderWidth: number;
  itemBorderRadius: number;
  itemPaddingX: number;
  itemPaddingY: number;
  itemTextColor: string;
  itemFontSize: number;
  isDragged?: boolean;
  shouldShift?: boolean;
  shiftDirection?: "forward" | "backward" | null;
  contentLayout?: "horizontal" | "vertical";
  itemSpacing?: number;
  isAnimatingDrop?: boolean;
  isApplyingTransform?: boolean;
  dropTransform?: { x: number; y: number } | undefined;
  showPlaceholder?: boolean;
  placeholderIndex?: number | null;
  onRef?: (ref: HTMLDivElement | null) => void;
}

const OrderedItemRenderer: React.FC<OrderedItemRendererProps> = ({
  item,
  mode,
  orderListId,
  orderNumber,
  showNumber,
  numberPosition,
  numberColor,
  numberBackground,
  itemBackground,
  itemBorderColor,
  itemBorderWidth,
  itemBorderRadius,
  itemPaddingX,
  itemPaddingY,
  itemTextColor,
  itemFontSize,
  isDragged = false,
  shouldShift = false,
  shiftDirection = null,
  contentLayout = "vertical",
  itemSpacing = 8,
  isAnimatingDrop = false,
  isApplyingTransform = false,
  dropTransform,
  showPlaceholder = false,
  placeholderIndex = null,
  onRef,
}) => {
  const [isDragging, setIsDragging] = React.useState(false);
  const itemRef = React.useRef<HTMLDivElement>(null);
  const hasMathContent =
    (typeof item.mathml === "string" && item.mathml.trim().length > 0) ||
    (typeof item.latex === "string" && item.latex.trim().length > 0);
  const contentType =
    item.contentType === "text" || !item.contentType
      ? hasMathContent
        ? "mathtype"
        : "text"
      : item.contentType;
  const isMathContent =
    contentType === "mathtype" || contentType === "mathlive";
  const isImageContent = contentType === "image";
  const hasImage = isImageContent && Boolean(item.imageUrl);
  const hasFrameWidth =
    typeof item.frameWidth === "number" && item.frameWidth > 0;
  const hasFrameHeight =
    typeof item.frameHeight === "number" && item.frameHeight > 0;
  const shouldStretchImage =
    !item.imageWidth && !item.imageHeight && (hasFrameWidth || hasFrameHeight);
  const numberSize = 24;
  const numberGap = 8;
  // Honor the item padding for every content type (including images) so the
  // "item padding" settings are visible; images fit inside the padded box.
  const basePaddingX = itemPaddingX;
  const basePaddingY = itemPaddingY;
  const reserveNumberSpace =
    showNumber && !hasImage && numberPosition === "middle-left";
  const numberReserve = reserveNumberSpace ? numberSize + numberGap : 0;
  const numberOffsetX = hasImage ? 8 : basePaddingX;
  const numberOffsetY = hasImage ? 8 : basePaddingY;

  // Set ref callback
  const setItemRef = React.useCallback(
    (ref: HTMLDivElement | null) => {
      itemRef.current = ref;
      if (onRef) {
        onRef(ref);
      }
    },
    [onRef]
  );

  const handleItemDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    if (mode !== "preview") return;
    setIsDragging(true);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/x-ordered-item-id", item.id);
    e.dataTransfer.setData("application/x-order-list-id", orderListId);

    // Dispatch custom event to notify parent OrderListRenderer
    window.dispatchEvent(
      new CustomEvent("ordered-item-drag-start", {
        detail: { itemId: item.id, orderListId },
      })
    );
  };

  const handleItemDragEnd = () => {
    setIsDragging(false);

    // Dispatch custom event to notify parent OrderListRenderer
    window.dispatchEvent(
      new CustomEvent("ordered-item-drag-end", {
        detail: { orderListId },
      })
    );
  };

  // Calculate transform for shifting animation during drag
  const [shiftTransform, setShiftTransform] = React.useState("translate(0, 0)");

  React.useEffect(() => {
    if (!shouldShift || !itemRef.current || shiftDirection === null) {
      setShiftTransform("translate(0, 0)");
      return;
    }

    const isHorizontal = contentLayout === "horizontal";

    if (isHorizontal) {
      // For horizontal layout, shift left or right
      const itemWidth = itemRef.current.offsetWidth || 120; // fallback to minWidth
      const shiftAmount = itemWidth + itemSpacing;

      if (shiftDirection === "forward") {
        setShiftTransform(`translateX(${shiftAmount}px)`);
      } else {
        setShiftTransform(`translateX(-${shiftAmount}px)`);
      }
    } else {
      // For vertical layout, shift up or down
      const itemHeight = itemRef.current.offsetHeight || 40; // fallback estimate
      const shiftAmount = itemHeight + itemSpacing;

      if (shiftDirection === "forward") {
        setShiftTransform(`translateY(${shiftAmount}px)`);
      } else {
        setShiftTransform(`translateY(-${shiftAmount}px)`);
      }
    }
  }, [shouldShift, shiftDirection, contentLayout, itemSpacing]);

  // Calculate drop animation transform (only transform, no visual effects)
  const getDropTransform = () => {
    if (!isAnimatingDrop || !dropTransform) {
      return "translate(0, 0)";
    }
    // Only apply transform for position animation, no scale or other effects
    return `translate(${dropTransform.x}px, ${dropTransform.y}px)`;
  };

  const shouldCenterContent = contentType === "text" || isMathContent;
  const containerStyle: React.CSSProperties = {
    width:
      typeof item.frameWidth === "number" && item.frameWidth > 0
        ? `${item.frameWidth}px`
        : "auto",
    minWidth:
      typeof item.frameWidth === "number" && item.frameWidth > 0
        ? undefined
        : "120px",
    height:
      typeof item.frameHeight === "number" && item.frameHeight > 0
        ? `${item.frameHeight}px`
        : "auto",
    backgroundColor: itemBackground,
    border: `${itemBorderWidth}px solid ${itemBorderColor}`,
    borderRadius: `${itemBorderRadius}px`,
    paddingTop: `${basePaddingY}px`,
    paddingRight: `${basePaddingX}px`,
    paddingBottom: `${basePaddingY}px`,
    paddingLeft: `${basePaddingX + numberReserve}px`,
    boxSizing: "border-box",
    userSelect: "none",
    textAlign: shouldCenterContent ? "center" : "left",
    cursor: mode === "preview" ? "grab" : "default",
    transition: isDragging
      ? "opacity 0.2s"
      : isApplyingTransform
      ? "none" // No transition when applying transform (FLIP: Invert)
      : isAnimatingDrop
      ? "transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)"
      : "all 0.3s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s, transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    opacity: isDragging ? 0.5 : 1, // Full opacity when not dragging (including during drop animation)
    transform: isDragging
      ? "scale(0.95)"
      : isAnimatingDrop
      ? getDropTransform()
      : shouldShift
      ? shiftTransform
      : "scale(1)",
    position: "relative",
    overflow: hasImage ? "hidden" : "visible",
    display: "flex",
    alignItems: hasImage ? "stretch" : "center",
    justifyContent: shouldCenterContent ? "center" : "flex-start",
    gap: hasImage ? 0 : "8px",
  };

  const numberStyle: React.CSSProperties = {
    width: numberSize,
    height: numberSize,
    borderRadius: "50%",
    backgroundColor: numberBackground,
    color: numberColor,
    fontSize: 12,
    fontWeight: 600,
    lineHeight: 1,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    pointerEvents: "none",
    zIndex: 2,
    flexShrink: 0,
    position: "absolute",
  };

  const numberPositionStyle: React.CSSProperties = (() => {
    switch (numberPosition) {
      case "top-left":
        return { top: numberOffsetY, left: numberOffsetX };
      case "top-center":
        return {
          top: numberOffsetY,
          left: "50%",
          transform: "translateX(-50%)",
        };
      case "top-right":
        return { top: numberOffsetY, right: numberOffsetX };
      case "middle-left":
        return {
          top: "50%",
          left: numberOffsetX,
          transform: "translateY(-50%)",
        };
      case "center":
        return {
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
        };
      case "middle-right":
        return {
          top: "50%",
          right: numberOffsetX,
          transform: "translateY(-50%)",
        };
      case "bottom-left":
        return { bottom: numberOffsetY, left: numberOffsetX };
      case "bottom-center":
        return {
          bottom: numberOffsetY,
          left: "50%",
          transform: "translateX(-50%)",
        };
      case "bottom-right":
        return { bottom: numberOffsetY, right: numberOffsetX };
      default:
        return {
          top: "50%",
          left: numberOffsetX,
          transform: "translateY(-50%)",
        };
    }
  })();

  const imageStyle: React.CSSProperties = {
    width: item.imageWidth
      ? `${item.imageWidth}px`
      : hasFrameWidth
      ? "100%"
      : "auto",
    height: item.imageHeight
      ? `${item.imageHeight}px`
      : hasFrameHeight
      ? "100%"
      : "auto",
    // Never let the image overflow its item box; scale it down to fit.
    maxWidth: "100%",
    maxHeight: "100%",
    objectFit:
      item.imageObjectFit ||
      (hasFrameWidth || hasFrameHeight ? "cover" : "contain"),
    borderRadius: `${item.imageBorderRadius ?? itemBorderRadius}px`,
    display: "block",
    flex: shouldStretchImage ? 1 : undefined,
  };

  return (
    <div
      ref={setItemRef}
      draggable={true}
      onDragStart={handleItemDragStart}
      onDragEnd={handleItemDragEnd}
      style={containerStyle}
      className="qe-ordered-item"
      data-item-id={item.id}
    >
      {showNumber ? (
        <span aria-hidden style={{ ...numberStyle, ...numberPositionStyle }}>
          {orderNumber}
        </span>
      ) : null}
      {contentType === "text" && (
        <span
          style={{
            fontWeight: 500,
            fontSize: `${itemFontSize}px`,
            position: "relative",
            zIndex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "100%",
            textAlign: "center",
            color: itemTextColor,
          }}
        >
          {item.label || ""}
        </span>
      )}
      {isMathContent && (
        <div
          style={{
            width: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <MathMLContent
            html={item.mathml || item.latex || ""}
            style={{
              fontSize: `${itemFontSize}px`,
              color: itemTextColor,
              display: "inline-block",
            }}
          />
        </div>
      )}
      {contentType === "image" && item.imageUrl && (
        <img src={item.imageUrl} alt={item.imageAlt || ""} style={imageStyle} />
      )}
    </div>
  );
};

interface FillInTheBlanksRendererProps {
  elementId: string;
  content: string;
  blanks?: Blank[];
  appearance: FillInTheBlanksAppearance;
  mode: "canvas" | "preview";
  formState?: Record<string, string | string[] | Record<string, string>>;
  onFillInTheBlanksChange?: (
    elementId: string,
    values: Record<string, string>
  ) => void;
}

const FillInTheBlanksRenderer: React.FC<FillInTheBlanksRendererProps> = ({
  elementId,
  content,
  blanks = [],
  appearance,
  mode,
  formState,
  onFillInTheBlanksChange,
}) => {
  const t = getT();
  const [values, setValues] = React.useState<Record<string, string>>({});

  // Initialize values from formState
  React.useEffect(() => {
    if (mode === "preview" && formState?.[elementId]) {
      const savedValues = formState[elementId];
      if (typeof savedValues === "object" && !Array.isArray(savedValues)) {
        setValues(savedValues as Record<string, string>);
      }
    }
  }, [formState, elementId, mode]);

  const handleInputChange = (blankId: string, value: string) => {
    if (mode !== "preview") return;

    const newValues = { ...values, [blankId]: value };
    setValues(newValues);

    if (onFillInTheBlanksChange) {
      onFillInTheBlanksChange(elementId, newValues);
    }
  };

  // Parse content to find blanks [answer]
  // We split by regex to get parts: text, blank, text, blank...
  const parts = content.split(/\[(.*?)\]/g);

  const containerStyle: React.CSSProperties = {
    fontFamily: appearance.fontFamily,
    fontSize: `${appearance.fontSize}px`,
    fontWeight: appearance.fontWeight,
    color: appearance.color,
    lineHeight: appearance.lineHeight,
    backgroundColor: appearance.backgroundColor,
    padding: `${appearance.paddingY}px ${appearance.paddingX}px`,
    borderRadius: `${appearance.borderRadius}px`,
    border:
      appearance.borderWidth > 0
        ? `${appearance.borderWidth}px solid ${appearance.borderColor}`
        : "none",
    boxSizing: "border-box",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    fontStyle: appearance.italic ? "italic" : "normal",
    textDecoration: appearance.underline ? "underline" : "none",
    textAlign: appearance.textAlign,
    letterSpacing: `${appearance.letterSpacing}px`,
    textTransform: appearance.textTransform,
    boxShadow: appearance.shadow,
  };

  return (
    <div style={containerStyle}>
      {parts.map((part, index) => {
        // Even indices are text, odd indices are blanks (captured groups)
        if (index % 2 === 0) {
          return <span key={`text-${index}`}>{part}</span>;
        } else {
          // This is a blank
          const blankIndex = Math.floor(index / 2);
          // Get the blank ID from the blanks array, or fall back to index-based ID
          const blankId = blanks[blankIndex]?.id || `blank-${blankIndex}`;
          const blank = blanks[blankIndex];
          const value = values[blankId] || "";

          // Merge blank-specific appearance with global appearance
          const blankAppearance = blank?.appearance || {};
          const mergedAppearance = {
            ...appearance,
            inputBackgroundColor:
              blankAppearance.inputBackgroundColor ??
              appearance.inputBackgroundColor,
            inputBorderColor:
              blankAppearance.inputBorderColor ?? appearance.inputBorderColor,
            inputBorderWidth:
              blankAppearance.inputBorderWidth ?? appearance.inputBorderWidth,
            inputBorderRadius:
              blankAppearance.inputBorderRadius ?? appearance.inputBorderRadius,
            inputTextColor:
              blankAppearance.inputTextColor ?? appearance.inputTextColor,
            inputPaddingX:
              blankAppearance.inputPaddingX ?? appearance.inputPaddingX,
            inputPaddingY:
              blankAppearance.inputPaddingY ?? appearance.inputPaddingY,
            inputMinWidth:
              blankAppearance.inputMinWidth ?? appearance.inputMinWidth,
            inputDefaultWidth:
              blankAppearance.inputDefaultWidth ?? appearance.inputDefaultWidth,
            inputTextAlign:
              blankAppearance.inputTextAlign ?? appearance.inputTextAlign,
            inputFontWeight:
              blankAppearance.inputFontWeight ?? appearance.inputFontWeight,
            inputFontFamily:
              blankAppearance.inputFontFamily ?? appearance.inputFontFamily,
            inputFontSize:
              blankAppearance.inputFontSize ?? appearance.inputFontSize,
            inputItalic: blankAppearance.inputItalic ?? appearance.inputItalic,
            inputUnderline:
              blankAppearance.inputUnderline ?? appearance.inputUnderline,
            inputLetterSpacing:
              blankAppearance.inputLetterSpacing ??
              appearance.inputLetterSpacing,
            inputBorderStyle:
              blankAppearance.inputBorderStyle ?? appearance.inputBorderStyle,
          };

          // Create input style for this specific blank
          const blankInputStyle: React.CSSProperties = {
            // Only apply styles if they are explicitly set (not undefined)
            ...(mergedAppearance.inputBackgroundColor !== undefined && {
              backgroundColor: mergedAppearance.inputBackgroundColor,
            }),
            ...(mergedAppearance.inputBorderWidth !== undefined &&
              mergedAppearance.inputBorderColor !== undefined && {
                border:
                  mergedAppearance.inputBorderStyle === "bottom"
                    ? "none"
                    : `${mergedAppearance.inputBorderWidth}px solid ${mergedAppearance.inputBorderColor}`,
                borderBottom: `${mergedAppearance.inputBorderWidth}px solid ${mergedAppearance.inputBorderColor}`,
              }),
            ...(mergedAppearance.inputBorderRadius !== undefined &&
              mergedAppearance.inputBorderStyle !== "bottom" && {
                borderRadius: `${mergedAppearance.inputBorderRadius}px`,
              }),
            ...(mergedAppearance.inputTextColor !== undefined && {
              color: mergedAppearance.inputTextColor,
            }),
            ...(mergedAppearance.inputPaddingX !== undefined &&
              mergedAppearance.inputPaddingY !== undefined && {
                padding: `${mergedAppearance.inputPaddingY}px ${mergedAppearance.inputPaddingX}px`,
              }),
            ...(mergedAppearance.inputMinWidth !== undefined && {
              minWidth: `${mergedAppearance.inputMinWidth}px`,
            }),

            // Font styles - use input-specific if set, otherwise fall back to text styles
            fontFamily:
              mergedAppearance.inputFontFamily ?? mergedAppearance.fontFamily,
            fontSize:
              mergedAppearance.inputFontSize !== undefined
                ? `${mergedAppearance.inputFontSize}px`
                : `${mergedAppearance.fontSize}px`,
            ...(mergedAppearance.inputFontWeight !== undefined && {
              fontWeight: mergedAppearance.inputFontWeight,
            }),
            ...(mergedAppearance.inputItalic !== undefined && {
              fontStyle: mergedAppearance.inputItalic ? "italic" : "normal",
            }),
            ...(mergedAppearance.inputUnderline !== undefined && {
              textDecoration: mergedAppearance.inputUnderline
                ? "underline"
                : "none",
            }),
            ...(mergedAppearance.inputLetterSpacing !== undefined && {
              letterSpacing: `${mergedAppearance.inputLetterSpacing}px`,
            }),
            ...(mergedAppearance.inputTextAlign !== undefined && {
              textAlign: mergedAppearance.inputTextAlign,
            }),

            outline: "none",
            margin: "0 4px",
            display: "inline-block",
            boxSizing: "border-box", // Include padding and border in width calculation
          };

          // Width: start from minWidth (defaultWidth if set, else inputMinWidth), grow with content
          const defaultWidthPx = mergedAppearance.inputDefaultWidth;
          const minWidthPx =
            defaultWidthPx ??
            mergedAppearance.inputMinWidth ??
            appearance.inputMinWidth ??
            0;

          // Check if this blank has choices (dropdown mode)
          const hasChoices = blank?.choices && blank.choices.length > 0;

          // Calculate width based on font size
          const fontSize =
            mergedAppearance.inputFontSize ?? mergedAppearance.fontSize ?? 18;
          const charWidthPx = fontSize * 0.5;
          const paddingX = mergedAppearance.inputPaddingX ?? 8;
          const dropdownPadding = paddingX + 24; // padding + dropdown arrow space

          // Create a modified style for dropdown mode with reduced padding (half of original)
          const reducedPaddingX = Math.round(paddingX / 2);
          const dropdownInputStyle: React.CSSProperties = {
            ...blankInputStyle,
            // Reduce horizontal padding for dropdown mode (half of original)
            padding: `${mergedAppearance.inputPaddingY ?? 4}px ${reducedPaddingX}px`,
          };

          if (mode === "canvas") {
            // Use actual input element in canvas mode to match preview appearance exactly
            // If hideAnswers is true, show empty input (blank), otherwise show the answer
            const displayValue = mergedAppearance.hideAnswers ? "" : part;

            if (hasChoices) {
              // In canvas mode, show dropdown with choices
              // Calculate width based on longest option for canvas (so all options are visible)
              const allOptions = [displayValue, ...(blank.choices || [])];
              const maxOptionLength = Math.max(...allOptions.filter(Boolean).map(opt => opt.length));
              const canvasDropdownWidthInCh = maxOptionLength + 7;

              return (
                <select
                  key={`blank-${index}`}
                  disabled
                  style={{
                    ...dropdownInputStyle,
                    width: `${canvasDropdownWidthInCh}ch`,
                    minWidth: minWidthPx > 0 ? `${minWidthPx}px` : undefined,
                    maxWidth: "300px",
                    cursor: "default",
                  }}
                >
                  <option value="">{displayValue}</option>
                  {blank.choices?.map((choice) => (
                    <option key={choice} value={choice}>
                      {choice}
                    </option>
                  ))}
                </select>
              );
            }

            return (
              <input
                key={`blank-${index}`}
                type="text"
                value={displayValue}
                readOnly
                disabled
                style={{
                  ...blankInputStyle,
                  width: minWidthPx > 0 ? `${minWidthPx}px` : "auto",
                  minWidth: minWidthPx > 0 ? `${minWidthPx}px` : undefined,
                  cursor: "default",
                  pointerEvents: "none",
                }}
              />
            );
          }

          // Preview mode
          if (hasChoices) {
            // Calculate dropdown width based on selected value
            // If no value selected, use fixed width. If selected, use width based on selected value.
            const displayText = value || t("renderers.fillInTheBlanks.selectPlaceholder");
            // Use ch units for more accurate text width calculation
            const textLengthInCh = displayText.length;
            // Add extra space for dropdown arrow and internal padding
            const totalWidthInCh = textLengthInCh + 7;

            return (
              <select
                key={blankId}
                value={value}
                onChange={(e) => handleInputChange(blankId, e.target.value)}
                style={{
                  ...dropdownInputStyle,
                  width: `${totalWidthInCh}ch`,
                  minWidth: minWidthPx > 0 ? `${minWidthPx}px` : undefined,
                  maxWidth: "300px",
                }}
              >
                <option value="">{t("renderers.fillInTheBlanks.selectPlaceholder")}</option>
                {blank.choices?.map((choice) => (
                  <option key={choice} value={choice}>
                    {choice}
                  </option>
                ))}
              </select>
            );
          }

          // Calculate width dynamically based on font size (reusing fontSize, charWidthPx, paddingX from dropdown calculation)
          const textInputPadding = paddingX * 2 + 8; // padding + safety margin (different from dropdown)

          const estimatedContentWidth =
            value.length * charWidthPx + textInputPadding;

          // Clamp between minWidth and 300px
          const finalWidth = Math.min(
            300,
            Math.max(minWidthPx, estimatedContentWidth)
          );

          return (
            <input
              key={blankId}
              type="text"
              value={value}
              onChange={(e) => handleInputChange(blankId, e.target.value)}
              style={{
                ...blankInputStyle,
                width: `${finalWidth}px`,
                minWidth: minWidthPx > 0 ? `${minWidthPx}px` : undefined,
                maxWidth: "300px",
              }}
              autoComplete="off"
            />
          );
        }
      })}
    </div>
  );
};

type PaintShapeType = PaintAreaAppearance["shapeType"];

type Matrix = {
  a: number;
  b: number;
  c: number;
  d: number;
  e: number;
  f: number;
};

const buildIntersectionKey = (ids: string[]): string => {
  return ids.slice().sort().join("|");
};

const parseIntersectionKey = (key: string): string[] => {
  return key.split("|").filter(Boolean);
};

const PAINT_SWATCH_PREFIX = "paint-swatch:";
const PAINT_INTERSECTION_PREFIX = "paint-intersection:";
const PAINT_INTERSECTION_SWATCH_PREFIX = "paint-intersection-swatch:";
const PAINT_HIT_TOLERANCE = 6;
const PAINT_HIT_MAX_TOLERANCE = 10;
const PAINT_INTERSECTION_TOLERANCE = 10;
const PAINT_SHAPE_INSET = 2;
const buildPaintHitOffsets = (radius: number) => [
  { x: 0, y: 0 },
  { x: radius, y: 0 },
  { x: -radius, y: 0 },
  { x: 0, y: radius },
  { x: 0, y: -radius },
  { x: radius * 0.7, y: radius * 0.7 },
  { x: -radius * 0.7, y: radius * 0.7 },
  { x: radius * 0.7, y: -radius * 0.7 },
  { x: -radius * 0.7, y: -radius * 0.7 },
];
const PAINT_HIT_OFFSETS = buildPaintHitOffsets(PAINT_HIT_TOLERANCE);
const getPaintHitOffsets = (radius: number) =>
  radius === PAINT_HIT_TOLERANCE
    ? PAINT_HIT_OFFSETS
    : buildPaintHitOffsets(radius);

const translateMatrix = (tx: number, ty: number): Matrix => ({
  a: 1,
  b: 0,
  c: 0,
  d: 1,
  e: tx,
  f: ty,
});

const scaleMatrix = (sx: number, sy: number): Matrix => ({
  a: sx,
  b: 0,
  c: 0,
  d: sy,
  e: 0,
  f: 0,
});

const rotateMatrix = (angleDeg: number): Matrix => {
  const angle = (angleDeg * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  return {
    a: cos,
    b: sin,
    c: -sin,
    d: cos,
    e: 0,
    f: 0,
  };
};

const multiplyMatrix = (m1: Matrix, m2: Matrix): Matrix => ({
  a: m1.a * m2.a + m1.c * m2.b,
  b: m1.b * m2.a + m1.d * m2.b,
  c: m1.a * m2.c + m1.c * m2.d,
  d: m1.b * m2.c + m1.d * m2.d,
  e: m1.a * m2.e + m1.c * m2.f + m1.e,
  f: m1.b * m2.e + m1.d * m2.f + m1.f,
});

const invertMatrix = (m: Matrix): Matrix | null => {
  const det = m.a * m.d - m.b * m.c;
  if (!det) return null;
  const nextA = m.d / det;
  const nextB = -m.b / det;
  const nextC = -m.c / det;
  const nextD = m.a / det;
  const nextE = (m.c * m.f - m.d * m.e) / det;
  const nextF = (m.b * m.e - m.a * m.f) / det;
  return { a: nextA, b: nextB, c: nextC, d: nextD, e: nextE, f: nextF };
};

const getElementTransformMatrix = (
  element: EditorElement,
  inset = 0
): Matrix => {
  const rawWidth = element.size.width ?? 0;
  const rawHeight = element.size.height ?? rawWidth ?? 0;
  const width = Math.max(1, rawWidth - inset * 2);
  const height = Math.max(1, rawHeight - inset * 2);
  const angle = element.rotation ?? 0;
  const centerX = width / 2;
  const centerY = height / 2;

  const transforms = [
    translateMatrix(element.position.x + inset, element.position.y + inset),
    translateMatrix(centerX, centerY),
    rotateMatrix(angle),
    translateMatrix(-centerX, -centerY),
    scaleMatrix(width / 100, height / 100),
  ];

  return transforms.reduce((acc, next) => multiplyMatrix(acc, next), {
    a: 1,
    b: 0,
    c: 0,
    d: 1,
    e: 0,
    f: 0,
  });
};

const getRelativeTransform = (
  fromElement: EditorElement,
  toElement: EditorElement,
  inset = 0
): Matrix | null => {
  const fromMatrix = getElementTransformMatrix(fromElement, inset);
  const toMatrix = getElementTransformMatrix(toElement, inset);
  const inverted = invertMatrix(toMatrix);
  if (!inverted) return null;
  return multiplyMatrix(inverted, fromMatrix);
};

const matrixToSvgTransform = (matrix: Matrix): string => {
  const { a, b, c, d, e, f } = matrix;
  return `matrix(${a} ${b} ${c} ${d} ${e} ${f})`;
};

const matrixToDomMatrix = (matrix: Matrix): DOMMatrix | null => {
  if (typeof DOMMatrix === "undefined") return null;
  return new DOMMatrix([
    matrix.a,
    matrix.b,
    matrix.c,
    matrix.d,
    matrix.e,
    matrix.f,
  ]);
};

let paintHitCanvas: HTMLCanvasElement | null = null;
let paintHitContext: CanvasRenderingContext2D | null = null;
const paintShapePathCache = new Map<string, Path2D>();

// Free-polygon paint areas store their vertices on element.data.points.
const getPaintElementPoints = (
  element: EditorElement
): PaintPoint[] | undefined => {
  const pts = (element.data as { points?: PaintPoint[] })?.points;
  return Array.isArray(pts) ? pts : undefined;
};

const getPaintHitContext = (): CanvasRenderingContext2D | null => {
  if (paintHitContext) return paintHitContext;
  if (typeof document === "undefined") return null;
  paintHitCanvas = document.createElement("canvas");
  paintHitContext = paintHitCanvas.getContext("2d");
  return paintHitContext;
};

const buildPaintShapePath = (
  shapeType: PaintShapeType,
  points?: PaintPoint[] | null
): Path2D | null => {
  if (typeof Path2D === "undefined") return null;
  // Free polygons vary per element, so they bypass the shared shape cache.
  if (shapeType !== "polygon") {
    const cached = paintShapePathCache.get(shapeType);
    if (cached) return cached;
  }
  const path = new Path2D(getPaintShapePathD(shapeType, points));
  if (shapeType !== "polygon") {
    paintShapePathCache.set(shapeType, path);
  }
  return path;
};

const buildPaintAreaPath = (
  element: EditorElement,
  inset = 0
): Path2D | null => {
  const basePath = buildPaintShapePath(
    resolvePaintAreaAppearance(element).shapeType,
    getPaintElementPoints(element)
  );
  if (!basePath || typeof Path2D === "undefined") return null;
  const matrix = getElementTransformMatrix(element, inset);
  const domMatrix = matrixToDomMatrix(matrix);
  if (!domMatrix) return null;
  const path = new Path2D();
  path.addPath(basePath, domMatrix);
  return path;
};

const isPointInPaintShape = (
  shapeType: PaintShapeType,
  x: number,
  y: number,
  tolerance = 0,
  points?: PaintPoint[] | null
): boolean => {
  const context = getPaintHitContext();
  const path = buildPaintShapePath(shapeType, points);
  if (!context || !path) return false;
  if (context.isPointInPath(path, x, y)) {
    return true;
  }
  if (tolerance <= 0) {
    return false;
  }
  const previousLineWidth = context.lineWidth;
  context.lineWidth = tolerance;
  const hit = context.isPointInStroke(path, x, y);
  context.lineWidth = previousLineWidth;
  return hit;
};

const renderPaintShapeClip = (
  shapeType: PaintShapeType,
  props?: React.SVGAttributes<SVGElement>,
  points?: PaintPoint[] | null
) => <path d={getPaintShapePathD(shapeType, points)} {...(props as object)} />;

// İki boya renginin eşleşip eşleşmediği (hex normalizasyonu + birebir
// string eşleşmesi). Doğru/yanlış geri bildirimi için kullanılır.
const paintColorsMatch = (a?: string, b?: string): boolean => {
  if (!a || !b) return false;
  if (a.trim().toLowerCase() === b.trim().toLowerCase()) return true;
  return areSameColor(a, b);
};

// Paint Area Renderer
interface PaintAreaRendererProps {
  elementId: string;
  appearance: PaintAreaAppearance;
  paintedColor: string | undefined; // Current painted color for this area
  correctColor?: string; // Doğru cevap rengi (answer key veya inspector)
  mode: "canvas" | "preview";
  formState?: RenderOptions["formState"];
  allElements?: EditorElement[];
  onUpdate?: (elementId: string, data: Partial<EditorElement["data"]>) => void;
}

const PaintAreaRenderer: React.FC<PaintAreaRendererProps> = ({
  elementId,
  appearance,
  paintedColor,
  correctColor,
  mode,
  formState,
  allElements,
  onUpdate,
}) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [isDragOver, setIsDragOver] = React.useState(false);
  const currentElement = allElements?.find((el) => el.id === elementId);
  const formStateColor =
    typeof formState?.[elementId] === "string"
      ? (formState[elementId] as string)
      : undefined;
  const dataIntersectionColors =
    (currentElement?.data as { intersectionColors?: Record<string, string> })
      ?.intersectionColors || {};
  const formStateIntersectionColors = React.useMemo(() => {
    const entries = formState ? Object.entries(formState) : [];
    return entries.reduce<Record<string, string>>((acc, [key, value]) => {
      if (!key.startsWith(PAINT_INTERSECTION_PREFIX)) return acc;
      if (typeof value !== "string") return acc;
      const intersectionKey = key.slice(PAINT_INTERSECTION_PREFIX.length);
      if (!intersectionKey) return acc;
      acc[intersectionKey] = value;
      return acc;
    }, {});
  }, [formState]);
  // Use local state to track painted color for immediate UI update
  const [localPaintedColor, setLocalPaintedColor] = React.useState<
    string | undefined
  >(formStateColor || paintedColor);
  const [localIntersectionColors, setLocalIntersectionColors] = React.useState<
    Record<string, string>
  >({ ...dataIntersectionColors, ...formStateIntersectionColors });
  const paintedValue = localPaintedColor ?? paintedColor;
  const isPainted = Boolean(paintedValue && paintedValue !== "transparent");

  // ── Doğru/yanlış geri bildirimi (yalnızca önizleme/çözüm modunda) ──
  // Doğru renk answer key'den (AnswerKeyModal) ya da inspector alanından.
  const targetColor = correctColor ?? appearance.correctColor;
  const hasTarget = mode === "preview" && Boolean(targetColor);
  const isCorrect =
    hasTarget && isPainted && paintColorsMatch(paintedValue, targetColor);
  const isWrong = hasTarget && isPainted && !isCorrect;

  // Yanlış boyandığında şekli titret (kısa animasyon). paintedValue her
  // değiştiğinde (tıkla/sürükle fark etmez) değerlendirilir.
  const [isShaking, setIsShaking] = React.useState(false);
  React.useEffect(() => {
    if (isWrong) {
      setIsShaking(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paintedValue]);

  const paintAreaElements = React.useMemo(
    () => (allElements || []).filter((el) => el.type === "paint-area"),
    [allElements]
  );
  const fillMaskShapes = React.useMemo(() => {
    if (!currentElement) return [];
    return paintAreaElements
      .filter((el) => el.id !== elementId)
      .map((el) => {
        const transformMatrix = getRelativeTransform(
          el,
          currentElement,
          PAINT_SHAPE_INSET
        );
        return {
          id: el.id,
          shapeType: resolvePaintAreaAppearance(el).shapeType,
          points: getPaintElementPoints(el),
          transform: transformMatrix
            ? matrixToSvgTransform(transformMatrix)
            : undefined,
        };
      });
  }, [paintAreaElements, currentElement, elementId]);
  const fillMaskId = `paint-fill-mask-${elementId}`;

  const getDropPoint = React.useCallback(
    (event: React.DragEvent<HTMLDivElement>) => {
      if (typeof window === "undefined") return null;
      const canvasElement = containerRef.current?.closest(
        ".qe-preview-canvas, .qe-fullscreen-preview__canvas, .qe-preview-only__canvas, .qe-canvas"
      ) as HTMLElement | null;
      if (!canvasElement) return null;

      const rect = canvasElement.getBoundingClientRect();
      const scaleX = rect.width / (canvasElement.clientWidth || rect.width || 1);
      const scaleY =
        rect.height / (canvasElement.clientHeight || rect.height || 1);
      const style = window.getComputedStyle(canvasElement);
      const paddingLeft = Number.parseFloat(style.paddingLeft) || 0;
      const paddingTop = Number.parseFloat(style.paddingTop) || 0;

      const canvasX = (event.clientX - rect.left) / scaleX - paddingLeft;
      const canvasY = (event.clientY - rect.top) / scaleY - paddingTop;

      return { x: canvasX, y: canvasY };
    },
    []
  );

  const isPointInPaintArea = React.useCallback(
    (
      element: EditorElement,
      point: { x: number; y: number },
      tolerancePx = PAINT_HIT_TOLERANCE
    ) => {
      const context = getPaintHitContext();
      const shapePath = buildPaintAreaPath(element, PAINT_SHAPE_INSET);
      if (context && shapePath) {
        if (context.isPointInPath(shapePath, point.x, point.y)) {
          return true;
        }
        if (tolerancePx <= 0) {
          return false;
        }
        const previousLineWidth = context.lineWidth;
        context.lineWidth = tolerancePx;
        const hit = context.isPointInStroke(shapePath, point.x, point.y);
        context.lineWidth = previousLineWidth;
        return hit;
      }

      const width = element.size.width;
      const height = element.size.height ?? element.size.width ?? 0;
      if (!width || !height) return false;

      const angle = element.rotation ?? 0;
      const centerX = element.position.x + width / 2;
      const centerY = element.position.y + height / 2;
      const rad = (-angle * Math.PI) / 180;
      const cos = Math.cos(rad);
      const sin = Math.sin(rad);
      const shapeType = resolvePaintAreaAppearance(element).shapeType;
      const dx = point.x - centerX;
      const dy = point.y - centerY;
      const localX = dx * cos - dy * sin + width / 2;
      const localY = dx * sin + dy * cos + height / 2;

      if (localX < 0 || localY < 0 || localX > width || localY > height) {
        return false;
      }

      const normalizedX = (localX / width) * 100;
      const normalizedY = (localY / height) * 100;
      const minSize = Math.min(width, height);
      const normalizedTolerance =
        minSize > 0 ? (tolerancePx / minSize) * 100 : 0;
      const clampedTolerance = Math.min(
        PAINT_HIT_MAX_TOLERANCE,
        normalizedTolerance
      );
      return isPointInPaintShape(
        shapeType,
        normalizedX,
        normalizedY,
        clampedTolerance,
        getPaintElementPoints(element)
      );
    },
    []
  );

  const getStableHitAreas = React.useCallback(
    (point: { x: number; y: number }, tolerancePx = PAINT_HIT_TOLERANCE) => {
      const offsets = getPaintHitOffsets(tolerancePx);
      let best: EditorElement[] = [];
      let bestWithCurrent: EditorElement[] = [];

      for (const offset of offsets) {
        const samplePoint = {
          x: point.x + offset.x,
          y: point.y + offset.y,
        };
        const hits = paintAreaElements.filter((el) =>
          isPointInPaintArea(el, samplePoint, tolerancePx)
        );
        if (hits.length > best.length) {
          best = hits;
        }
        if (
          currentElement &&
          hits.some((el) => el.id === currentElement.id) &&
          hits.length > bestWithCurrent.length
        ) {
          bestWithCurrent = hits;
        }
      }

      return bestWithCurrent.length > 0 ? bestWithCurrent : best;
    },
    [paintAreaElements, isPointInPaintArea, currentElement]
  );

  // Sync local state with prop when it changes
  React.useEffect(() => {
    setLocalPaintedColor(formStateColor || paintedColor);
  }, [formStateColor, paintedColor]);

  const mergedIntersectionColors = React.useMemo(
    () => ({
      ...dataIntersectionColors,
      ...formStateIntersectionColors,
    }),
    [dataIntersectionColors, formStateIntersectionColors]
  );

  const areIntersectionColorsEqual = React.useCallback(
    (left: Record<string, string>, right: Record<string, string>) => {
      const leftKeys = Object.keys(left);
      const rightKeys = Object.keys(right);
      if (leftKeys.length !== rightKeys.length) return false;
      for (const key of leftKeys) {
        if (left[key] !== right[key]) return false;
      }
      return true;
    },
    []
  );

  React.useEffect(() => {
    setLocalIntersectionColors((prev) => {
      if (areIntersectionColorsEqual(prev, mergedIntersectionColors)) {
        return prev;
      }
      return mergedIntersectionColors;
    });
  }, [areIntersectionColorsEqual, mergedIntersectionColors]);

  const intersectionKeysForElement = React.useMemo(
    () =>
      Object.keys(localIntersectionColors).filter((key) =>
        parseIntersectionKey(key).includes(elementId)
      ),
    [localIntersectionColors, elementId]
  );

  const clearPaint = React.useCallback(() => {
    const clearedColor = "transparent";
    setLocalPaintedColor(clearedColor);
    if (onUpdate) {
      if (mode === "preview") {
        onUpdate(elementId, {
          paintedColor: clearedColor,
          paintSwatchId: null,
        });
      } else {
        onUpdate(elementId, { paintedColor: clearedColor });
      }
    }
    if (intersectionKeysForElement.length > 0) {
      setLocalIntersectionColors((prev) => {
        const next = { ...prev };
        intersectionKeysForElement.forEach((key) => {
          next[key] = clearedColor;
        });
        return next;
      });
      if (onUpdate) {
        intersectionKeysForElement.forEach((key) => {
          const updateData: Record<string, string | null> = {
            intersectionKey: key,
            intersectionColor: clearedColor,
          };
          if (mode === "preview") {
            updateData.intersectionSwatchId = null;
          }
          onUpdate(elementId, updateData);
        });
      }
    }
  }, [elementId, intersectionKeysForElement, mode, onUpdate]);

  // Handle drop from paint-swatch or color-palette.
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    const types = Array.from(e.dataTransfer.types);
    if (
      types.includes("application/x-paint-color") ||
      types.includes("application/x-paint-swatch-id") ||
      types.includes("application/x-paint-swatch-color")
    ) {
      e.preventDefault();
      e.stopPropagation();
      setIsDragOver(true);
      e.dataTransfer.dropEffect = "copy";
    }
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const draggedSwatchId =
      e.dataTransfer.getData("application/x-paint-swatch-id") || undefined;
    let color =
      e.dataTransfer.getData("application/x-paint-color") ||
      e.dataTransfer.getData("application/x-paint-swatch-color") ||
      undefined;

    if (!color && draggedSwatchId) {
      const swatchElement = allElements?.find((el) => el.id === draggedSwatchId);
      if (swatchElement && swatchElement.type === "paint-swatch") {
        color = resolvePaintSwatchAppearance(swatchElement).color;
      }
    }

    if (!color) return;
    const paintColor = color;

    const dropPoint = getDropPoint(e);
    let hitAreas = dropPoint ? getStableHitAreas(dropPoint) : [];
    if (dropPoint && hitAreas.length <= 1) {
      const looseHitAreas = getStableHitAreas(
        dropPoint,
        PAINT_INTERSECTION_TOLERANCE
      );
      if (looseHitAreas.length > hitAreas.length) {
        hitAreas = looseHitAreas;
      }
    }

    if (hitAreas.length > 1) {
      const intersectionKey = buildIntersectionKey(
        hitAreas.map((area) => area.id)
      );

      setLocalIntersectionColors((prev) => ({
        ...prev,
        [intersectionKey]: paintColor,
      }));

      if (onUpdate) {
        if (mode === "preview") {
          onUpdate(elementId, {
            intersectionKey,
            intersectionColor: paintColor,
            intersectionSwatchId: draggedSwatchId ?? null,
          });
        } else {
          hitAreas.forEach((area) => {
            const areaData = area.data as {
              intersectionColors?: Record<string, string>;
            };
            const nextIntersectionColors = {
              ...(areaData.intersectionColors || {}),
              [intersectionKey]: paintColor,
            };
            onUpdate(area.id, { intersectionColors: nextIntersectionColors });
          });
        }
      } else {
        console.log("onUpdate callback is not provided!");
      }
      return;
    }

    const targetElement = hitAreas[0] || currentElement;
    if (!targetElement) {
      return;
    }

    if (targetElement.id === elementId) {
      // Update local state immediately for instant UI feedback
      setLocalPaintedColor(paintColor);
    }

    if (onUpdate) {
      if (mode === "preview") {
        onUpdate(targetElement.id, {
          paintedColor: paintColor,
          paintSwatchId: draggedSwatchId ?? null,
        });
      } else {
        onUpdate(targetElement.id, { paintedColor: paintColor });
      }
    }
  };

  // Tıkla-boyama: kartelada seçili renk (window.__qeSelectedPaintColor)
  // varken şekle tıklanınca bu şekli o renge boyar. Sürükle-bırak
  // yerine/yanında çalışır.
  const handlePaintClick = () => {
    if (mode !== "preview") return;
    const selected = (
      window as unknown as { __qeSelectedPaintColor?: string }
    ).__qeSelectedPaintColor;
    if (!selected) return;

    setLocalPaintedColor(selected);
    if (onUpdate) {
      if (mode === "preview") {
        onUpdate(elementId, {
          paintedColor: selected,
          paintSwatchId: null,
        });
      } else {
        onUpdate(elementId, { paintedColor: selected });
      }
    }
  };

  const handlePaintDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    if (mode !== "preview" || !isPainted) {
      return;
    }
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/x-painted-area-id", elementId);
  };

  const handlePaintDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    if (mode !== "preview" || !isPainted) {
      return;
    }
    if (e.dataTransfer.dropEffect === "none") {
      clearPaint();
    }
  };

  const renderShapeLayer = (layer: "fill" | "stroke") => {
    const color = localPaintedColor || paintedColor || "transparent";
    const inset = PAINT_SHAPE_INSET;
    // Doğru boyandıysa şekli yeşil (kalın, düz) çerçeveyle çerçevele.
    const correctFrame = isCorrect && layer === "stroke";
    const strokeWidth = correctFrame
      ? Math.max(appearance.borderWidth ?? 1, 3)
      : Math.max(appearance.borderWidth ?? 1, 0);
    const strokeColor = correctFrame
      ? "#22c55e"
      : appearance.borderColor || "#cbd5e1";
    const strokeValue = strokeWidth > 0 ? strokeColor : "none";
    const strokeDasharray = correctFrame
      ? undefined
      : appearance.borderStyle === "dashed"
      ? "4 4"
      : appearance.borderStyle === "dotted"
      ? "1 3"
      : undefined;
    const isFill = layer === "fill";
    const fillValue = isFill && color !== "transparent" ? color : "none";
    const layerStroke = isFill ? "none" : strokeValue;
    const layerStrokeWidth = isFill ? 0 : strokeWidth;
    const layerDasharray = isFill ? undefined : strokeDasharray;
    const shapeStyle: React.CSSProperties = {
      position: "absolute",
      top: `${inset}px`,
      left: `${inset}px`,
      width: `calc(100% - ${inset * 2}px)`,
      height: `calc(100% - ${inset * 2}px)`,
      transition: isFill ? "fill 0.2s, background-color 0.2s" : "none",
      pointerEvents: "none",
    };

    const maskId = isFill ? `url(#${fillMaskId})` : undefined;

    // The shape's geometry (0–100 viewBox) drives both the visible fill/stroke
    // path and the click hit-test, sourced from the shared shape library.
    const mainPoints = currentElement
      ? getPaintElementPoints(currentElement)
      : undefined;
    const mainD = getPaintShapePathD(appearance.shapeType, mainPoints);

    return (
      <svg style={shapeStyle} viewBox="0 0 100 100" preserveAspectRatio="none">
        {isFill && (
          <defs>
            <mask
              id={fillMaskId}
              maskUnits="userSpaceOnUse"
              maskContentUnits="userSpaceOnUse"
            >
              <rect x="0" y="0" width="100" height="100" fill="#fff" />
              {fillMaskShapes.map((shape) => (
                <path
                  key={`fill-mask-${elementId}-${shape.id}`}
                  d={getPaintShapePathD(shape.shapeType, shape.points)}
                  fill="#000"
                  transform={shape.transform}
                />
              ))}
            </mask>
          </defs>
        )}
        <path
          d={mainD}
          fill={fillValue}
          stroke={layerStroke}
          strokeWidth={layerStrokeWidth}
          strokeDasharray={layerDasharray}
          mask={maskId}
        />
      </svg>
    );
  };

  const renderShape = () => renderShapeLayer("fill");
  const renderBorder = () => {
    const strokeWidth = Math.max(appearance.borderWidth ?? 1, 0);
    // Doğru boyandığında yeşil çerçeveyi göstermek için, şeklin normalde
    // kenarlığı olmasa bile stroke katmanını render et.
    if (
      !isCorrect &&
      (appearance.borderStyle === "none" || strokeWidth <= 0)
    ) {
      return null;
    }
    return renderShapeLayer("stroke");
  };

  const intersectionGroups = React.useMemo(() => {
    return Object.entries(localIntersectionColors).reduce<
      Array<{ key: string; ids: string[]; color: string }>
    >((acc, [key, color]) => {
      const ids = parseIntersectionKey(key);
      if (!ids.includes(elementId)) return acc;
      acc.push({ key, ids, color });
      return acc;
    }, []);
  }, [localIntersectionColors, elementId]);

  const intersectionClips = React.useMemo(() => {
    if (!currentElement || intersectionGroups.length === 0) {
      return [];
    }

    const uniqueIds = new Set<string>();
    intersectionGroups.forEach((group) => {
      group.ids.forEach((id) => uniqueIds.add(id));
    });

    return Array.from(uniqueIds).map((id) => {
      const targetElement = paintAreaElements.find((el) => el.id === id);
      if (!targetElement) {
        return null;
      }

      const transformMatrix =
        id === elementId
          ? null
          : getRelativeTransform(
              targetElement,
              currentElement,
              PAINT_SHAPE_INSET
            );
      return {
        id,
        shapeType: resolvePaintAreaAppearance(targetElement).shapeType,
        points: getPaintElementPoints(targetElement),
        transform: transformMatrix ? matrixToSvgTransform(transformMatrix) : undefined,
      };
    }).filter(Boolean) as Array<{
      id: string;
      shapeType: PaintShapeType;
      points?: PaintPoint[];
      transform?: string;
    }>;
  }, [intersectionGroups, paintAreaElements, currentElement, elementId]);

  const containerStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    position: "relative",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: `${appearance.borderRadius}px`,
    overflow: "visible",
    boxSizing: "border-box",
    // Don't use image as background - it's for reference only
  };

  const intersectionStyle: React.CSSProperties = {
    position: "absolute",
    top: `${PAINT_SHAPE_INSET}px`,
    left: `${PAINT_SHAPE_INSET}px`,
    width: `calc(100% - ${PAINT_SHAPE_INSET * 2}px)`,
    height: `calc(100% - ${PAINT_SHAPE_INSET * 2}px)`,
    pointerEvents: "none",
  };

  return (
    <div
      ref={containerRef}
      style={{
        ...containerStyle,
        cursor: mode === "preview" ? "pointer" : containerStyle.cursor,
      }}
      draggable={mode === "preview" && isPainted}
      onDragStart={handlePaintDragStart}
      onDragEnd={handlePaintDragEnd}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={handlePaintClick}
      onAnimationEnd={() => setIsShaking(false)}
      className={`${isDragOver ? "qe-paint-area--drag-over" : ""} ${
        isShaking ? "qe-paint-area--shake" : ""
      }`.trim()}
    >
      {renderShape()}
      {/* Free-polygon vertex editing overlay (editor canvas only). Draggable
          handles update data.points; the fill/hit-test follow automatically. */}
      {mode === "canvas" &&
        appearance.shapeType === "polygon" &&
        currentElement &&
        (currentElement.data as { editVertices?: boolean }).editVertices ===
          true && (
          <div style={{ position: "absolute", inset: 0, pointerEvents: "none" }}>
            <PolygonShapeRenderer
              elementId={elementId}
              points={
                getPaintElementPoints(currentElement) ??
                DEFAULT_FREE_POLYGON_POINTS
              }
              fill="rgba(37, 99, 235, 0.08)"
              stroke="#2563eb"
              strokeWidth={1.5}
              mode="canvas"
              onUpdate={onUpdate}
              editable
            />
          </div>
        )}
      {intersectionGroups.length > 0 && (
        <svg
          style={intersectionStyle}
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <defs>
            {intersectionClips.map((clip) => (
              <clipPath
                key={`paint-clip-${elementId}-${clip.id}`}
                id={`paint-clip-${elementId}-${clip.id}`}
                clipPathUnits="userSpaceOnUse"
              >
                {renderPaintShapeClip(
                  clip.shapeType,
                  {
                    fill: "#fff",
                    transform: clip.transform,
                  },
                  clip.points
                )}
              </clipPath>
            ))}
          </defs>
          {intersectionGroups.map((group) => {
            let node = (
              <rect
                x="0"
                y="0"
                width="100"
                height="100"
                fill={group.color}
              />
            );

            group.ids.forEach((id) => {
              node = (
                <g clipPath={`url(#paint-clip-${elementId}-${id})`}>{node}</g>
              );
            });

            return <g key={group.key}>{node}</g>;
          })}
        </svg>
      )}
      {renderBorder()}
    </div>
  );
};

// Paint Swatch Renderer
interface PaintSwatchRendererProps {
  elementId: string;
  appearance: PaintSwatchAppearance;
  mode: "canvas" | "preview";
  formState?: RenderOptions["formState"];
}

const PaintSwatchRenderer: React.FC<PaintSwatchRendererProps> = ({
  elementId,
  appearance,
  mode,
  formState,
}) => {
  const maxUses = appearance.maxUses ?? 0;
  const usageCount = React.useMemo(() => {
    if (!formState) return 0;
    return Object.entries(formState).reduce((count, [key, value]) => {
      if (
        key.startsWith(PAINT_SWATCH_PREFIX) ||
        key.startsWith(PAINT_INTERSECTION_SWATCH_PREFIX)
      ) {
        if (value === elementId) {
          return count + 1;
        }
      }
      return count;
    }, 0);
  }, [formState, elementId]);
  const isUsageExhausted =
    mode === "preview" && maxUses > 0 && usageCount >= maxUses;
  if (isUsageExhausted) {
    return null;
  }

  const selectThisColor = () => {
    if (mode !== "preview") return;
    try {
      (
        window as unknown as { __qeSelectedPaintColor?: string }
      ).__qeSelectedPaintColor = appearance.color;
    } catch {
      // ignore non-browser environments
    }
  };

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    e.dataTransfer.effectAllowed = "copy";
    e.dataTransfer.setData("application/x-paint-swatch-id", elementId);
    e.dataTransfer.setData(
      "application/x-paint-swatch-color",
      appearance.color
    );
    selectThisColor();
  };

  const containerStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    backgroundColor: appearance.color,
    border: `${appearance.borderWidth}px solid ${appearance.borderColor}`,
    borderRadius: `${appearance.borderRadius}px`,
    boxSizing: "border-box",
    cursor: mode === "preview" ? "pointer" : "default",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    userSelect: "none",
    position: "relative",
  };

  return (
    <div
      draggable={true}
      onDragStart={handleDragStart}
      onClick={selectThisColor}
      style={containerStyle}
      className="qe-paint-swatch"
    >
      {appearance.showLabel && appearance.label && (
        <span
          style={{
            color: appearance.labelColor,
            fontSize: `${appearance.labelFontSize}px`,
            fontWeight: 500,
            textAlign: "center",
            pointerEvents: "none",
          }}
        >
          {appearance.label}
        </span>
      )}
    </div>
  );
};

interface ColorPaletteRendererProps {
  element: EditorElement;
  mode: "canvas" | "preview";
}

const ColorPaletteRenderer: React.FC<ColorPaletteRendererProps> = ({
  element,
  mode,
}) => {
  const data = element.data as {
    colors?: string[];
    columns?: number;
    gap?: number;
    borderRadius?: number;
    borderColor?: string;
    borderWidth?: number;
    showLabels?: boolean;
  };
  const colors =
    Array.isArray(data.colors) && data.colors.length > 0
      ? data.colors
      : ["#ef4444", "#3b82f6", "#22c55e", "#eab308", "#000000", "#ffffff"];
  const columns = Math.max(1, data.columns ?? 6);
  const gap = data.gap ?? 4;
  const radius = data.borderRadius ?? 6;
  const borderColor = data.borderColor ?? "#e5e7eb";
  const borderWidth = data.borderWidth ?? 1;
  const showLabels = data.showLabels === true;
  const [selectedIndex, setSelectedIndex] = React.useState<number | null>(null);

  const selectColor = (index: number) => {
    setSelectedIndex(index);
    try {
      (window as unknown as { __qeSelectedPaintColor?: string })
        .__qeSelectedPaintColor = colors[index];
    } catch {
      // ignore non-browser environments
    }
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        display: "grid",
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap,
        padding: gap,
        boxSizing: "border-box",
      }}
    >
      {colors.map((color, index) => {
        const isSelected = selectedIndex === index;
        const handleDragStart = (e: React.DragEvent<HTMLButtonElement>) => {
          if (mode !== "preview") return;
          e.dataTransfer.effectAllowed = "copy";
          e.dataTransfer.setData("application/x-paint-color", color);
          e.dataTransfer.setData("application/x-paint-swatch-color", color);
          selectColor(index);
        };

        return (
          <button
            key={`${color}-${index}`}
            type="button"
            draggable={mode === "preview"}
            onDragStart={handleDragStart}
            onClick={(e) => {
              e.stopPropagation();
              selectColor(index);
            }}
            title={color}
            style={{
              width: "100%",
              height: "100%",
              minHeight: 24,
              padding: 0,
              background: color,
              borderRadius: radius,
              border: isSelected
                ? "2px solid #1d4ed8"
                : `${borderWidth}px solid ${borderColor}`,
              boxShadow: isSelected
                ? "0 0 0 2px rgba(29, 78, 216, 0.25)"
                : undefined,
              cursor: mode === "preview" ? "grab" : "default",
              boxSizing: "border-box",
              color:
                /^#?[0-9a-f]{6}$/i.test(color.replace("#", "")) &&
                parseInt(color.replace("#", "").slice(0, 2), 16) > 180
                  ? "#0f172a"
                  : "#ffffff",
              fontSize: 10,
            }}
          >
            {showLabels ? color : ""}
          </button>
        );
      })}
    </div>
  );
};

// Table Renderer
interface TableRendererProps {
  element: EditorElement;
  mode: "canvas" | "preview";
  onUpdate?: (elementId: string, data: Partial<EditorElement["data"]>) => void;
  onSelect?: (elementId: string) => void;
  onContextMenu?: (elementId: string, x: number, y: number) => void;
}

const TableRenderer: React.FC<TableRendererProps> = ({
  element,
  mode,
  onUpdate,
  onSelect,
  onContextMenu,
}) => {
  const t = getT();
  const appearance = resolveTableAppearance(element);
  const data = element.data as {
    rows: number;
    columns: number;
    cells: TableCell[];
    rowHeights?: number[];
    columnWidths?: number[];
    appearance?: Partial<TableAppearance>;
    selectedCellId?: string;
    selectedCellIds?: string[];
    cellElements?: Record<string, EditorElement>; // Map of elementId -> element
    locked?: boolean; // Whether table is locked (children are interactive)
  };

  const {
    rows,
    columns,
    cells,
    rowHeights,
    columnWidths,
    cellElements = {},
    locked = false,
  } = data;
  const [dragOverCellId, setDragOverCellId] = React.useState<string | null>(
    null
  );
  // Excel-like inline cell editor state.
  const [editingCellId, setEditingCellId] = React.useState<string | null>(null);
  const [editingDraft, setEditingDraft] = React.useState<string>("");

  // Commit the inline edit (or cancel) and optionally navigate to a neighbor.
  const commitCellEdit = (
    nextRow?: number,
    nextCol?: number,
    cancel = false
  ) => {
    if (!editingCellId || !onUpdate) {
      setEditingCellId(null);
      return;
    }
    if (!cancel) {
      const updatedCells = cells.map((c) =>
        c.id === editingCellId ? { ...c, text: editingDraft } : c
      );
      onUpdate(element.id, { cells: updatedCells });
    }
    setEditingCellId(null);
    setEditingDraft("");
    if (nextRow !== undefined && nextCol !== undefined) {
      const next = cells.find((c) => c.row === nextRow && c.col === nextCol);
      if (next) {
        // Open the next cell on the following tick so we don't fight the blur.
        setTimeout(() => {
          setEditingCellId(next.id);
          setEditingDraft(next.text ?? "");
          onUpdate(element.id, { selectedCellId: next.id });
        }, 0);
      }
    }
  };

  const startCellEdit = (cell: TableCell) => {
    if (mode !== "canvas") return;
    setEditingCellId(cell.id);
    setEditingDraft(cell.text ?? "");
    if (onUpdate) onUpdate(element.id, { selectedCellId: cell.id });
  };

  // Helper function to get cell by row and column
  const getCell = (row: number, col: number): TableCell | undefined => {
    return cells.find((cell) => cell.row === row && cell.col === col);
  };

  // Calculate table dimensions
  const totalWidth =
    columnWidths?.reduce((sum, w) => sum + w, 0) ?? element.size.width;
  const totalHeight = rowHeights?.reduce((sum, h) => sum + h, 0) ?? 300;

  const tableStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    borderRadius: `${appearance.borderRadius}px`,
    // Outer border (table border)
    border: appearance.showOuterBorder
      ? `${appearance.outerBorderWidth}px ${appearance.outerBorderStyle} ${appearance.outerBorderColor}`
      : "none",
    borderCollapse: "separate",
    borderSpacing: `${appearance.columnSpacing}px ${appearance.rowSpacing}px`,
    backgroundColor: appearance.cellBackgroundColor,
    overflow: "hidden",
    boxSizing: "border-box",
  };

  return (
    <table style={tableStyle}>
      <tbody>
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <tr key={`row-${rowIndex}`}>
            {Array.from({ length: columns }).map((_, colIndex) => {
              const cell = getCell(rowIndex, colIndex);
              const isHeader = rowIndex === 0;
              const isAlternateRow =
                appearance.alternateRowColor && rowIndex % 2 === 1;

              const cellBackgroundColor = isHeader
                ? appearance.headerBackgroundColor ??
                  appearance.cellBackgroundColor
                : isAlternateRow
                ? appearance.alternateRowBackgroundColor ??
                  appearance.cellBackgroundColor
                : cell?.backgroundColor ?? appearance.cellBackgroundColor;

              const isDragOver =
                mode === "canvas" && cell && cell.id === dragOverCellId;

              // Smart border strategy to avoid double borders
              const isLastColumn = colIndex === columns - 1;
              const isLastRow = rowIndex === rows - 1;

              // Per-cell style overrides (Excel-like) and multi-cell selection.
              const cellCustom = cell?.cellStyle;
              const multiSelectedIds: string[] = Array.isArray(
                data.selectedCellIds
              )
                ? data.selectedCellIds
                : data.selectedCellId
                ? [data.selectedCellId]
                : [];
              const isSelectedCell =
                mode === "canvas" &&
                !!cell &&
                multiSelectedIds.includes(cell.id);
              const effectiveBg =
                cellCustom?.backgroundColor ??
                (isDragOver ? "#e0f2fe" : cellBackgroundColor);

              const cellStyle: React.CSSProperties = {
                width: columnWidths?.[colIndex] ?? `${100 / columns}%`,
                height: rowHeights?.[rowIndex] ?? `${100 / rows}%`,
                minHeight: rowHeights?.[rowIndex] ?? `${100 / rows}%`,
                padding: `${appearance.cellPadding}px`,
                backgroundColor: effectiveBg,
                // Inner border (cell borders) - only right and bottom to avoid doubling
                borderRight:
                  appearance.showCellBorders && !isLastColumn
                    ? `${appearance.cellBorderWidth}px ${appearance.cellBorderStyle} ${appearance.cellBorderColor}`
                    : "none",
                borderBottom:
                  appearance.showCellBorders && !isLastRow
                    ? `${appearance.cellBorderWidth}px ${appearance.cellBorderStyle} ${appearance.cellBorderColor}`
                    : "none",
                borderTop: "none",
                borderLeft: "none",
                verticalAlign: cellCustom?.verticalAlign ?? "top",
                textAlign: cellCustom?.textAlign ?? "left",
                color:
                  cellCustom?.color ??
                  (isHeader
                    ? appearance.headerTextColor ?? "#1f2937"
                    : "#1f2430"),
                fontSize: cellCustom?.fontSize,
                fontWeight:
                  cellCustom?.fontWeight ??
                  (isHeader ? appearance.headerFontWeight ?? 600 : 400),
                fontStyle: cellCustom?.italic ? "italic" : undefined,
                textDecoration: cellCustom?.underline ? "underline" : undefined,
                boxSizing: "border-box",
                position: "relative",
                cursor: mode === "canvas" ? "cell" : "default",
                outline: isSelectedCell
                  ? "2px solid #2563eb"
                  : isDragOver
                  ? "2px dashed #2563eb"
                  : "none",
                outlineOffset: "-2px",
                transition: "background-color 0.2s, outline 0.2s",
              };

              // Cell click handler - no longer used for selection
              const handleCellClick = () => {
                // Elements inside cells will handle their own click events
              };

              const handleDragOver = (e: React.DragEvent) => {
                if (mode === "canvas" && cell) {
                  e.preventDefault();
                  e.stopPropagation();
                  setDragOverCellId(cell.id);
                }
              };

              const handleDragLeave = (e: React.DragEvent) => {
                if (mode === "canvas" && cell) {
                  e.preventDefault();
                  e.stopPropagation();
                  setDragOverCellId(null);
                }
              };

              const handleDrop = (e: React.DragEvent) => {
                e.preventDefault();
                e.stopPropagation();
                setDragOverCellId(null);

                if (mode !== "canvas" || !cell || !onUpdate) return;

                try {
                  // Try to get template data from library items
                  const templateKey = e.dataTransfer.getData(TEMPLATE_MIME);

                  let droppedElement: EditorElement | null = null;

                  if (templateKey) {
                    // Create element from template
                    droppedElement = createElementFromTemplate(
                      templateKey as ElementTemplateKey
                    );
                  } else {
                    // Try to get direct element data
                    const dragData = e.dataTransfer.getData("application/json");
                    if (dragData) {
                      droppedElement = JSON.parse(dragData) as EditorElement;
                    }
                  }

                  if (!droppedElement) return;

                  // Position element in center of cell (relative to cell, not canvas)
                  // Account for cell padding - use actual table dimensions
                  const cellPadding = appearance.cellPadding || 0;
                  const cellWidth =
                    element.size.width / columns - cellPadding * 2;
                  const cellHeight =
                    (element.size.height ?? 300) / rows - cellPadding * 2;

                  // Resize element if it's too large for the cell
                  const elementWidth = droppedElement.size.width ?? 100;
                  const elementHeight = droppedElement.size.height ?? 50;

                  if (elementWidth > cellWidth || elementHeight > cellHeight) {
                    // Scale down to fit while maintaining aspect ratio
                    const scaleX = cellWidth / elementWidth;
                    const scaleY = cellHeight / elementHeight;
                    const scale = Math.min(scaleX, scaleY, 1) * 0.9; // 90% of cell to leave some margin

                    droppedElement.size = {
                      ...droppedElement.size,
                      width: Math.floor(elementWidth * scale),
                      height: Math.floor(elementHeight * scale),
                    };
                  }

                  // Center element in cell
                  const finalWidth = droppedElement.size.width ?? 100;
                  const finalHeight = droppedElement.size.height ?? 50;

                  droppedElement.position = {
                    x: Math.max(0, (cellWidth - finalWidth) / 2),
                    y: Math.max(0, (cellHeight - finalHeight) / 2),
                  };

                  // Add element to cellElements map
                  const newCellElements = {
                    ...cellElements,
                    [droppedElement.id]: droppedElement,
                  };

                  // Add element ID to cell's elements array
                  const updatedCells = cells.map((c) =>
                    c.id === cell.id
                      ? { ...c, elements: [...c.elements, droppedElement!.id] }
                      : c
                  );

                  onUpdate(element.id, {
                    cells: updatedCells,
                    cellElements: newCellElements,
                  });
                } catch (error) {
                  console.error("Drop error:", error);
                }
              };

              // Render elements inside cell
              let cellContent: React.ReactNode = null;

              if (cell) {
                // Calculate actual content area based on table size
                const cellPadding = appearance.cellPadding || 0;
                // Use actual table size divided by rows/columns for accurate cell dimensions
                const cellWidth =
                  element.size.width / columns - cellPadding * 2;
                const cellHeight =
                  (element.size.height ?? 300) / rows - cellPadding * 2;

                if (cell.elements.length === 0) {
                  // Cell with no embedded elements — Excel-style inline text cell.
                  const isEditing =
                    editingCellId === cell.id && mode === "canvas";
                  if (isEditing) {
                    cellContent = (
                      <input
                        autoFocus
                        value={editingDraft}
                        onChange={(e) => setEditingDraft(e.target.value)}
                        onBlur={() => commitCellEdit()}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        onPointerDown={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            const nextRow = e.shiftKey
                              ? Math.max(0, rowIndex - 1)
                              : Math.min(rows - 1, rowIndex + 1);
                            commitCellEdit(nextRow, colIndex);
                          } else if (e.key === "Tab") {
                            e.preventDefault();
                            const goingBack = e.shiftKey;
                            const nextCol = goingBack
                              ? colIndex - 1
                              : colIndex + 1;
                            if (nextCol < 0 || nextCol >= columns) {
                              const wrapRow = goingBack
                                ? Math.max(0, rowIndex - 1)
                                : Math.min(rows - 1, rowIndex + 1);
                              const wrapCol = goingBack ? columns - 1 : 0;
                              commitCellEdit(wrapRow, wrapCol);
                            } else {
                              commitCellEdit(rowIndex, nextCol);
                            }
                          } else if (e.key === "Escape") {
                            e.preventDefault();
                            commitCellEdit(undefined, undefined, true);
                          }
                          e.stopPropagation();
                        }}
                        style={{
                          width: "100%",
                          height: "100%",
                          border: "none",
                          outline: "none",
                          padding: 0,
                          margin: 0,
                          background: "transparent",
                          font: "inherit",
                          color: "inherit",
                          textAlign: cellCustom?.textAlign ?? "left",
                          boxSizing: "border-box",
                        }}
                      />
                    );
                  } else if (cell.text && cell.text.length > 0) {
                    cellContent = (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems:
                            cellCustom?.verticalAlign === "middle"
                              ? "center"
                              : cellCustom?.verticalAlign === "bottom"
                              ? "flex-end"
                              : "flex-start",
                          justifyContent:
                            cellCustom?.textAlign === "center"
                              ? "center"
                              : cellCustom?.textAlign === "right"
                              ? "flex-end"
                              : "flex-start",
                          userSelect: "none",
                          overflow: "hidden",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-word",
                        }}
                      >
                        {cell.text}
                      </div>
                    );
                  } else if (mode === "canvas") {
                    cellContent = (
                      <div
                        style={{
                          width: "100%",
                          height: "100%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          color: "#cbd5e1",
                          fontSize: "11px",
                          userSelect: "none",
                          fontStyle: "italic",
                        }}
                      >
                        {t("renderers.table.doubleClickToType")}
                      </div>
                    );
                  }
                } else {
                  // Render elements inside cell
                  // Fill the entire cell content area
                  cellContent = (
                    <div
                      style={{
                        position: "relative",
                        width: "100%",
                        height: "100%",
                        overflow: "hidden",
                      }}
                    >
                      {cell.elements.map((elemId) => {
                        const cellElement = cellElements[elemId];
                        if (!cellElement) return null;

                        // When table is locked, render interactive elements
                        if (locked && mode === "canvas" && onUpdate) {
                          const isSelected =
                            (data as any).selectedCellElementId === elemId;
                          return (
                            <CellElement
                              key={elemId}
                              element={cellElement}
                              cellWidth={cellWidth}
                              cellHeight={cellHeight}
                              isSelected={isSelected}
                              onUpdate={(updatedElement: EditorElement) => {
                                const newCellElements = {
                                  ...cellElements,
                                  [elemId]: updatedElement,
                                };
                                onUpdate(element.id, {
                                  cellElements: newCellElements,
                                });
                              }}
                              onDelete={() => {
                                // Remove element from cell and cellElements
                                const updatedCells = cells.map((c) =>
                                  c.id === cell.id
                                    ? {
                                        ...c,
                                        elements: c.elements.filter(
                                          (id) => id !== elemId
                                        ),
                                      }
                                    : c
                                );
                                const newCellElements = { ...cellElements };
                                delete newCellElements[elemId];
                                onUpdate(element.id, {
                                  cells: updatedCells,
                                  cellElements: newCellElements,
                                });
                              }}
                              onSelect={(elemId) => {
                                // Store the selected cell element ID in table data
                                // and keep the table selected
                                if (onUpdate) {
                                  onUpdate(element.id, {
                                    selectedCellElementId: elemId,
                                  });
                                }
                                // Also select the table so properties panel opens
                                if (onSelect) {
                                  onSelect(element.id);
                                }
                              }}
                              onContextMenu={(elemId, x, y) => {
                                // Pass through the context menu event for cell elements
                                if (onContextMenu) {
                                  onContextMenu(elemId, x, y);
                                }
                              }}
                            />
                          );
                        }

                        // Non-interactive rendering (table not locked or preview mode)
                        return (
                          <div
                            key={elemId}
                            style={{
                              position: "absolute",
                              left: cellElement.position.x,
                              top: cellElement.position.y,
                              width: cellElement.size.width,
                              height: cellElement.size.height,
                              pointerEvents: "none",
                            }}
                          >
                            {renderCanvasElementContent(cellElement, { mode })}
                          </div>
                        );
                      })}
                    </div>
                  );
                }
              }

              return (
                <td
                  key={`cell-${rowIndex}-${colIndex}`}
                  style={cellStyle}
                  colSpan={cell?.colSpan ?? 1}
                  rowSpan={cell?.rowSpan ?? 1}
                  onClick={(e) => {
                    if (mode === "canvas" && cell && onUpdate) {
                      e.stopPropagation();
                      // Read existing multi-selection (back-compat with single id).
                      const existing: string[] = Array.isArray(
                        data.selectedCellIds
                      )
                        ? data.selectedCellIds
                        : data.selectedCellId
                        ? [data.selectedCellId]
                        : [];
                      let nextIds: string[];
                      if (e.shiftKey || e.metaKey || e.ctrlKey) {
                        // Toggle this cell in/out of the selection.
                        nextIds = existing.includes(cell.id)
                          ? existing.filter((id) => id !== cell.id)
                          : [...existing, cell.id];
                      } else {
                        nextIds = [cell.id];
                      }
                      onUpdate(element.id, {
                        selectedCellIds: nextIds,
                        // Keep legacy single-id pointer = last clicked.
                        selectedCellId:
                          nextIds[nextIds.length - 1] ?? undefined,
                        // Clicking the cell itself deselects any nested element
                        // so the inspector returns to the cell controls.
                        selectedCellElementId: undefined,
                      });
                    }
                    handleCellClick();
                  }}
                  onDoubleClick={(e) => {
                    if (
                      mode === "canvas" &&
                      cell &&
                      cell.elements.length === 0
                    ) {
                      e.stopPropagation();
                      startCellEdit(cell);
                    }
                  }}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={handleDrop}
                >
                  {cellContent}
                </td>
              );
            })}
          </tr>
        ))}
      </tbody>
    </table>
  );
};

type PushToTalkStatus = "idle" | "listening" | "processing" | "error";

interface PushToTalkRendererProps {
  elementId: string;
  appearance: PushToTalkAppearance;
  mode: "canvas" | "preview";
  formState?: Record<string, string | string[] | Record<string, string>>;
  onPushToTalkChange?: (elementId: string, transcript: string) => void;
}

const PushToTalkRenderer: React.FC<PushToTalkRendererProps> = (props) => {
  const { mode, appearance } = props;
  if (mode !== "preview") {
    return <PushToTalkStatic appearance={appearance} />;
  }
  return <PushToTalkPreviewRenderer {...props} />;
};

const PushToTalkPreviewRenderer: React.FC<PushToTalkRendererProps> = ({
  elementId,
  appearance,
  mode,
  formState,
  onPushToTalkChange,
}) => {
  const t = getT();
  const isPreview = mode === "preview";
  const speechRecognitionCtor = React.useMemo<null | (new () => any)>(() => {
    if (typeof window === "undefined") {
      return null;
    }
    return (
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition ||
      null
    );
  }, []);
  const secureContext =
    typeof window !== "undefined" ? window.isSecureContext : false;
  const baseSpeechSupport = Boolean(speechRecognitionCtor) && secureContext;
  const [speechDisabled] = React.useState(!baseSpeechSupport);
  const supportsSpeechRecognition = baseSpeechSupport && !speechDisabled;

  const [status, setStatus] = React.useState<PushToTalkStatus>("idle");
  const [transcript, setTranscript] = React.useState<string>(() => {
    const initialValue = formState?.[elementId];
    return typeof initialValue === "string" ? initialValue : "";
  });
  // No realtime transcription. We only commit transcript once the user stops.
  const [, setInterimText] = React.useState("");
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);
  const resolvedDefaultErrorMessage =
    appearance.errorPreviewMessage ||
    t("renderers.pushToTalk.defaultErrorMessage");
  const [permanentlyDisabled, setPermanentlyDisabled] = React.useState(false);
  const [hasMicAttempted, setHasMicAttempted] = React.useState(false);
  const recognitionRef = React.useRef<any>(null);
  const isActiveRef = React.useRef(false); // Are we actively recording?
  const bufferedFinalTextRef = React.useRef("");
  const stopRequestedRef = React.useRef(false);

  React.useEffect(() => {
    const externalValue = formState?.[elementId];
    if (typeof externalValue === "string" && externalValue !== transcript) {
      setTranscript(externalValue);
    }
  }, [formState, elementId, transcript]);

  React.useEffect(() => {
    if (!baseSpeechSupport) {
      setPermanentlyDisabled(true);
      setErrorMessage(resolvedDefaultErrorMessage);
    }
  }, [baseSpeechSupport, resolvedDefaultErrorMessage]);

  React.useEffect(() => {
    return () => {
      isActiveRef.current = false;
      if (recognitionRef.current) {
        try {
          recognitionRef.current.abort();
        } catch {
          // Ignore cleanup errors
        }
        recognitionRef.current = null;
      }
    };
  }, []);

  const persistTranscript = React.useCallback(
    (value: string) => {
      setTranscript(value);
      if (onPushToTalkChange) {
        onPushToTalkChange(elementId, value);
      }
      if (value.trim()) {
        console.log("[PushToTalk]", elementId, value);
      }
    },
    [elementId, onPushToTalkChange]
  );

  const stopListening = React.useCallback(() => {
    console.log("=== STOP LISTENING CALLED ===");

    isActiveRef.current = false; // STOP AUTO-RESTART

    if (recognitionRef.current) {
      try {
        stopRequestedRef.current = true;
        setStatus("processing");
        recognitionRef.current.stop();
      } catch (e) {
        console.error("Stop error:", e);
        try {
          recognitionRef.current.abort();
        } catch {
          // ignore
        }
      }
    }

    setInterimText("");
  }, []);

  const startListening = React.useCallback(() => {
    console.log("=== START LISTENING CALLED ===");

    if (recognitionRef.current) {
      console.log("Already running!");
      return;
    }

    if (
      !isPreview ||
      !speechRecognitionCtor ||
      !supportsSpeechRecognition ||
      speechDisabled
    ) {
      console.log("Not supported!");
      return;
    }

    // Detect browser
    const isChrome =
      /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
    const isSafari =
      /Safari/.test(navigator.userAgent) &&
      /Apple Computer/.test(navigator.vendor);
    console.log(
      "🌐 Browser:",
      isChrome ? "Chrome" : isSafari ? "Safari" : "Other"
    );

    try {
      const recognition = new speechRecognitionCtor();

      // Language setting
      const language = appearance.language || "tr-TR";
      recognition.lang = language;
      console.log("🗣️ Language:", language);

      recognition.continuous = true;
      recognition.interimResults = false;
      recognition.maxAlternatives = 1;
      console.log("⚙️ Config: continuous=true, interimResults=false");

      // HANDLER: onstart
      recognition.onstart = () => {
        console.log("✅ STARTED");
        console.log("[PushToTalk][start]", elementId);
      };

      // HANDLER: onresult
      recognition.onresult = (event: any) => {
        console.log(
          "📝 RESULT EVENT - resultIndex:",
          event.resultIndex,
          "total results:",
          event.results.length
        );
        let finalAggregate = "";
        for (let i = 0; i < event.results.length; i++) {
          const result = event.results[i];
          if (result?.isFinal) {
            finalAggregate += `${result[0]?.transcript ?? ""} `;
          }
        }
        bufferedFinalTextRef.current = finalAggregate
          .replace(/\s+/g, " ")
          .trim();
      };

      // HANDLER: onerror
      recognition.onerror = (event: any) => {
        console.log("❌ ERROR:", event.error);
        console.log("[PushToTalk][error]", elementId, event?.error);

        if (event.error === "aborted") return;

        if (event.error === "no-speech") {
          console.log("No speech - will auto restart");
          return;
        }

        // Always show a single default message (customizable from editor appearance).
        setPermanentlyDisabled(true);
        setErrorMessage(resolvedDefaultErrorMessage);
        setStatus("error");
        isActiveRef.current = false;
      };

      // HANDLER: onend
      recognition.onend = () => {
        console.log("🛑 ENDED - isActive:", isActiveRef.current);
        console.log("[PushToTalk][end]", elementId);

        recognitionRef.current = null;

        if (stopRequestedRef.current) {
          const final = bufferedFinalTextRef.current
            .replace(/\s+/g, " ")
            .trim();
          if (final) {
            persistTranscript(final);
          }
          stopRequestedRef.current = false;
          isActiveRef.current = false;
          setStatus("idle");
          return;
        }

        // Browser ended unexpectedly; keep recording if user intent is active.
        if (isActiveRef.current) {
          window.setTimeout(() => {
            if (isActiveRef.current && !recognitionRef.current) {
              startListening();
            }
          }, 150);
          return;
        }

        setStatus("idle");
      };

      recognitionRef.current = recognition;

      console.log("🆕 START - resetting buffer");
      bufferedFinalTextRef.current = "";
      persistTranscript("");
      setInterimText("");
      stopRequestedRef.current = false;

      isActiveRef.current = true;
      setStatus("listening");
      setErrorMessage(null);

      recognition.start();
      console.log("🎙️ START CALLED");
    } catch (error: any) {
      console.error("FATAL ERROR:", error);
      setErrorMessage(resolvedDefaultErrorMessage);
      setStatus("error");
    }
  }, [
    resolvedDefaultErrorMessage,
    appearance.language,
    elementId,
    isPreview,
    persistTranscript,
    speechRecognitionCtor,
    speechDisabled,
    supportsSpeechRecognition,
  ]);

  const isSupported = supportsSpeechRecognition && !permanentlyDisabled;
  const buttonDisabled = !isPreview || !isSupported;
  let buttonBackground = appearance.buttonIdleColor;
  let buttonForeground = appearance.buttonIdleTextColor;
  if (status === "listening") {
    buttonBackground = appearance.buttonListeningColor;
    buttonForeground = appearance.buttonListeningTextColor;
  }

  const containerStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: 0,
    boxSizing: "border-box",
    padding: 0,
    color: appearance.textColor,
    alignItems: "center",
    justifyContent: "center",
  };

  const transcriptionInputStyle: React.CSSProperties = {
    width: "100%",
    minHeight: "44px",
    borderRadius: "12px",
    border: "1px solid #cbd5e1",
    padding: "10px 12px",
    fontSize: "14px",
    boxSizing: "border-box" as const,
    // Keep text visible across themes: if textColor is light, default textarea (white)
    // background would make typed text look "invisible". Tie background to appearance.
    backgroundColor: appearance.backgroundColor,
    lineHeight: 1.4,
    whiteSpace: "pre-wrap",
  };

  const displayedTranscript = transcript;

  const handleManualChange = React.useCallback(
    (value: string) => {
      if (!isPreview) return;
      setInterimText("");
      persistTranscript(value);
    },
    [isPreview, persistTranscript]
  );

  const handleButtonClick = () => {
    if (!isPreview || !isSupported) {
      return;
    }
    if (status === "listening") {
      stopListening();
    } else if (status === "idle") {
      setHasMicAttempted(true);
      startListening();
    }
  };

  const hasTranscript = Boolean(displayedTranscript.trim());
  const showManualFallback = Boolean(errorMessage) || !isSupported;
  const showTranscript =
    isPreview &&
    appearance.showManualInput !== false &&
    appearance.showTranscription !== false &&
    (hasTranscript || (hasMicAttempted && showManualFallback));
  const micSizePx = appearance.buttonSizePx || 72;
  const rippleRadiusPx =
    appearance.buttonShape === "square"
      ? Math.max(0, Math.min(32, appearance.buttonCornerRadiusPx || 12))
      : appearance.buttonCornerRadiusPx || 999;
  const buttonContentType = appearance.buttonContentType || "icon";
  const buttonContent = (() => {
    const type = appearance.buttonContentType || "icon";
    if (
      type === "image" &&
      (appearance.buttonImageUrl ||
        appearance.buttonImageUrlIdle ||
        appearance.buttonImageUrlListening)
    ) {
      const resolvedImageUrl =
        status === "listening"
          ? appearance.buttonImageUrlListening ||
            appearance.buttonImageUrl ||
            appearance.buttonImageUrlIdle ||
            ""
          : appearance.buttonImageUrlIdle ||
            appearance.buttonImageUrl ||
            appearance.buttonImageUrlListening ||
            "";
      return (
        <img
          src={resolvedImageUrl}
          alt=""
          draggable={false}
          style={{
            width: appearance.buttonContentSizePx || 28,
            height: appearance.buttonContentSizePx || 28,
            maxWidth: "100%",
            maxHeight: "100%",
            objectFit: "cover",
            objectPosition: "center",
            display: "block",
            pointerEvents: "none",
            userSelect: "none",
          }}
        />
      );
    }
    if (type === "text") {
      const label =
        status === "listening"
          ? appearance.listeningButtonText || t("renderers.pushToTalk.stopButtonText")
          : appearance.idleButtonText || t("renderers.pushToTalk.startButtonText");
      return (
        <span
          style={{
            fontSize: `${appearance.buttonTextFontSize || 14}px`,
            fontWeight: 700,
            letterSpacing: 0.2,
            lineHeight: 1,
            padding: "0 10px",
            textAlign: "center",
            pointerEvents: "none",
            userSelect: "none",
          }}
        >
          {label}
        </span>
      );
    }
    return (
      <svg
        width={appearance.buttonContentSizePx || 28}
        height={appearance.buttonContentSizePx || 28}
        viewBox="0 0 24 24"
        fill="none"
        aria-hidden
        style={{ display: "block" }}
      >
        <path
          d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3Z"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        <path
          d="M19 11a7 7 0 0 1-14 0"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
        <path
          d="M12 18v3"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        />
      </svg>
    );
  })();

  return (
    <div style={containerStyle}>
      <style>
        {`
          @keyframes qe-ptt-ripple {
            0% { transform: translate(-50%, -50%) scale(0.65); opacity: 0.35; }
            100% { transform: translate(-50%, -50%) scale(1.35); opacity: 0; }
          }
          @keyframes qe-ptt-breathe {
            0% { transform: scale(1); }
            50% { transform: scale(1.03); }
            100% { transform: scale(1); }
          }
          .qe-ptt-button {
            -webkit-tap-highlight-color: transparent;
            outline: none;
          }
          .qe-ptt-button:focus-visible {
            box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.35), 0 10px 30px rgba(15, 23, 42, 0.12);
          }
        `}
      </style>

      {(!errorMessage || appearance.showMicButton !== false) && (
        <button
          type="button"
          className="qe-ptt-button"
          disabled={buttonDisabled}
          onClick={(event) => {
            event.preventDefault();
            handleButtonClick();
          }}
          title={
            status === "listening"
              ? t("renderers.pushToTalk.stopTitle")
              : t("renderers.pushToTalk.startTitle")
          }
          aria-label={
            status === "listening"
              ? t("renderers.pushToTalk.listeningAriaLabel")
              : t("renderers.pushToTalk.idleAriaLabel")
          }
          style={{
            width: buttonContentType === "text" ? "auto" : `${micSizePx}px`,
            minWidth:
              buttonContentType === "text" ? `${micSizePx}px` : undefined,
            height: `${micSizePx}px`,
            borderRadius: `${appearance.buttonCornerRadiusPx || 999}px`,
            border:
              (appearance.buttonBorderWidthPx || 0) > 0
                ? `${appearance.buttonBorderWidthPx}px solid ${
                    appearance.buttonBorderColor || "transparent"
                  }`
                : "none",
            padding: `${appearance.buttonPaddingPx || 0}px`,
            outline: "none",
            backgroundColor: buttonDisabled ? "#94a3b8" : buttonBackground,
            color: buttonForeground,
            cursor: buttonDisabled ? "not-allowed" : "pointer",
            display: "grid",
            placeItems: "center",
            position: "relative",
            overflow: buttonContentType === "image" ? "hidden" : "visible",
            boxShadow:
              status === "listening"
                ? `0 0 0 6px ${appearance.buttonListeningColor}22`
                : "0 10px 30px rgba(15, 23, 42, 0.12)",
            transition:
              "transform 160ms ease, background-color 200ms ease, box-shadow 200ms ease",
            animation:
              status === "listening"
                ? "qe-ptt-breathe 1.6s ease-in-out infinite"
                : undefined,
          }}
        >
          {status === "listening" && (
            <>
              <span
                aria-hidden
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  width: "100%",
                  height: "100%",
                  borderRadius: `${rippleRadiusPx}px`,
                  border: `2px solid ${appearance.waveformColor}`,
                  transform: "translate(-50%, -50%)",
                  opacity: 0.25,
                }}
              />
              <span
                aria-hidden
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  width: "100%",
                  height: "100%",
                  borderRadius: `${rippleRadiusPx}px`,
                  border: `2px solid ${appearance.waveformColor}`,
                  animation: "qe-ptt-ripple 1.35s ease-out infinite",
                  transform: "translate(-50%, -50%)",
                }}
              />
              <span
                aria-hidden
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  width: "100%",
                  height: "100%",
                  borderRadius: `${rippleRadiusPx}px`,
                  border: `2px solid ${appearance.waveformColor}`,
                  animation: "qe-ptt-ripple 1.35s ease-out infinite",
                  animationDelay: "0.45s",
                  transform: "translate(-50%, -50%)",
                }}
              />
            </>
          )}
          {buttonContent}
        </button>
      )}

      {appearance.showErrorAlert !== false && errorMessage && (
        <div
          style={{
            fontSize: "12px",
            padding: "8px 12px",
            backgroundColor: appearance.errorBackgroundColor || "#fef2f2",
            border: `1px solid ${appearance.errorBorderColor || "#fecaca"}`,
            borderRadius: "6px",
            color: appearance.errorTextColor || "#dc2626",
            lineHeight: 1.4,
            width: "100%",
          }}
        >
          {errorMessage}
        </div>
      )}

      {showTranscript && (
        <div
          style={{
            width: "100%",
            display: "flex",
            flexDirection: "column",
            gap: "6px",
          }}
        >
          {appearance.showManualInputLabel !== false && (
            <div style={{ fontWeight: 600, fontSize: "13px" }}>
              {appearance.transcriptionLabel || t("renderers.pushToTalk.canvasTranscriptionLabel")}
            </div>
          )}
          <textarea
            aria-live="polite"
            value={displayedTranscript}
            onChange={(event) => handleManualChange(event.target.value)}
            placeholder={appearance.transcriptionPlaceholder}
            readOnly={!isPreview}
            style={{
              ...transcriptionInputStyle,
              color: appearance.textColor,
              resize: "none",
            }}
          >
          </textarea>
        </div>
      )}
    </div>
  );
};

const PushToTalkStatic: React.FC<{ appearance: PushToTalkAppearance }> = ({
  appearance,
}) => {
  const t = getT();
  const previewStatus = appearance.editorPreviewStatus || "idle";
  const isListening = previewStatus === "listening";
  const isError = previewStatus === "error";
  const buttonType = appearance.buttonContentType || "icon";
  const buttonSizePx = appearance.buttonSizePx || 72;
  const rippleRadiusPx =
    appearance.buttonShape === "square"
      ? Math.max(0, Math.min(32, appearance.buttonCornerRadiusPx || 12))
      : appearance.buttonCornerRadiusPx || 999;
  const buttonBg = isError
    ? appearance.buttonListeningColor
    : isListening
    ? appearance.buttonListeningColor
    : appearance.buttonIdleColor;
  const buttonFg = isError
    ? appearance.buttonListeningTextColor
    : isListening
    ? appearance.buttonListeningTextColor
    : appearance.buttonIdleTextColor;
  const imageUrl = isListening
    ? appearance.buttonImageUrlListening ||
      appearance.buttonImageUrl ||
      appearance.buttonImageUrlIdle ||
      ""
    : appearance.buttonImageUrlIdle ||
      appearance.buttonImageUrl ||
      appearance.buttonImageUrlListening ||
      "";

  const containerStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "10px",
    backgroundColor: "transparent",
    border: "none",
    borderRadius: 0,
    boxSizing: "border-box",
    padding: isError ? 12 : 0,
    color: appearance.textColor,
    alignItems: "stretch",
    justifyContent: isError ? "flex-start" : "center",
    overflow: isError ? "auto" : "hidden",
  };

  return (
    <div style={containerStyle}>
      <style>
        {`
          @keyframes qe-ptt-ripple {
            0% { transform: translate(-50%, -50%) scale(0.65); opacity: 0.35; }
            100% { transform: translate(-50%, -50%) scale(1.35); opacity: 0; }
          }
          @keyframes qe-ptt-breathe {
            0% { transform: scale(1); }
            50% { transform: scale(1.03); }
            100% { transform: scale(1); }
          }
        `}
      </style>
      {(!isError || appearance.showMicButton !== false) && (
        <button
          type="button"
          disabled
          aria-label={t("renderers.pushToTalk.canvasAriaLabel")}
          style={{
            alignSelf: "center",
            width: buttonType === "text" ? "auto" : `${buttonSizePx}px`,
            minWidth: buttonType === "text" ? `${buttonSizePx}px` : undefined,
            height: `${buttonSizePx}px`,
            borderRadius: `${appearance.buttonCornerRadiusPx || 999}px`,
            border:
              (appearance.buttonBorderWidthPx || 0) > 0
                ? `${appearance.buttonBorderWidthPx}px solid ${
                    appearance.buttonBorderColor || "transparent"
                  }`
                : "none",
            padding: `${appearance.buttonPaddingPx || 0}px`,
            backgroundColor: buttonBg,
            color: buttonFg,
            cursor: "not-allowed",
            display: "grid",
            placeItems: "center",
            boxShadow: "0 10px 30px rgba(15, 23, 42, 0.10)",
            pointerEvents: "none",
            overflow:
              appearance.buttonContentType === "image" ? "hidden" : "visible",
            position: "relative",
            animation: isListening
              ? "qe-ptt-breathe 1.6s ease-in-out infinite"
              : undefined,
          }}
        >
          {isListening && !isError && (
            <>
              <span
                aria-hidden
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  width: "100%",
                  height: "100%",
                  borderRadius: `${rippleRadiusPx}px`,
                  border: `2px solid ${appearance.waveformColor}`,
                  transform: "translate(-50%, -50%)",
                  opacity: 0.25,
                }}
              />
              <span
                aria-hidden
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  width: "100%",
                  height: "100%",
                  borderRadius: `${rippleRadiusPx}px`,
                  border: `2px solid ${appearance.waveformColor}`,
                  animation: "qe-ptt-ripple 1.35s ease-out infinite",
                  transform: "translate(-50%, -50%)",
                }}
              />
              <span
                aria-hidden
                style={{
                  position: "absolute",
                  left: "50%",
                  top: "50%",
                  width: "100%",
                  height: "100%",
                  borderRadius: `${rippleRadiusPx}px`,
                  border: `2px solid ${appearance.waveformColor}`,
                  animation: "qe-ptt-ripple 1.35s ease-out infinite",
                  animationDelay: "0.45s",
                  transform: "translate(-50%, -50%)",
                }}
              />
            </>
          )}
          {(() => {
            const type = appearance.buttonContentType || "icon";
            if (
              type === "image" &&
              (appearance.buttonImageUrl ||
                appearance.buttonImageUrlIdle ||
                appearance.buttonImageUrlListening)
            ) {
              return (
                <img
                  src={imageUrl}
                  alt=""
                  draggable={false}
                  style={{
                    width: appearance.buttonContentSizePx || 28,
                    height: appearance.buttonContentSizePx || 28,
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "cover",
                    objectPosition: "center",
                    display: "block",
                    pointerEvents: "none",
                    userSelect: "none",
                  }}
                />
              );
            }
            if (type === "text") {
              const label = isListening
                ? appearance.listeningButtonText || t("renderers.pushToTalk.stopButtonText")
                : appearance.idleButtonText || t("renderers.pushToTalk.startButtonText");
              return (
                <span
                  style={{
                    fontSize: `${appearance.buttonTextFontSize || 14}px`,
                    fontWeight: 700,
                    letterSpacing: 0.2,
                    lineHeight: 1,
                    padding: "0 10px",
                    textAlign: "center",
                    pointerEvents: "none",
                    userSelect: "none",
                    whiteSpace: "nowrap",
                  }}
                >
                  {label}
                </span>
              );
            }
            return (
              <svg
                width={appearance.buttonContentSizePx || 28}
                height={appearance.buttonContentSizePx || 28}
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden
              >
                <path
                  d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3Z"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M19 11a7 7 0 0 1-14 0"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M12 18v3"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            );
          })()}
        </button>
      )}
      {isError && (
        <>
          {appearance.showErrorAlert !== false && (
            <div
              style={{
                fontSize: "12px",
                padding: "8px 12px",
                backgroundColor: appearance.errorBackgroundColor || "#fef2f2",
                border: `1px solid ${appearance.errorBorderColor || "#fecaca"}`,
                borderRadius: "6px",
                color: appearance.errorTextColor || "#dc2626",
                lineHeight: 1.4,
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
                textAlign: "left",
              }}
            >
              {appearance.errorPreviewMessage ||
                t("renderers.pushToTalk.canvasErrorMessage")}
            </div>
          )}

          {appearance.showManualInput !== false &&
            appearance.showTranscription !== false && (
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  gap: "6px",
                }}
              >
                {appearance.showManualInputLabel !== false && (
                  <div style={{ fontWeight: 600, fontSize: "13px" }}>
                    {appearance.transcriptionLabel || t("renderers.pushToTalk.canvasTranscriptionLabel")}
                  </div>
                )}
                <input
                  value=""
                  placeholder={appearance.transcriptionPlaceholder}
                  readOnly
                  type="text"
                  style={{
                    width: "100%",
                    height: "44px",
                    borderRadius: "12px",
                    border: "1px solid #cbd5e1",
                    padding: "10px 12px",
                    fontSize: "14px",
                    boxSizing: "border-box",
                    color: appearance.textColor,
                    backgroundColor: appearance.backgroundColor,
                  }}
                />
              </div>
            )}
        </>
      )}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Geometric shape element renderer
// ─────────────────────────────────────────────────────────────────────
interface ShapeRendererProps {
  element: EditorElement;
  mode: "canvas" | "preview";
  onUpdate?: (elementId: string, data: Partial<EditorElement["data"]>) => void;
}

const ShapeRenderer: React.FC<ShapeRendererProps> = ({
  element,
  mode,
  onUpdate,
}) => {
  const data = element.data as {
    kind?: ShapeKind;
    fill?: string;
    stroke?: string;
    strokeWidth?: number;
    strokeStyle?: "solid" | "dashed" | "dotted";
    points?: Array<{ x: number; y: number }>;
    lockAspectRatio?: boolean;
    cornerRadius?: number;
    editVertices?: boolean;
  };
  const kind = data.kind ?? "rectangle";
  const fill = data.fill ?? "#dbeafe";
  const stroke = data.stroke ?? "#1d4ed8";
  const strokeWidth = data.strokeWidth ?? 2;
  const cornerRadius =
    typeof data.cornerRadius === "number" && data.cornerRadius > 0
      ? data.cornerRadius
      : 0;
  const dasharray =
    data.strokeStyle === "dashed"
      ? "6 4"
      : data.strokeStyle === "dotted"
      ? // Zero-length dashes + round linecap (set via CSS) render as round dots.
        // Spacing scales with the stroke width so dots stay evenly separated.
        `0 ${Math.max(strokeWidth * 2, 1)}`
      : undefined;
  // When the aspect ratio is locked the SVG keeps its 1:1 viewBox proportions
  // (centered inside the element box); otherwise it stretches to fill.
  const defaultLocked = SHAPES_WITH_LOCKED_DEFAULT.has(kind);
  const locked = data.lockAspectRatio ?? defaultLocked;
  const preserveAR = locked ? "xMidYMid meet" : "none";

  // Polygon: render free-form polygon from data.points, with vertex handles
  // in canvas mode for direct manipulation.
  if (kind === "polygon") {
    const points =
      Array.isArray(data.points) && data.points.length >= 3
        ? data.points
        : DEFAULT_POLYGON_POINTS;
    return (
      <PolygonShapeRenderer
        elementId={element.id}
        points={points}
        fill={fill}
        stroke={stroke}
        strokeWidth={strokeWidth}
        dasharray={dasharray}
        mode={mode}
        onUpdate={onUpdate}
        preserveAspectRatio={preserveAR}
        cornerRadius={cornerRadius}
        // Vertex-drag handles only when explicitly editing; otherwise the SVG
        // stays click-through so the element can be resized/moved normally.
        editable={data.editVertices === true}
      />
    );
  }

  // When the user wants rounded corners, replace the parametric SVG body of
  // polygon-based shapes with a path that Q-bezier-rounds each vertex.
  // Rectangles use native SVG rx (cleaner, no aliasing).
  if (cornerRadius > 0 && isRoundableShape(kind)) {
    if (kind === "rectangle" || kind === "square") {
      return (
        <svg
          className="qe-shape-svg"
          width="100%"
          height="100%"
          viewBox="0 0 100 100"
          preserveAspectRatio={preserveAR}
          style={{
            display: "block",
            overflow: "visible",
            pointerEvents: "none",
          }}
        >
          <rect
            x={strokeWidth / 2}
            y={strokeWidth / 2}
            width={100 - strokeWidth}
            height={100 - strokeWidth}
            rx={cornerRadius}
            ry={cornerRadius}
            fill={fill}
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeDasharray={dasharray}
          />
        </svg>
      );
    }
    const pts = POLYGON_POINTS_FOR_SHAPE[kind];
    if (pts && pts.length >= 3) {
      const d = roundedPolygonPath(pts, cornerRadius);
      return (
        <svg
          className="qe-shape-svg"
          width="100%"
          height="100%"
          viewBox="0 0 100 100"
          preserveAspectRatio={preserveAR}
          style={{
            display: "block",
            overflow: "visible",
            pointerEvents: "none",
          }}
        >
          <path
            d={d}
            fill={fill}
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeDasharray={dasharray}
            strokeLinejoin="round"
          />
        </svg>
      );
    }
  }

  const def = SHAPE_BY_KIND[kind] ?? SHAPE_BY_KIND.rectangle;
  return (
    <svg
      className="qe-shape-svg"
      width="100%"
      height="100%"
      viewBox="0 0 100 100"
      preserveAspectRatio={preserveAR}
      style={{
        display: "block",
        overflow: "visible",
        pointerEvents: "none",
      }}
    >
      {def.render({ fill, stroke, strokeWidth, dasharray })}
    </svg>
  );
};

/**
 * Shape kinds that default to aspect-ratio-locked rendering. Stretching these
 * to non-1:1 boxes produces visually broken figures, so the editor keeps them
 * proportional by default. The user can still opt out per element.
 */
const SHAPES_WITH_LOCKED_DEFAULT = new Set<ShapeKind>([
  "circle",
  "square",
  "triangle",
  "pentagon",
  "hexagon",
  "octagon",
  "star",
  "rhombus",
  "heart",
  "cube",
  "cylinder",
  "cone",
  "sphere",
  "pyramid",
]);

const DEFAULT_POLYGON_POINTS: Array<{ x: number; y: number }> = [
  { x: 50, y: 10 },
  { x: 88, y: 38 },
  { x: 73, y: 88 },
  { x: 27, y: 88 },
  { x: 12, y: 38 },
];

interface PolygonShapeRendererProps {
  elementId: string;
  points: Array<{ x: number; y: number }>;
  fill: string;
  stroke: string;
  strokeWidth: number;
  dasharray?: string;
  mode: "canvas" | "preview";
  onUpdate?: (elementId: string, data: Partial<EditorElement["data"]>) => void;
  preserveAspectRatio?: string;
  cornerRadius?: number;
  editable?: boolean;
}

const PolygonShapeRenderer: React.FC<PolygonShapeRendererProps> = ({
  elementId,
  points,
  fill,
  stroke,
  strokeWidth,
  dasharray,
  mode,
  onUpdate,
  preserveAspectRatio = "none",
  cornerRadius = 0,
  editable = false,
}) => {
  // Vertex handles are interactive only while editing; otherwise the SVG is
  // click-through so the element box can be resized/moved like any other shape.
  const showHandles = mode === "canvas" && editable;
  const svgRef = React.useRef<SVGSVGElement | null>(null);
  const dragIndexRef = React.useRef<number | null>(null);

  const polyStr = points.map((p) => `${p.x},${p.y}`).join(" ");

  // Translate a pointer event into SVG viewBox coordinates (0–100).
  const pointerToViewBox = (
    e: React.PointerEvent<SVGElement>
  ): { x: number; y: number } | null => {
    const svg = svgRef.current;
    if (!svg) return null;
    const rect = svg.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return null;
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    return {
      x: Math.max(0, Math.min(100, x)),
      y: Math.max(0, Math.min(100, y)),
    };
  };

  const updatePoint = (idx: number, p: { x: number; y: number }) => {
    if (!onUpdate) return;
    const next = points.map((pt, i) => (i === idx ? p : pt));
    onUpdate(elementId, { points: next });
  };

  const handleHandlePointerDown =
    (idx: number) => (e: React.PointerEvent<SVGCircleElement>) => {
      if (mode !== "canvas" || !onUpdate) return;
      e.stopPropagation();
      e.preventDefault();
      dragIndexRef.current = idx;
      (e.target as SVGCircleElement).setPointerCapture(e.pointerId);
    };
  const handleHandlePointerMove = (
    e: React.PointerEvent<SVGCircleElement>
  ) => {
    const idx = dragIndexRef.current;
    if (idx == null) return;
    const p = pointerToViewBox(e);
    if (!p) return;
    updatePoint(idx, p);
  };
  const handleHandlePointerUp = (e: React.PointerEvent<SVGCircleElement>) => {
    dragIndexRef.current = null;
    try {
      (e.target as SVGCircleElement).releasePointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
  };

  const handleR = 2.2;

  return (
    <svg
      ref={svgRef}
      className="qe-shape-svg"
      width="100%"
      height="100%"
      viewBox="0 0 100 100"
      preserveAspectRatio={preserveAspectRatio}
      style={{
        display: "block",
        overflow: "visible",
        pointerEvents: showHandles ? "auto" : "none",
      }}
    >
      {cornerRadius > 0 ? (
        <path
          d={roundedPolygonPath(points, cornerRadius)}
          fill={fill}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeDasharray={dasharray}
          strokeLinejoin="round"
          pointerEvents="none"
        />
      ) : (
        <polygon
          points={polyStr}
          fill={fill}
          stroke={stroke}
          strokeWidth={strokeWidth}
          strokeDasharray={dasharray}
          strokeLinejoin="round"
          pointerEvents="none"
        />
      )}
      {showHandles &&
        points.map((p, i) => (
          <circle
            key={i}
            cx={p.x}
            cy={p.y}
            r={handleR}
            fill="#ffffff"
            stroke="#1d4ed8"
            strokeWidth={0.6}
            data-role="no-drag"
            onPointerDown={handleHandlePointerDown(i)}
            onPointerMove={handleHandlePointerMove}
            onPointerUp={handleHandlePointerUp}
            style={{ cursor: "grab" }}
          />
        ))}
    </svg>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Click-to-count ("Tıklayarak Sayma") element renderer
// ─────────────────────────────────────────────────────────────────────

/** Convert a number to Turkish words so TTS reads "on iki" instead of
 *  "twelve" when no Turkish voice is installed. */
function numberToTurkish(n: number): string {
  if (n === 0) return "sıfır";
  if (!Number.isFinite(n)) return String(n);
  if (n < 0) return "eksi " + numberToTurkish(-n);
  if (!Number.isInteger(n)) {
    const [intPart, decPart] = String(n).split(".");
    const intWords = numberToTurkish(parseInt(intPart, 10));
    const decWords = decPart
      .split("")
      .map((d) => numberToTurkish(parseInt(d, 10)))
      .join(" ");
    return `${intWords} virgül ${decWords}`;
  }
  const ones = [
    "",
    "bir",
    "iki",
    "üç",
    "dört",
    "beş",
    "altı",
    "yedi",
    "sekiz",
    "dokuz",
  ];
  const tens = [
    "",
    "on",
    "yirmi",
    "otuz",
    "kırk",
    "elli",
    "altmış",
    "yetmiş",
    "seksen",
    "doksan",
  ];
  const group = (g: number, withOne: boolean): string => {
    const parts: string[] = [];
    const h = Math.floor(g / 100);
    const t = Math.floor((g % 100) / 10);
    const o = g % 10;
    if (h > 0) parts.push(h === 1 ? "yüz" : ones[h] + " yüz");
    if (t > 0) parts.push(tens[t]);
    if (o > 0) parts.push(ones[o]);
    if (parts.length === 0 && withOne) parts.push("bir");
    return parts.join(" ");
  };
  const million = Math.floor(n / 1_000_000);
  const thousand = Math.floor((n % 1_000_000) / 1000);
  const rest = n % 1000;
  const out: string[] = [];
  if (million > 0) out.push(group(million, true) + " milyon");
  if (thousand > 0)
    out.push(thousand === 1 ? "bin" : group(thousand, false) + " bin");
  if (rest > 0) out.push(group(rest, false));
  return out.join(" ").trim() || "sıfır";
}

const CountAreaRenderer: React.FC<{
  element: EditorElement;
  mode: "canvas" | "preview";
  onUpdate?: (elementId: string, data: Partial<EditorElement["data"]>) => void;
}> = ({ element, mode, onUpdate }) => {
  const data = element.data as {
    items?: Array<{
      id: string;
      type: string;
      imageUrl: string;
      position: { x: number; y: number };
      size: { width: number; height: number };
    }>;
    appearance?: {
      backgroundColor?: string;
      borderColor?: string;
      borderWidth?: number;
      borderStyle?: string;
      borderRadius?: number;
      showFrame?: boolean;
      showNumbers?: boolean;
      speakOnClick?: boolean;
      numberingMode?: "total" | "per-type";
      numberBadgeBg?: string;
      numberBadgeColor?: string;
      numberBadgeSize?: number;
      numberBadgeFontWeight?: 400 | 500 | 600 | 700 | 800;
      numberBadgePosition?:
        | "top-left"
        | "top-right"
        | "bottom-right"
        | "bottom-left"
        | "center";
      numberBadgeRadius?: number;
      speechLanguage?: string;
      itemGap?: number;
      startNumber?: number;
      incrementBy?: number;
    };
  };
  const items = data.items ?? [];
  const ap = data.appearance ?? {};
  const showNumbers = ap.showNumbers !== false;
  const speakOnClick = ap.speakOnClick !== false;
  const numberingMode: "total" | "per-type" =
    ap.numberingMode === "per-type" ? "per-type" : "total";
  const badgeSize = ap.numberBadgeSize ?? 28;
  // Badge corner rounding: undefined keeps the historic full circle (half the
  // badge size); a set value is applied as px (CSS clamps to half → circle).
  const badgeRadius = ap.numberBadgeRadius ?? Math.round(badgeSize / 2);
  const badgePos = ap.numberBadgePosition ?? "center";
  const badgeInset = 4;
  const badgePositionStyle: React.CSSProperties =
    badgePos === "top-left"
      ? { top: badgeInset, left: badgeInset }
      : badgePos === "top-right"
      ? { top: badgeInset, right: badgeInset }
      : badgePos === "bottom-right"
      ? { bottom: badgeInset, right: badgeInset }
      : badgePos === "bottom-left"
      ? { bottom: badgeInset, left: badgeInset }
      : { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  const speechLang = ap.speechLanguage ?? "tr-TR";
  const startNumber = typeof ap.startNumber === "number" ? ap.startNumber : 1;
  const incrementBy =
    typeof ap.incrementBy === "number" && ap.incrementBy !== 0
      ? ap.incrementBy
      : 1;

  const [assignedNumbers, setAssignedNumbers] = React.useState<
    Record<string, number>
  >({});
  const [counters, setCounters] = React.useState<Record<string, number>>({});

  const speakNumber = (n: number) => {
    if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
    const speech = speechLang.startsWith("tr") ? numberToTurkish(n) : String(n);
    try {
      const utter = new SpeechSynthesisUtterance(speech);
      utter.lang = speechLang;
      window.speechSynthesis.cancel();
      window.speechSynthesis.speak(utter);
    } catch {
      /* ignore TTS errors */
    }
  };

  const handleItemClick = (itemId: string, itemType: string) => {
    if (mode !== "preview") return;
    // Re-clicking keeps the number (only the first click counts).
    if (assignedNumbers[itemId] !== undefined) return;
    let assigned: number;
    if (numberingMode === "total") {
      const used = Object.values(assignedNumbers).length;
      assigned = startNumber + used * incrementBy;
    } else {
      const idx = counters[itemType] ?? 0;
      assigned = startNumber + idx * incrementBy;
      setCounters((prev) => ({ ...prev, [itemType]: idx + 1 }));
    }
    setAssignedNumbers((prev) => ({ ...prev, [itemId]: assigned }));
    if (speakOnClick) speakNumber(assigned);
  };

  const handleReset = () => {
    setAssignedNumbers({});
    setCounters({});
    if (typeof window !== "undefined" && "speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
  };

  // Canvas mode: the author drags an item to reposition it.
  const dragRef = React.useRef<{
    itemId: string;
    startX: number;
    startY: number;
    initialPos: { x: number; y: number };
  } | null>(null);

  const beginItemDrag = (
    itemId: string,
    e: React.PointerEvent<HTMLDivElement>
  ) => {
    if (mode !== "canvas" || !onUpdate) return;
    const item = items.find((i) => i.id === itemId);
    if (!item) return;
    e.stopPropagation();
    e.preventDefault();
    dragRef.current = {
      itemId,
      startX: e.clientX,
      startY: e.clientY,
      initialPos: { ...item.position },
    };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };

  const moveItemDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || !onUpdate) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    const next = items.map((i) =>
      i.id === drag.itemId
        ? {
            ...i,
            position: {
              x: Math.max(0, Math.round(drag.initialPos.x + dx)),
              y: Math.max(0, Math.round(drag.initialPos.y + dy)),
            },
          }
        : i
    );
    onUpdate(element.id, { items: next });
  };

  const endItemDrag = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    try {
      (e.target as HTMLElement).releasePointerCapture(e.pointerId);
    } catch {
      /* noop */
    }
    dragRef.current = null;
  };

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        backgroundColor: ap.backgroundColor ?? "#f8fafc",
        border:
          ap.showFrame === false
            ? "none"
            : `${ap.borderWidth ?? 2}px ${ap.borderStyle ?? "dashed"} ${
                ap.borderColor ?? "#cbd5e1"
              }`,
        borderRadius: ap.borderRadius ?? 12,
        overflow: "hidden",
        boxSizing: "border-box",
      }}
    >
      {items.map((item) => {
        const assigned = assignedNumbers[item.id];
        return (
          <div
            key={item.id}
            onClick={() => handleItemClick(item.id, item.type)}
            onPointerDown={(e) => beginItemDrag(item.id, e)}
            onPointerMove={moveItemDrag}
            onPointerUp={endItemDrag}
            onPointerCancel={endItemDrag}
            style={{
              position: "absolute",
              left: item.position.x,
              top: item.position.y,
              width: item.size.width,
              height: item.size.height,
              cursor: mode === "preview" ? "pointer" : "move",
              userSelect: "none",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title={
              mode === "preview"
                ? "Tıkla — sayma yap"
                : `${item.type} (sürükleyerek taşı)`
            }
          >
            {item.imageUrl ? (
              <img
                src={item.imageUrl}
                alt={item.type}
                draggable={false}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                  pointerEvents: "none",
                }}
              />
            ) : (
              <div
                style={{
                  width: "100%",
                  height: "100%",
                  background: "#e2e8f0",
                  border: "1px dashed #94a3b8",
                  borderRadius: 8,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#64748b",
                  fontSize: 11,
                  textAlign: "center",
                  padding: 4,
                }}
              >
                {item.type || "?"}
              </div>
            )}
            {showNumbers && assigned !== undefined ? (
              <span
                aria-hidden
                style={{
                  position: "absolute",
                  ...badgePositionStyle,
                  width: badgeSize,
                  height: badgeSize,
                  borderRadius: badgeRadius,
                  background: ap.numberBadgeBg ?? "#1d4ed8",
                  color: ap.numberBadgeColor ?? "#ffffff",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: Math.round(badgeSize * 0.55),
                  fontWeight: ap.numberBadgeFontWeight ?? 700,
                  pointerEvents: "none",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.25)",
                  border: "2px solid #ffffff",
                }}
              >
                {assigned}
              </span>
            ) : null}
          </div>
        );
      })}
      {mode === "preview" && Object.keys(assignedNumbers).length > 0 ? (
        <button
          type="button"
          onClick={handleReset}
          style={{
            position: "absolute",
            top: 6,
            right: 6,
            padding: "4px 10px",
            fontSize: 12,
            background: "rgba(15,23,42,0.85)",
            color: "#ffffff",
            border: "none",
            borderRadius: 6,
            cursor: "pointer",
            zIndex: 5,
          }}
          title="Sayımı sıfırla"
        >
          🔄 Sıfırla
        </button>
      ) : null}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Klavye (Keypad) — önizlemede öğrencinin tıklayarak input doldurması
// için sanal tuş takımı. Tuşa basınca son odaklanan input'a (genellikle
// bir boşluk doldurma input'una) karakter eklenir, ⌫ siler.
//
// Layout: "1-5" (1-5 + ⌫), "0-9" (0-9 + ⌫), "math" (rakamlar + işlemler).
// Canvas modunda yazar klavyeyi konumlandırıp stillendirir; preview
// modunda ise kanvasın alt-ortasında kayan bir ikon/popover olur.
// ─────────────────────────────────────────────────────────────────────
const KeypadRenderer: React.FC<{
  element: EditorElement;
  mode: "canvas" | "preview";
}> = ({ element, mode }) => {
  type KeypadKey = {
    id?: string;
    label: string;
    value: string;
    gridColumn?: string;
    gridRow?: string;
  };
  const data = element.data as {
    layout?: unknown;
    appearance?: {
      backgroundColor?: string;
      borderRadius?: number;
      padding?: number;
      gap?: number;
      keyBg?: string;
      keyHoverBg?: string;
      keyActiveBg?: string;
      keyColor?: string;
      keyBorderColor?: string;
      keyBorderWidth?: number;
      keyBorderRadius?: number;
      keyFontFamily?: string;
      keyFontSize?: number;
      keyFontWeight?: 400 | 500 | 600 | 700 | 800;
    };
  };
  const layout: KeypadLayout =
    data.layout === "1-5" || data.layout === "0-9" || data.layout === "math"
      ? data.layout
      : "0-9";
  const ap = data.appearance ?? {};

  // Layout tanımı: hangi tuşlar var, kaç sütun
  const { keys, columns } = React.useMemo<{
    keys: KeypadKey[];
    columns: number;
  }>(() => {
    if (layout === "1-5") {
      return {
        keys: [
          { label: "1", value: "1" },
          { label: "2", value: "2" },
          { label: "3", value: "3" },
          { label: "4", value: "4" },
          { label: "5", value: "5" },
          { label: "⌫", value: "__BACK__" },
        ],
        columns: 6,
      };
    }
    if (layout === "math") {
      // 2 sıra × 8 sütun = 16 tuş.
      // Üst: 1 2 3 4 5 | + − ×   Alt: 6 7 8 9 0 | ÷ ⌫ ,
      return {
        keys: [
          { label: "1", value: "1" },
          { label: "2", value: "2" },
          { label: "3", value: "3" },
          { label: "4", value: "4" },
          { label: "5", value: "5" },
          { label: "+", value: "+" },
          { label: "−", value: "-" },
          { label: "×", value: "x" },
          { label: "6", value: "6" },
          { label: "7", value: "7" },
          { label: "8", value: "8" },
          { label: "9", value: "9" },
          { label: "0", value: "0" },
          { label: "÷", value: ":" },
          { label: "⌫", value: "__BACK__" },
          { label: ",", value: "," },
        ],
        columns: 8,
      };
    }
    // "0-9" default — 2 sıra × 6 sütun; ⌫ son sütunda 2 satıra yayılır.
    return {
      keys: [
        {
          label: "⌫",
          value: "__BACK__",
          gridColumn: "6",
          gridRow: "1 / span 2",
        },
        { label: "1", value: "1" },
        { label: "2", value: "2" },
        { label: "3", value: "3" },
        { label: "4", value: "4" },
        { label: "5", value: "5" },
        { label: "6", value: "6" },
        { label: "7", value: "7" },
        { label: "8", value: "8" },
        { label: "9", value: "9" },
        { label: "0", value: "0" },
      ],
      columns: 6,
    };
  }, [layout]);

  // Son odaklanan input'u takip et (window-level). Önizlemede tüm
  // input'lara focusin ile bu ref güncellenir.
  React.useEffect(() => {
    if (mode !== "preview") return;
    const onFocus = (e: FocusEvent) => {
      const t = e.target as HTMLElement;
      const w = window as unknown as {
        __qeLastKeypadInput?: HTMLInputElement;
      };
      if (
        t instanceof HTMLInputElement &&
        (t.type === "text" || t.type === "" || t.type === "search")
      ) {
        w.__qeLastKeypadInput = t;
      }
    };
    document.addEventListener("focusin", onFocus);
    return () => document.removeEventListener("focusin", onFocus);
  }, [mode]);

  const insertChar = (val: string) => {
    if (mode !== "preview") return;
    const w = window as unknown as {
      __qeLastKeypadInput?: HTMLInputElement;
    };

    let input: HTMLInputElement | null = null;
    if (document.activeElement instanceof HTMLInputElement) {
      input = document.activeElement;
    } else if (w.__qeLastKeypadInput) {
      input = w.__qeLastKeypadInput;
    }
    if (!input) return;

    const start = input.selectionStart ?? input.value.length;
    const end = input.selectionEnd ?? input.value.length;
    let newValue: string;
    let newPos: number;
    if (val === "__BACK__") {
      if (start !== end) {
        newValue = input.value.slice(0, start) + input.value.slice(end);
        newPos = start;
      } else if (start > 0) {
        newValue = input.value.slice(0, start - 1) + input.value.slice(end);
        newPos = start - 1;
      } else {
        return;
      }
    } else {
      newValue = input.value.slice(0, start) + val + input.value.slice(end);
      newPos = start + val.length;
    }

    // React'in onChange'ini tetiklemek için native value setter + input event
    const setter = Object.getOwnPropertyDescriptor(
      window.HTMLInputElement.prototype,
      "value"
    )?.set;
    if (setter) setter.call(input, newValue);
    else input.value = newValue;
    input.dispatchEvent(new Event("input", { bubbles: true }));

    setTimeout(() => {
      try {
        input?.focus();
        input?.setSelectionRange(newPos, newPos);
      } catch {
        /* noop */
      }
    }, 0);
  };

  const gridBackground = ap.backgroundColor ?? "#1e3a8a";
  const keyBackground = ap.keyBg ?? "#ffffff";
  const keyHoverBackground = ap.keyHoverBg ?? "#e2e8f0";
  const keyColor = ap.keyColor ?? "#1e3a8a";
  const keyBorderColor = ap.keyBorderColor ?? "#cbd5e1";
  const keyBorderWidth = ap.keyBorderWidth ?? 1;
  const padding = ap.padding ?? 8;
  const gap = ap.gap ?? 6;

  const renderKeyGrid = (
    sizeMode: "fill" | "fixed",
    fixedWidth?: number,
    fixedHeight?: number
  ) => (
    <div
      style={{
        width: sizeMode === "fill" ? "100%" : fixedWidth,
        height: sizeMode === "fill" ? "100%" : fixedHeight,
        background: gridBackground,
        borderRadius: ap.borderRadius ?? 12,
        padding,
        boxSizing: "border-box",
        display: "grid",
        gridTemplateColumns: `repeat(${columns}, 1fr)`,
        gap,
        boxShadow:
          sizeMode === "fixed" ? "0 10px 30px rgba(0,0,0,0.25)" : undefined,
      }}
    >
      {keys.map((k, idx) => (
        <button
          key={k.id ?? idx}
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
          }}
          onClick={() => insertChar(k.value)}
          disabled={mode !== "preview"}
          style={{
            background: keyBackground,
            color: keyColor,
            border: `${keyBorderWidth}px solid ${keyBorderColor}`,
            borderRadius: ap.keyBorderRadius ?? 8,
            gridColumn:
              "gridColumn" in k && k.gridColumn ? k.gridColumn : undefined,
            gridRow: "gridRow" in k && k.gridRow ? k.gridRow : undefined,
            fontFamily: ap.keyFontFamily ?? "inherit",
            fontSize: ap.keyFontSize ?? 22,
            fontWeight: ap.keyFontWeight ?? 700,
            cursor: mode === "preview" ? "pointer" : "default",
            boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
            userSelect: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background 0.1s, transform 0.05s",
            minWidth: sizeMode === "fixed" ? 24 : undefined,
            minHeight: sizeMode === "fixed" ? 24 : undefined,
          }}
          onMouseEnter={(e) => {
            if (mode !== "preview") return;
            (e.currentTarget as HTMLElement).style.background =
              keyHoverBackground;
          }}
          onMouseLeave={(e) => {
            if (mode !== "preview") return;
            (e.currentTarget as HTMLElement).style.background = keyBackground;
          }}
        >
          {k.label}
        </button>
      ))}
    </div>
  );

  // Preview modunda klavye kanvasın altında kayan ikon/popover olur.
  if (mode === "preview") {
    const rowsCount = Math.max(1, Math.ceil(keys.length / columns));
    return (
      <KeypadFloatingTrigger
        elementWidth={element.size.width}
        elementHeight={element.size.height ?? 200}
        columns={columns}
        rows={rowsCount}
        padding={padding}
        gap={gap}
        iconBackground={ap.backgroundColor ?? "#1e3a8a"}
        iconForeground={ap.keyBg ?? "#ffffff"}
        renderGrid={renderKeyGrid}
      />
    );
  }

  // Canvas modunda yazar klavyeyi konumlandırabilsin / stillendirebilsin.
  return renderKeyGrid("fill");
};

/**
 * Önizleme modunda klavye için kullanılan kayan widget.
 *  - Element'in normal konumunda görünmez bir anchor span tutar.
 *  - O anchor'dan en yakın preview-canvas atasını bulur; ikonu o
 *    kapsayıcının alt-orta köşesine sabitleyip body'e portal'lar.
 *  - İkona tıklayınca klavye ızgarası popover olarak açılır.
 */
const KeypadFloatingTrigger: React.FC<{
  elementWidth: number;
  elementHeight: number;
  columns: number;
  rows: number;
  padding: number;
  gap: number;
  iconBackground: string;
  iconForeground: string;
  renderGrid: (
    sizeMode: "fill" | "fixed",
    fixedWidth?: number,
    fixedHeight?: number
  ) => React.ReactNode;
}> = ({
  columns,
  rows,
  padding,
  gap,
  iconBackground,
  iconForeground,
  renderGrid,
}) => {
  const anchorRef = React.useRef<HTMLSpanElement>(null);
  const [canvasEl, setCanvasEl] = React.useState<HTMLElement | null>(null);
  const [rect, setRect] = React.useState<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(null);
  const [open, setOpen] = React.useState(false);

  // Anchor mount olduğunda en yakın preview-canvas atasını bul ve
  // element wrapper'ının pointer-events'ini iptal et (görünmez alan
  // arkadaki input'lara tıklamayı engellemesin).
  React.useLayoutEffect(() => {
    const a = anchorRef.current;
    if (!a) return;

    let p: HTMLElement | null = a.parentElement;
    while (p) {
      const cs = p.style.position || getComputedStyle(p).position;
      if (cs === "absolute") {
        p.style.pointerEvents = "none";
        break;
      }
      p = p.parentElement;
    }

    const candidates = [
      ".qe-preview-canvas",
      ".qe-fullscreen-preview__canvas",
      "[data-preview-canvas]",
    ];
    let found: HTMLElement | null = null;
    for (const sel of candidates) {
      const el = a.closest(sel) as HTMLElement | null;
      if (el) {
        found = el;
        break;
      }
    }
    setCanvasEl(found ?? null);
  }, []);

  // Anchor'ın (kanvasın) bounding rect'ini canlı takip et.
  React.useEffect(() => {
    if (!canvasEl) return;
    const update = () => {
      const r = canvasEl.getBoundingClientRect();
      setRect({ left: r.left, top: r.top, width: r.width, height: r.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(canvasEl);
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      ro.disconnect();
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [canvasEl]);

  const ICON_WIDTH = 58;
  const ICON_HEIGHT = 37;
  const ICON_SIZE = ICON_HEIGHT;
  const MARGIN = 8;

  return (
    <>
      <span
        ref={anchorRef}
        aria-hidden
        style={{ display: "block", width: 0, height: 0, overflow: "hidden" }}
      />

      {rect &&
        createPortal(
          <>
            <button
              type="button"
              onClick={() => setOpen((o) => !o)}
              aria-label="Klavyeyi aç/kapat"
              title="Klavye"
              style={{
                position: "fixed",
                left: rect.left + rect.width / 2 - ICON_WIDTH / 2,
                top: rect.top + rect.height - MARGIN - ICON_HEIGHT,
                width: ICON_WIDTH,
                height: ICON_HEIGHT,
                borderRadius: 10,
                background: iconBackground,
                color: iconForeground,
                border: "none",
                boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 20100,
                padding: 0,
                transition:
                  "background 0.15s ease, color 0.15s ease, transform 0.08s",
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.transform =
                  "scale(1.06)";
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.transform = "scale(1)";
              }}
            >
              <svg
                width={33}
                height={33}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden
              >
                <rect x="2" y="6" width="20" height="12" rx="2" />
                <path d="M6 10h.01" />
                <path d="M10 10h.01" />
                <path d="M14 10h.01" />
                <path d="M18 10h.01" />
                <path d="M6 14h.01" />
                <path d="M18 14h.01" />
                <path d="M10 14h4" />
              </svg>
            </button>

            {open &&
              (() => {
                const maxPopW = Math.max(100, rect.width - MARGIN * 2);
                const idealKey = 32;
                let keySize = idealKey;
                let popW =
                  padding * 2 + keySize * columns + gap * (columns - 1);
                if (popW > maxPopW) {
                  popW = maxPopW;
                  keySize = Math.max(
                    18,
                    (popW - padding * 2 - gap * (columns - 1)) / columns
                  );
                  popW =
                    padding * 2 + keySize * columns + gap * (columns - 1);
                }
                const popH = padding * 2 + keySize * rows + gap * (rows - 1);
                return (
                  <div
                    role="dialog"
                    aria-label="Klavye"
                    style={{
                      position: "fixed",
                      left: Math.max(
                        rect.left + MARGIN,
                        Math.min(
                          rect.left + rect.width - popW - MARGIN,
                          rect.left + rect.width / 2 - popW / 2
                        )
                      ),
                      top:
                        rect.top +
                        rect.height -
                        MARGIN -
                        ICON_SIZE -
                        8 -
                        popH,
                      width: popW,
                      height: popH,
                      zIndex: 20100,
                    }}
                  >
                    {renderGrid("fixed", popW, popH)}
                  </div>
                );
              })()}
          </>,
          document.body
        )}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Oynat (Sesli Metin) — tıklanınca metni tarayıcı TTS'i ile seslendirir.
// "Dinleyerek Cevaplama" soru tipinin temel aracı.
// ─────────────────────────────────────────────────────────────────────

/** Metni Web Speech API ile seslendirir (tarayıcı/işletim sistemi sesi). */
function speakText(text: string, language = "tr-TR"): void {
  if (typeof window === "undefined" || !("speechSynthesis" in window)) return;
  const trimmed = text.trim();
  if (!trimmed) return;
  try {
    const utter = new SpeechSynthesisUtterance(trimmed);
    utter.lang = language;
    window.speechSynthesis.cancel();
    window.speechSynthesis.speak(utter);
  } catch {
    /* ignore TTS errors */
  }
}

const PlayButtonRenderer: React.FC<{
  element: EditorElement;
  mode: "canvas" | "preview";
}> = ({ element, mode }) => {
  const data = element.data as {
    text?: string;
    language?: string;
    appearance?: {
      buttonFill?: string;
      buttonBorder?: string;
      buttonBorderWidth?: number;
      buttonBorderRadius?: number;
      triangleFill?: string;
      triangleBorder?: string;
      triangleBorderWidth?: number;
    };
  };
  const ap = data.appearance ?? {};
  const buttonFill = ap.buttonFill ?? "#1e3a8a";
  const buttonBorder = ap.buttonBorder ?? "#1e3a8a";
  const buttonBorderWidth = ap.buttonBorderWidth ?? 2;
  const buttonBorderRadius = ap.buttonBorderRadius ?? 14;
  const triangleFill = ap.triangleFill ?? "#ffffff";
  const triangleBorder = ap.triangleBorder ?? "#ffffff";
  const triangleBorderWidth = ap.triangleBorderWidth ?? 0;
  const lang = data.language ?? "tr-TR";
  const text = data.text ?? "";

  const handleClick = (e: React.MouseEvent) => {
    if (mode !== "preview") return;
    if (!text.trim()) return;
    e.stopPropagation();
    speakText(text, lang);
  };

  return (
    <div
      style={{
        width: "100%",
        height: "100%",
        cursor: mode === "preview" ? "pointer" : "default",
        userSelect: "none",
      }}
      onClick={handleClick}
      title={
        mode === "preview"
          ? text
            ? "Tıkla — seslendirilir"
            : "Metin girilmemiş"
          : undefined
      }
    >
      <svg
        width="100%"
        height="100%"
        viewBox="0 0 100 100"
        preserveAspectRatio="none"
        style={{ display: "block", pointerEvents: "none" }}
      >
        <rect
          x={buttonBorderWidth / 2}
          y={buttonBorderWidth / 2}
          width={100 - buttonBorderWidth}
          height={100 - buttonBorderWidth}
          rx={buttonBorderRadius}
          ry={buttonBorderRadius}
          fill={buttonFill}
          stroke={buttonBorder}
          strokeWidth={buttonBorderWidth}
        />
        <polygon
          points="35,21 35,79 79,50"
          fill={triangleFill}
          stroke={triangleBorder}
          strokeWidth={triangleBorderWidth}
          strokeLinejoin="round"
        />
      </svg>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────
// Lego/Unifix Küp — Toplama-Çıkarma
// ─────────────────────────────────────────────────────────────────────

/** Tek küpün SVG çizimi: yuvarlatılmış kare gövde + (opsiyonel) sağa bakan
 *  stud + merkez daire. cube-bar'da yalnız en sağdaki dolu kübün studu
 *  görünür (showStud=true); diğerlerinde false. */
const CubeShape: React.FC<{
  fill: string;
  stroke: string;
  strokeWidth: number;
  circleFill?: string;
  circleStroke?: string;
  circleStrokeWidth?: number;
  ariaHidden?: boolean;
  showStud?: boolean;
}> = ({
  fill,
  stroke,
  strokeWidth,
  circleFill,
  circleStroke,
  circleStrokeWidth = 2,
  ariaHidden,
  showStud = true,
}) => (
  <svg
    width="100%"
    height="100%"
    viewBox={showStud ? "0 0 120 100" : "0 0 100 100"}
    preserveAspectRatio="none"
    aria-hidden={ariaHidden}
    overflow="visible"
    style={{ display: "block", pointerEvents: "none", overflow: "visible" }}
  >
    {showStud &&
      (() => {
        const left = 100;
        const right = 120;
        const top = 25;
        const bottom = 75;
        const r = 4;
        const d = `M ${left} ${top} L ${right - r} ${top} A ${r} ${r} 0 0 1 ${right} ${
          top + r
        } L ${right} ${bottom - r} A ${r} ${r} 0 0 1 ${right - r} ${bottom} L ${left} ${bottom} Z`;
        return (
          <path
            d={d}
            fill={fill}
            stroke={stroke}
            strokeWidth={strokeWidth}
            strokeLinejoin="miter"
          />
        );
      })()}
    <rect
      x={strokeWidth / 2}
      y={strokeWidth / 2}
      width={100 - strokeWidth}
      height={100 - strokeWidth}
      rx={8}
      ry={8}
      fill={fill}
      stroke={stroke}
      strokeWidth={strokeWidth}
    />
    {circleFill !== undefined || circleStroke !== undefined ? (
      <circle
        cx={50}
        cy={50}
        r={25}
        fill={circleFill ?? "none"}
        stroke={circleStroke ?? "none"}
        strokeWidth={circleStrokeWidth}
      />
    ) : null}
  </svg>
);

/** Hedef "Küp Çubuğu" — slotCount yuvalı yatay sıra. value kadar başlangıç
 *  küpü dolu. Kaynaktan sürükleyip bırakınca eklenir; dolu küpe tıklayınca
 *  (veya dışarı sürükleyince) çıkarılır. expectedTotal < başlangıç ise
 *  çıkarma sorusu (başlangıç küpleri de çıkarılabilir). */
const CubeBarRenderer: React.FC<{
  element: EditorElement;
  mode: "canvas" | "preview";
  onCubeBarChange?: (elementId: string, total: number) => void;
  expectedTotal?: number;
}> = ({ element, mode, onCubeBarChange, expectedTotal }) => {
  const data = element.data as {
    slotCount?: number;
    value?: number;
    cubeColor?: string;
    emptySlotColor?: string;
    borderColor?: string;
    borderWidth?: number;
    circleFill?: string;
    circleBorder?: string;
    circleBorderWidth?: number;
    addedCubeColor?: string;
    addedBorderColor?: string;
    addedCircleFill?: string;
    addedCircleBorder?: string;
    addedCircleBorderWidth?: number;
    emptyCircleFill?: string;
    emptyCircleBorder?: string;
    emptyCircleBorderWidth?: number;
    emptyBorderColor?: string;
    barBackground?: string;
    barBorderColor?: string;
    barBorderWidth?: number;
    barBorderRadius?: number;
  };
  const slotCount = Math.max(1, Math.min(20, data.slotCount ?? 10));
  const initialValue = Math.max(0, Math.min(slotCount, data.value ?? 0));
  const [addedCount, setAddedCount] = React.useState(0);
  const [removedInitialCount, setRemovedInitialCount] = React.useState(0);
  React.useEffect(() => {
    setAddedCount(0);
    setRemovedInitialCount(0);
  }, [initialValue]);
  const canRemoveInitial =
    typeof expectedTotal === "number" && expectedTotal < initialValue;
  React.useEffect(() => {
    setRemovedInitialCount(0);
  }, [expectedTotal]);
  const visibleInitial = Math.max(0, initialValue - removedInitialCount);
  const localValue = Math.min(slotCount, visibleInitial + addedCount);

  React.useEffect(() => {
    if (mode === "preview" && onCubeBarChange) {
      onCubeBarChange(element.id, localValue);
    }
  }, [mode, onCubeBarChange, element.id, localValue]);

  const cubeColor = data.cubeColor ?? "#ec4899";
  const emptyColor = data.emptySlotColor ?? "#ffffff";
  const borderColor = data.borderColor ?? "#be185d";
  const borderWidth = data.borderWidth ?? 2;
  const circleFill = data.circleFill ?? "#f9a8d4";
  const circleBorder = data.circleBorder ?? "#be185d";
  const circleBorderWidth = data.circleBorderWidth ?? 2;
  const addedCubeColor = data.addedCubeColor ?? "#60a5fa";
  const addedBorderColor = data.addedBorderColor ?? "#2563eb";
  const addedCircleFill = data.addedCircleFill ?? "#93c5fd";
  const addedCircleBorder = data.addedCircleBorder ?? "#2563eb";
  const addedCircleBorderWidth = data.addedCircleBorderWidth ?? 2;
  const emptyCircleFill = data.emptyCircleFill ?? "#cbd5e1";
  const emptyCircleBorder = data.emptyCircleBorder ?? "transparent";
  const emptyCircleBorderWidth = data.emptyCircleBorderWidth ?? 0;
  const emptyBorderColor =
    data.emptyBorderColor ?? data.borderColor ?? "#cbd5e1";
  const barBackground = data.barBackground ?? "#ffffff";
  const barBorderColor = data.barBorderColor ?? "#94a3b8";
  const barBorderWidth = data.barBorderWidth ?? 2;
  const barBorderRadius = data.barBorderRadius ?? 10;
  const innerPadding = 12;

  const [isDragOver, setIsDragOver] = React.useState(false);

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    if (mode !== "preview") return;
    if (!e.dataTransfer.types.includes("application/x-cube-source")) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
    setIsDragOver(true);
  };
  const handleDragLeave = () => setIsDragOver(false);
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    if (mode !== "preview") return;
    if (!e.dataTransfer.types.includes("application/x-cube-source")) return;
    e.preventDefault();
    setIsDragOver(false);
    if (visibleInitial + addedCount >= slotCount) return;
    setAddedCount((c) => c + 1);
  };

  const handleCubeClick = (idx: number) => {
    if (mode !== "preview") return;
    if (idx >= visibleInitial && idx < visibleInitial + addedCount) {
      setAddedCount((c) => Math.max(0, c - 1));
      return;
    }
    if (canRemoveInitial && idx >= 0 && idx < visibleInitial) {
      setRemovedInitialCount((c) => Math.min(initialValue, c + 1));
    }
  };

  const isDraggableCube = (idx: number) => {
    const isAdded = idx >= visibleInitial && idx < visibleInitial + addedCount;
    const isRemovableInitial =
      canRemoveInitial && idx >= 0 && idx < visibleInitial;
    return isAdded || isRemovableInitial;
  };
  const handleCubeDragStart = (
    e: React.DragEvent<HTMLDivElement>,
    idx: number
  ) => {
    if (mode !== "preview") return;
    if (!isDraggableCube(idx)) return;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("application/x-cube-return", String(idx));
    try {
      const target = e.currentTarget as HTMLDivElement;
      const r = target.getBoundingClientRect();
      e.dataTransfer.setDragImage(target, r.width / 2, r.height / 2);
    } catch {
      /* noop */
    }
  };
  const handleCubeDragEnd = (
    e: React.DragEvent<HTMLDivElement>,
    idx: number
  ) => {
    if (mode !== "preview") return;
    if (e.dataTransfer.dropEffect !== "none") return;
    if (idx >= visibleInitial && idx < visibleInitial + addedCount) {
      setAddedCount((c) => Math.max(0, c - 1));
    } else if (canRemoveInitial && idx >= 0 && idx < visibleInitial) {
      setRemovedInitialCount((c) => Math.min(initialValue, c + 1));
    }
  };

  const studRatio = 0.2;
  const containerRef = React.useRef<HTMLDivElement>(null);
  const [innerSize, setInnerSize] = React.useState({ w: 0, h: 0 });
  React.useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => {
      const r = el.getBoundingClientRect();
      setInnerSize({ w: r.width, h: r.height });
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const bodySize = Math.max(
    0,
    Math.min(innerSize.h, innerSize.w / (slotCount + studRatio))
  );
  const totalCubesWidth = slotCount * bodySize + studRatio * bodySize;
  const startX = Math.max(0, (innerSize.w - totalCubesWidth) / 2);
  const startY = Math.max(0, (innerSize.h - bodySize) / 2);

  return (
    <div
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      style={{
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
        background: barBackground,
        border: `${barBorderWidth}px solid ${barBorderColor}`,
        borderRadius: barBorderRadius,
        padding: innerPadding,
        boxShadow: isDragOver ? `0 0 0 3px rgba(59,130,246,0.35)` : undefined,
        transition: "box-shadow 0.15s ease",
        overflow: "hidden",
        position: "relative",
      }}
      title={
        mode === "preview"
          ? canRemoveInitial
            ? "Küp çıkar: dolu küpe tıkla (veya sürükleyip dışarı bırak)."
            : "Küp ekle: küp kaynağından sürükle. Çıkar: dolu küpe tıkla."
          : undefined
      }
    >
      <div
        ref={containerRef}
        style={{ position: "relative", width: "100%", height: "100%" }}
      >
        {bodySize > 0 && (
          <>
            {Array.from({ length: slotCount }).map((_, i) => {
              const isInitial = i < visibleInitial;
              const isAdded =
                i >= visibleInitial && i < visibleInitial + addedCount;
              const filled = isInitial || isAdded;
              const draggableCube =
                mode === "preview" &&
                (isAdded || (canRemoveInitial && isInitial));
              const cubeFillColor = isAdded
                ? addedCubeColor
                : isInitial
                ? cubeColor
                : emptyColor;
              const cubeStrokeColor = isAdded
                ? addedBorderColor
                : isInitial
                ? borderColor
                : emptyBorderColor;
              const cFill = isAdded
                ? addedCircleFill
                : isInitial
                ? circleFill
                : emptyCircleFill;
              const cStroke = isAdded
                ? addedCircleBorder
                : isInitial
                ? circleBorder
                : emptyCircleBorder;
              const cStrokeW = isAdded
                ? addedCircleBorderWidth
                : isInitial
                ? circleBorderWidth
                : emptyCircleBorderWidth;
              return (
                <div
                  key={i}
                  onClick={() => handleCubeClick(i)}
                  draggable={draggableCube}
                  onDragStart={(e) => handleCubeDragStart(e, i)}
                  onDragEnd={(e) => handleCubeDragEnd(e, i)}
                  style={{
                    position: "absolute",
                    left: `${startX + i * bodySize}px`,
                    top: `${startY}px`,
                    width: `${bodySize}px`,
                    height: `${bodySize}px`,
                    zIndex: i,
                    cursor: draggableCube ? "grab" : "default",
                  }}
                >
                  <CubeShape
                    fill={cubeFillColor}
                    stroke={cubeStrokeColor}
                    strokeWidth={borderWidth}
                    circleFill={cFill}
                    circleStroke={cStroke}
                    circleStrokeWidth={cStrokeW}
                    ariaHidden={!filled}
                    showStud={false}
                  />
                </div>
              );
            })}
            {localValue > 0 &&
              (() => {
                const lastIdx = localValue - 1;
                const lastIsAdded = lastIdx >= visibleInitial;
                return (
                  <div
                    style={{
                      position: "absolute",
                      left: `${startX + lastIdx * bodySize}px`,
                      top: `${startY}px`,
                      width: `${bodySize * (1 + studRatio)}px`,
                      height: `${bodySize}px`,
                      zIndex: slotCount + 10,
                      pointerEvents: "none",
                    }}
                  >
                    <CubeShape
                      fill={lastIsAdded ? addedCubeColor : cubeColor}
                      stroke={lastIsAdded ? addedBorderColor : borderColor}
                      strokeWidth={borderWidth}
                      circleFill={lastIsAdded ? addedCircleFill : circleFill}
                      circleStroke={lastIsAdded ? addedCircleBorder : circleBorder}
                      circleStrokeWidth={
                        lastIsAdded ? addedCircleBorderWidth : circleBorderWidth
                      }
                      showStud={true}
                    />
                  </div>
                );
              })()}
            {localValue < slotCount && (
              <div
                style={{
                  position: "absolute",
                  left: `${startX + (slotCount - 1) * bodySize}px`,
                  top: `${startY}px`,
                  width: `${bodySize * (1 + studRatio)}px`,
                  height: `${bodySize}px`,
                  zIndex: slotCount + 5,
                  pointerEvents: "none",
                }}
              >
                <CubeShape
                  fill={emptyColor}
                  stroke={emptyBorderColor}
                  strokeWidth={borderWidth}
                  circleFill={emptyCircleFill}
                  circleStroke={emptyCircleBorder}
                  circleStrokeWidth={emptyCircleBorderWidth}
                  showStud={true}
                  ariaHidden
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

/** Sınırsız kaynak küp — sürüklenince kaybolmaz (cloneable). cube-bar
 *  "application/x-cube-source" verisini kabul eder. */
const CubeSourceRenderer: React.FC<{
  element: EditorElement;
  mode: "canvas" | "preview";
}> = ({ element, mode }) => {
  const data = element.data as {
    cubeColor?: string;
    borderColor?: string;
    borderWidth?: number;
    circleFill?: string;
    circleBorder?: string;
    circleBorderWidth?: number;
  };
  const cubeColor = data.cubeColor ?? "#60a5fa";
  const borderColor = data.borderColor ?? "#2563eb";
  const borderWidth = data.borderWidth ?? 2;
  const circleFill = data.circleFill ?? "#93c5fd";
  const circleBorder = data.circleBorder ?? "#2563eb";
  const circleBorderWidth = data.circleBorderWidth ?? 2;

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>) => {
    if (mode !== "preview") return;
    e.dataTransfer.effectAllowed = "copy";
    e.dataTransfer.setData("application/x-cube-source", "1");
    try {
      const el = e.currentTarget as HTMLDivElement;
      const rect = el.getBoundingClientRect();
      e.dataTransfer.setDragImage(el, rect.width / 2, rect.height / 2);
    } catch {
      /* noop */
    }
  };
  return (
    <div
      draggable={mode === "preview"}
      onDragStart={handleDragStart}
      style={{
        width: "100%",
        height: "100%",
        cursor: mode === "preview" ? "grab" : "default",
        userSelect: "none",
      }}
      title={
        mode === "preview"
          ? "Sürükleyip çubuğa bırak — küp eklenir (sınırsız)"
          : undefined
      }
    >
      <CubeShape
        fill={cubeColor}
        stroke={borderColor}
        strokeWidth={borderWidth}
        circleFill={circleFill}
        circleStroke={circleBorder}
        circleStrokeWidth={circleBorderWidth}
        showStud={true}
      />
    </div>
  );
};
